import type { BaseType, Selection } from 'd3-selection';
import type { Simulation } from 'd3-force';
import type { ZoomBehavior } from 'd3-zoom';
import { NODE_RADIUS } from '../recipe_renderer/dimensions.js';
import { DisplayNode, EditionMode, Link, NodeType, assignLinkShape } from '../recipe_renderer/types.js';
import { resolveNodeTitle, cloneNode, remapTransformationParams } from '../recipe_renderer/DisplayGraph.js';
import { NodeVisualizer, VisualizerContext } from '../recipe_renderer/NodeVisualizer.js';
import { createGraphSimulation, setupSimulationTick } from './forces.js';
import { searchRecipeList } from '../api/index.js';
import { showToast } from '../ui/toast.js';
import { escapeHtml } from '../ui/escapeHtml.js';
import { showUnsavedChangesDialog } from '../ui/UnsavedChangesDialog.js';
import { getNodeImageUrl as resolveNodeImageUrl, getDefaultImageUrl } from '../recipe_renderer/imageUtils.js';
import type { RecipeListItem, RecipeNode as ApiRecipeNode, Recipe, Ingredient, Transformation } from '../api/types.js';
import { unitCache } from '../unit-cache.js';
import { RecipeHeaderStrip } from './RecipeHeaderStrip.js';
import { EditToolbar } from './EditToolbar.js';
import { NodeEditPopup } from './NodeEditPopup.js';
import { EditButtonRenderer } from './EditButtonRenderer.js';
import { TabSelector, type TabId } from './TabSelector.js';
import { setIcon } from '../ui/icon.js';
import type { StepTextRenderer } from '../recipe_renderer/NodeVisualizer.js';
import {
  buildIngredientsHtml,
  buildPreparationHtml,
  buildToolsHtml,
  computeNodeTotalDuration,
  renderStepTextForNode,
  renderIngredientsTextForNode,
  type RecipeTextContext,
} from '../recipe_renderer/RecipeTextRenderer.js';
import { durationBadgeHtml } from '../recipe_renderer/textFormatting.js';
import type { RecipeStore } from '../store/types.js';
// The store's own EditionMode enum — its string values (e.g. 'addParentLink')
// differ from recipe_renderer/types.js's EditionMode (e.g. 'add-parent-link'),
// which NodeVisualizer's callback type still requires. Always compare against
// or pass to the store using THIS enum, never the recipe_renderer one.
import { EditionMode as StoreEditionMode } from '../store/types.js';

/** Font family constant for SVG text elements */
const MONO_FONT = "'Courier New', Consolas, 'Liberation Mono', monospace";

/**
 * Registered recipe entry tracking what's loaded in the pool.
 */
interface RecipeEntry {
  rootNodeIds: string[];
  mainRootIndex: number;
  fullyLoaded: boolean;
}

/**
 * A picked "suggested transformation" to pre-fill a new Transform node with
 * — see requestTransformSelectedNodes() and applyTransformSeed() below.
 */
interface TransformSeed {
  ingredient?: Ingredient;
  transformation?: Transformation;
  imageUrl?: string;
}

/**
 * GraphVisualizer — state-free node pool visualizer.
 *
 * Replaces both RecipeVisualizer (detail view) and HomeGraph (browse view).
 * Holds a pool of all currently loaded nodes across all recipes and renders
 * them uniformly. Capabilities (selection, editing, expansion) are orthogonal
 * layers on top.
 *
 * Usage:
 * ```typescript
 * const viz = new GraphVisualizer(document.getElementById('graph-container')!);
 * await viz.addRootNodes(items);       // Browse mode — load root nodes
 * await viz.expandNode(nodeId);        // Expand a node (fetch subgraph if needed)
 * viz.foldNode(nodeId);                // Fold a node
 * await viz.loadFullRecipe(recipeId);  // Load a full recipe graph
 * viz.selectNode(node);                // Select a node
 * viz.editMode = true;                 // Enable edit mode
 * viz.destroy();                       // Cleanup
 * ```
 */
export class GraphVisualizer {
  // ── Store reference (single source of truth) ──
  private store: RecipeStore;

  // ── Core state (nodePool/linkPool managed by store, kept as local reference) ──
  private get nodePool(): Map<string, DisplayNode> { return this.store.getState().nodePool; }
  private get linkPool(): Link<DisplayNode>[] { return this.store.getState().linkPool; }
  private simulation: Simulation<DisplayNode, Link<DisplayNode>> | null = null;

  // ── D3 elements ──
  private svg: Selection<SVGSVGElement, unknown, null, undefined> | null = null;
  private mainGroup: Selection<SVGGElement, unknown, null, undefined> | null = null;
  private defsEl: Selection<SVGDefsElement, unknown, null, undefined> | null = null;
  private linksGroup: Selection<SVGGElement, unknown, null, undefined> | null = null;
  private nodesGroup: Selection<SVGGElement, unknown, null, undefined> | null = null;
  private zoomBehavior: ZoomBehavior<SVGSVGElement, unknown> | null = null;

  // ── Container dimensions ──
  private width: number = 800;
  private height: number = 500;

  // ── Sub-components ──
  private nodeVisualizer: NodeVisualizer | null = null;
  private visualizerContainer: HTMLElement | null = null;

  // ── Capabilities (orthogonal toggles) — delegated to store ──

  /** Scaling factor for yield-based quantity display (default 1 = no scaling) */
  private _scalingFactor: number = 1;
  /** The root node's original quantity (yield), used as baseline for scaling */
  private _originalRootQty: { amount: number; unit: string } | null = null;

  private _hoveredNode: DisplayNode | null = null;

  // ── Recipe selection (editing target) — delegated to store ──
  private onRecipeSelectionChangedCallback: ((recipeId: string | null) => void) | null = null;

  // ── Recipe header strip ──
  private recipeHeaderStrip: RecipeHeaderStrip | null = null;
  private recipeHeaderContainer: HTMLElement | null = null;
  private editToolbar: EditToolbar | null = null;
  private nodeEditPopup: NodeEditPopup | null = null;
  private popupNodeVisualizer: NodeVisualizer | null = null;

  // ── Recipes loaded in the pool — delegated to store ──

  // ── Recipes hidden/removed from view — delegated to store ──

  // ── Recipes auto-hidden when a main root is expanded — delegated to store ──

  // ── Recipes hidden by ingredient-search filtering — delegated to store ──

  // ── Container element ──
  private container: HTMLElement;

  // ── Side panel container ──
  private sidePanel: HTMLElement | null = null;

  // ── Tab selector ──
  private tabSelector: TabSelector | null = null;
  private tabSelectorContainer: HTMLElement | null = null;
  private onTabChangeCallback: ((tab: TabId) => void) | null = null;

  // ── Saved filter state (restored on edit exit) ──
  private _savedFilterState: string[] | null = null;

  // ── Edition sub-mode (link add/remove flow state) — delegated to store ──

  // ── Edit button renderer ──
  private editButtonRenderer: EditButtonRenderer | null = null;

  // ── Temp link line for add/delete link flows ──
  private tempLinkGroup: Selection<SVGGElement, unknown, null, undefined> | null = null;
  private tempLinkLine: Selection<SVGLineElement, unknown, null, undefined> | null = null;
  private tempGhostNode: Selection<SVGGElement, unknown, null, undefined> | null = null;
  private tempLinkSourceNode: DisplayNode | null = null;
  private tempLinkSourcePos: { x: number; y: number } = { x: 0, y: 0 };
  private tempLinkDocMouseUpHandler: ((e: MouseEvent) => void) | null = null;
  private tempLinkIsAdd: boolean = true;

  /** Cleanup functions for touch event handlers (added in capturing phase) */
  private tempLinkCleanups: Array<() => void> = [];

  // ── Pick node state (transformation parameter of type node) ──
  private _pickOnPicked: ((nodeId: string | null) => void) | null = null;
  private _pickSourceNodeId: string | null = null;
  private _pickParamName: string | null = null;
  private _pickEscapeHandler: ((e: KeyboardEvent) => void) | null = null;
  /** "Tria un node…" banner shown while picking with the node-edit popup hidden out of the way — see showPickBanner()/hidePickBanner(). */
  private _pickBanner: HTMLElement | null = null;
  /** Cancels pick mode on a click outside a node/caret/list-row — see the outsideClickHandler set up in onStartPickNode. */
  private _pickOutsideClickHandler: ((e: MouseEvent) => void) | null = null;

  // ── Callbacks ──
  private onSelectionChanged: ((node: DisplayNode | null) => void) | null = null;
  /** Callback when user clicks "Obre" on a recipe summary in the side panel */
  private onExpandRequested: ((nodeId: string) => void) | null = null;
  /** Callback when user clicks navigate to recipe page */
  private _onNavigateToRecipeCallback: ((recipeId: string) => void) | null = null;
  /** Callback when user clicks the EditToolbar's [⚘] "go to ingredient" button */
  private _onNavigateToIngredientCallback: ((ingredientId: string) => void) | null = null;
  /** Callback when user clicks (+) without a recipe selected (create new recipe) */
  private _onCreateNewRecipeCallback: (() => void) | null = null;
  /** Callback fired after a successful save (for home page to refresh root nodes) */
  private _onRecipeSavedCallback: (() => void) | null = null;
  /** Callbacks for when edit mode changes (supports multiple listeners) */
  private _onEditModeChangedCallbacks: ((editing: boolean) => void)[] = [];

  /** Callback for when focus changes (recipe expanded/folded) */
  private _onFocusChangedCallback: ((focused: boolean, recipeId?: string) => void) | null = null;
  /** Last focused recipe id seen by the store subscription, to detect focus changes (see constructor). */
  private _lastFocusedRecipeId: string | null = null;
  /** Last selected recipe id seen by the store subscription, to detect selection changes (see constructor). */
  private _lastSelectedRecipeIdForTabs: string | null = null;

  /** Whether the device is mobile (touchscreen with small viewport) */
  private _isMobile: boolean = false;
  private _lastIsWideScreen: boolean = false;
  private _resizeHandler: (() => void) | null = null;

  /** Touch start position for tap-vs-drag disambiguation on mobile */
  private _tapStartPos: { x: number; y: number } | null = null;
  /** Pointer down position for click-vs-drag disambiguation on desktop */
  private _pointerDownPos: { x: number; y: number } | null = null;

  constructor(container: HTMLElement, sidePanel: HTMLElement | undefined, store: RecipeStore) {
    this.store = store;
    this.container = container;
    if (sidePanel) {
      this.sidePanel = sidePanel;
    }
    this._isMobile = this.detectMobile();
    this._lastIsWideScreen = this.isWideScreen();
    this.init();
    this._lastFocusedRecipeId = this.store.getFocusedRecipeId();
    this._lastSelectedRecipeIdForTabs = this.store.getSelectedRecipeId();

    // Crossing the wide-screen breakpoint changes whether the side panel
    // should show the editable tabs (wide) or stay read-only (narrow) —
    // re-render it when that actually flips, not on every resize pixel.
    this._resizeHandler = () => {
      const nowWide = this.isWideScreen();
      if (nowWide !== this._lastIsWideScreen) {
        this._lastIsWideScreen = nowWide;
        this.updateSidePanel();
      }
    };
    window.addEventListener('resize', this._resizeHandler);

    // Re-render whenever the store changes, regardless of which view (this
    // one, List View, or a future one) triggered the change. Without this,
    // an action taken in another view is invisible here until this view
    // happens to render on its own (e.g. the user clicks a node).
    this.store.subscribe(() => {
      this.renderAll();
      this.updateSidePanel();
      this.updateRecipeHeader();

      // Keep the narrow-screen ingredients/preparation tabs in sync with
      // selection, no matter which code path selected the recipe — some
      // (e.g. RecipeStore.selectNodeWithAutoRecipe(), used when expanding a
      // main root) set selectedRecipeId directly without going through
      // GraphVisualizer.switchSelectedRecipe(), which used to be the only
      // place this was updated, leaving the tabs stuck on "browse" until
      // some unrelated click happened to refresh them.
      const selectedRecipeId = this.store.getSelectedRecipeId();
      if (selectedRecipeId !== this._lastSelectedRecipeIdForTabs) {
        this._lastSelectedRecipeIdForTabs = selectedRecipeId;
        if (selectedRecipeId !== null) {
          this.tabSelector?.showAllTabs();
        } else {
          this.tabSelector?.showBrowseTabs();
        }
      }

      // Fire onFocusChanged whenever the store's focused recipe actually
      // changes, no matter which action caused it (expandNode/foldNode's
      // focusRecipe()/unfocusRecipe() calls, discard, refresh, ...). This is
      // what HomePage listens to for pushing /recipe/{id} into the URL —
      // detecting it here instead of at each call site means it can't be
      // silently skipped by a path that changes focus without remembering
      // to fire it itself (which is exactly what left it dead before).
      const focusedRecipeId = this.store.getFocusedRecipeId();
      if (focusedRecipeId !== this._lastFocusedRecipeId) {
        this._lastFocusedRecipeId = focusedRecipeId;

        // A recipe just became focused while already in edit mode —
        // (re)initialize its ChangeTracker now that it's guaranteed fully
        // loaded (see the expand paths in RecipeStore.expandNode()).
        // enterEditMode() no longer does this itself for an unfocused
        // recipe (entering edit mode must not auto-focus — see its own
        // doc comment), so this is the one place it happens for a recipe
        // that gets focused *after* edit mode was already on. Focusing an
        // already-focused-when-edit-mode-was-entered recipe doesn't hit
        // this branch at all (focusedRecipeId hasn't changed), leaving
        // enterEditMode()'s own initialization for that case untouched.
        if (focusedRecipeId && this.store.isEditMode()) {
          try {
            this.store.initializeChangeTracker(focusedRecipeId);
          } catch (err) {
            console.error('Failed to initialize ChangeTracker on focus:', err);
          }
        }

        if (focusedRecipeId) {
          this._onFocusChangedCallback?.(true, focusedRecipeId);
        } else {
          this._onFocusChangedCallback?.(false);
        }
      }

      // Refresh Save/Discard visibility on every notify — the store's own
      // mutation methods self-track internally now (see RecipeStore's
      // trackNodeIfEditing()/trackGraphIfEditing()), so nothing else calls
      // this after a specific action. Placed last so it reflects a
      // freshly (re-)initialized tracker if the focus-change block above
      // just created one for a newly-focused recipe.
      this.updateSaveButtonVisibility();
    });
  }

  /**
   * Initialize the SVG and basic structure.
   */
  private init(): void {
    this.calculateDimensions();
    this.createSVG();
    this.setupZoom();
    this.setupBackgroundClick();
    this.initEditButtonRenderer();
    // Create the side panel's empty state immediately
    this.ensureNodeVisualizer();

  }

  /**
   * Initialize the EditButtonRenderer after the SVG is ready.
   */
  private initEditButtonRenderer(): void {
    if (!this.nodesGroup) return;

    this.editButtonRenderer = new EditButtonRenderer({
      nodesGroup: this.nodesGroup as unknown as Selection<SVGGElement, DisplayNode, BaseType, unknown>,
      focusedRecipeId: () => this.store.getFocusedRecipeId(),
      nodePool: () => this.nodePool,
      isNodeHidden: (nodeId: string) => this.store.isNodeHidden(nodeId),
      isNodeSelected: (nodeId: string) => this.store.isNodeSelected(nodeId),
      isNodeHovered: (nodeId: string) => this.store.isNodeHovered(nodeId),
      isNodeExpanded: (nodeId: string) => this.store.isNodeExpanded(nodeId),
      isMobile: this._isMobile,
      onAddParentLink: (node: DisplayNode) => {
        this.startAddLinkFlow(node, StoreEditionMode.ADD_PARENT_LINK);
      },
      onAddChildLink: (node: DisplayNode) => {
        this.startAddLinkFlow(node, StoreEditionMode.ADD_CHILD_LINK);
      },
      onDeleteParentLink: (node: DisplayNode) => {
        this.handleDeleteLink(node, 'parent');
      },
      onDeleteChildLink: (node: DisplayNode) => {
        this.handleDeleteLink(node, 'child');
      },
      onDeleteNode: (node: DisplayNode) => {
        this.handleDeleteNode(node);
      },
    });
  }

  /**
   * Calculate container dimensions.
   */
  private calculateDimensions(): void {
    const rect = this.container.getBoundingClientRect();
    this.width = Math.max(rect.width || 800, 400);
    this.height = Math.max(rect.height || 500, 300);
  }

  /**
   * Create the SVG element with grid background and groups.
   */
  private createSVG(): void {
    this.container.innerHTML = '';

    // On mobile, add 50px safe-area padding at the bottom so the (+) button
    // and graph controls are not obscured by browser chrome (see §8.9).
    const safeAreaPadding = this._isMobile ? 50 : 0;
    const svgHeight = this.height + safeAreaPadding;

    const svg = d3.select(this.container).append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${this.width} ${svgHeight}`)
      .style('touch-action', 'none')
      .style('background', '#0d1117')
      .style('background-image', `
        repeating-linear-gradient(0deg, transparent 0px, transparent 149px, var(--grid-minor) 149px, var(--grid-major) 150px),
        repeating-linear-gradient(90deg, transparent 0px, transparent 149px, var(--grid-minor) 149px, var(--grid-major) 150px),
        repeating-linear-gradient(0deg, transparent 0px, transparent 29px, #1a1a2e 29px, #1a1a2e 30px),
        repeating-linear-gradient(90deg, transparent 0px, transparent 29px, #1a1a2e 29px, #1a1a2e 30px)
      `.replace(/\s+/g, ' ').trim())
      .style('background-size', '150px 150px, 150px 150px, 30px 30px, 30px 30px')
      .style('background-position', '-15px -15px, -15px -15px, -15px -15px, -15px -15px');

    this.svg = svg;
    this.defsEl = svg.append('defs');
    this.mainGroup = svg.append('g').attr('class', 'main-group');
    this.linksGroup = this.mainGroup.append('g').attr('class', 'links');
    this.nodesGroup = this.mainGroup.append('g').attr('class', 'nodes');

    // Temp link line group (inside mainGroup so it moves with zoom/pan)
    this.tempLinkGroup = this.mainGroup.append('g')
      .attr('class', 'temp-link-group')
      .style('display', 'none');
    this.tempLinkLine = this.tempLinkGroup.append('line')
      .attr('class', 'temp-link-line')
      .style('stroke', 'var(--accent-alpha-40)')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5')
      .style('pointer-events', 'none');

    // Ghost node — semi-transparent placeholder shown when dragging over empty space
    this.tempGhostNode = this.tempLinkGroup.append('g')
      .attr('class', 'temp-ghost-node')
      .style('display', 'none');
    this.tempGhostNode.append('circle')
      .attr('r', NODE_RADIUS)
      .attr('fill', 'var(--accent-alpha-20)')
      .attr('stroke', 'var(--accent-alpha-60)')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '4,4')
      .style('pointer-events', 'none');
  }

  /**
   * Set up zoom behavior for pan and zoom.
   */
  private setupZoom(): void {
    if (!this.svg) return;

    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .filter((event: MouseEvent | TouchEvent | WheelEvent) => {
        // Replicate d3-zoom's own default filter (ignore ctrl-click / non-primary
        // mouse button; wheel events have neither, so they pass through).
        if ('button' in event && (event.ctrlKey || event.button)) return false;

        // Never let an edit-button gesture (+/−/×/transform/etc.) also pan the
        // canvas. Same reasoning as the node drag filter below: a touchstart
        // that begins on one of these buttons bubbles all the way up to the
        // SVG root — where zoom's own touchstart listener lives — unless
        // something along the way stops it, which is exactly what's fragile
        // on touch (device-detection-gated stopPropagation). Filtering here
        // stops the pan from starting at all, independent of that.
        const target = event.target as Element | null;
        return !target?.closest('.edit-btn, .edit-button-group');
      })
      .on('zoom', (event: any) => {
        if (this.mainGroup) {
          this.mainGroup.attr('transform', event.transform);
        }
      });

    this.svg.call(zoom);
    this.zoomBehavior = zoom;
  }

  /**
   * Smoothly zoom and pan the viewport to fit a set of nodes.
   * Centers the bounding box and scales to fill the viewport,
   * capped at a maximum zoom level.
   *
   * @param nodes - The nodes to fit into view
   * @param duration - Animation duration in ms (default 400)
   */
  private fitViewportToNodes(nodes: DisplayNode[], duration: number = 400): void {
    if (nodes.length === 0 || !this.svg || !this.zoomBehavior) return;

    // Half-size of a node including its decorative elements (buttons, caret, ring)
    const halfNodeExtent = NODE_RADIUS + 45;
    const padding = 80;

    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;

    for (const node of nodes) {
      if (node.x == null || node.y == null) continue;
      if (!isFinite(node.x) || !isFinite(node.y)) continue;

      minX = Math.min(minX, node.x - halfNodeExtent);
      minY = Math.min(minY, node.y - halfNodeExtent);
      maxX = Math.max(maxX, node.x + halfNodeExtent);
      maxY = Math.max(maxY, node.y + halfNodeExtent);
    }

    if (!isFinite(minX)) return; // No valid positions

    // Add padding around the content
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    const contentW = maxX - minX;
    const contentH = maxY - minY;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    // Compute scale to fit content, capped at max zoom
    const scaleToFit = Math.min(this.width / contentW, this.height / contentH);
    const maxZoom = 2.5;
    const scale = Math.min(scaleToFit, maxZoom);

    // Translate so the content center maps to the viewport center
    const tx = this.width / 2 - centerX * scale;
    const ty = this.height / 2 - centerY * scale;

    const transform = d3.zoomIdentity.translate(tx, ty).scale(scale);
    this.svg.transition().duration(duration).call(this.zoomBehavior.transform, transform);
  }

  /**
   * Click on SVG background to deselect.
   */
  private setupBackgroundClick(): void {
    if (!this.svg) return;

    const svgNode = this.svg.node();
    if (!svgNode) return;

    // Record pointer position on mousedown to detect drag-vs-click
    svgNode.addEventListener('mousedown', (event: MouseEvent) => {
      this._pointerDownPos = { x: event.clientX, y: event.clientY };
    });

    svgNode.addEventListener('click', (event: MouseEvent) => {
      // If the pointer moved significantly since mousedown, this was a
      // drag/zoom gesture — ignore the click event.
      if (this._pointerDownPos) {
        const dx = event.clientX - this._pointerDownPos.x;
        const dy = event.clientY - this._pointerDownPos.y;
        this._pointerDownPos = null;
        if (dx * dx + dy * dy > 25) return; // >5px movement = drag, not click
      }

      const target = event.target as Element | null;
      if (!target) return;
      // Check against svgNode rather than this.container, since the SVG
      // element may have been moved to a different container (responsive layout).
      if (!svgNode.contains(target)) {
        return;
      }
      if (target.closest('.viz-node')) return;
      if (target.closest('.edit-btn, .edit-button-group')) return;
      if (target.closest('.global-create-btn, .global-create-btn-inner')) return;
      // If in a link flow sub-mode or pick-node mode, cancel it instead of deselecting
      if (this.store.getEditionSubMode() !== StoreEditionMode.NONE) {
        if (this.store.getEditionSubMode() === StoreEditionMode.PICK_NODE) {
          this.cancelPickNode('backgroundClick');
        } else if (this.store.getEditionSubMode() === StoreEditionMode.DELETE_PARENT_LINK ||
            this.store.getEditionSubMode() === StoreEditionMode.DELETE_CHILD_LINK) {
          this.cancelDeleteLinkFlow('backgroundClick');
        } else {
          this.cancelLinkFlow('backgroundClick');
        }
        return;
      }

      // Click on empty space — disarm any pending delete arm
      this.editButtonRenderer?.disarmDelete();

      // Background click — deselect the node. If the selected recipe isn't
      // focused (browsing unfocused, just happens to still be the one
      // shown in the header), also deselect the recipe itself — clicking
      // empty space means "I'm done with this recipe", same as it would if
      // focused, except a focused recipe stays selected on background
      // clicks (only its node selection clears) since you're still zoomed
      // into it.
      this.store.deselect();
      if (this.store.getSelectedRecipeId() && !this.store.getFocusedRecipeId()) {
        this.deselectRecipe();
      }
    });

    // Capturing-phase touchstart to disarm pending delete on mobile.
    // D3 drag/zoom calls preventDefault on touchstart which blocks click
    // synthesis, so the click handler above never fires on mobile.
    // This fires before any stopPropagation() in button handlers.
    // Capturing-phase touch handlers for mobile.
    // D3 drag/zoom calls preventDefault on touchstart which blocks click
    // synthesis, so the click handler above never fires on mobile.
    // These fire before any stopPropagation() in button/node handlers.

    // Record touch start position for tap-vs-drag disambiguation
    svgNode.addEventListener('touchstart', (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch) {
        this._tapStartPos = { x: touch.clientX, y: touch.clientY };
      }

      const target = event.target as Element | null;
      if (!target) return;
      if (target.closest('.edit-btn, .edit-button-group')) return;
      this.editButtonRenderer?.disarmDelete();
    }, { capture: true, passive: true });

    // Detect taps on nodes (mobile) — select the node directly
    svgNode.addEventListener('touchend', async (event: TouchEvent) => {
      const touch = event.changedTouches[0];
      if (!touch || !this._tapStartPos) return;

      // Only treat as tap if finger didn't move significantly (avoid drag)
      const dx = touch.clientX - this._tapStartPos.x;
      const dy = touch.clientY - this._tapStartPos.y;
      if (dx * dx + dy * dy > 100) return; // >10px movement = drag, not tap
      this._tapStartPos = null;

      // Don't select during link flows (but allow pick-node mode through)
      if (this.store.getEditionSubMode() !== StoreEditionMode.NONE && this.store.getEditionSubMode() !== StoreEditionMode.PICK_NODE) return;

      // Check what's under the finger at release
      const elem = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!elem) return;

      // Ignore edit buttons — they have their own handlers
      if (elem.closest('.edit-btn, .edit-button-group')) return;
      // Ignore global create button
      if (elem.closest('.global-create-btn, .global-create-btn-inner')) return;

      // Tap on caret (>) → toggle expand/collapse
      if (elem.closest('.viz-node-caret')) {
        const nodeGroup = elem.closest('.viz-node');
        const nodeId = nodeGroup ? (nodeGroup as SVGElement).id?.replace('viz-node-', '') : null;
        const node = nodeId ? this.nodePool.get(nodeId) : null;
        if (node) {
          event.preventDefault();
          event.stopPropagation();
          // Reset fx/fy that D3 drag's start handler set — its end handler
          // won't fire because we called stopPropagation()
          node.fx = null;
          node.fy = null;
          this.editButtonRenderer?.disarmDelete();
          // Fold state change tracking is now handled inside
          // RecipeStore.collapseNode()/expandNode() themselves.
          await this.toggleExpandNode(node.id);
          return;
        }
      }

      // Tap on close (×) button → hide recipe from view
      if (elem.closest('.viz-node-close-btn')) {
        const nodeGroup = elem.closest('.viz-node');
        const nodeId = nodeGroup ? (nodeGroup as SVGElement).id?.replace('viz-node-', '') : null;
        const node = nodeId ? this.nodePool.get(nodeId) : null;
        // Only act if the button is actually visible — on mobile the touch
        // hitarea rect has pointer-events:all which can capture taps even
        // when the button has pointer-events:none (hidden state).
        if (node && (this.store.isNodeSelected(node.id) || this.store.isNodeHovered(node.id))) {
          event.preventDefault();
          event.stopPropagation();
          this.hideRecipe(node.recipeId);
          return;
        }
      }

      // Tap on a node → select it (bypasses D3 click which is blocked on mobile)
      const nodeGroup = elem.closest('.viz-node');
      if (nodeGroup) {
        const nodeId = (nodeGroup as SVGElement).id?.replace('viz-node-', '');
        const node = nodeId ? this.nodePool.get(nodeId) : null;
        if (node) {
          event.preventDefault();
          event.stopPropagation();
          if (this.store.getEditionSubMode() === StoreEditionMode.PICK_NODE) {
            // Pick node mode: clicking a node copies its ID to the param
            this.handleNodePick(node);
          } else if (this.store.isMultiSelectMode()) {
            this.store.toggleMultiSelectNode(node.id);
          } else if (this.store.getSelectedNode()?.id === node.id) {
            this.store.deselect();
          } else {
            this.selectNode(node);
          }
          return;
        }
      }

      // Tap on background — deselect (same as desktop click behavior)
      // In pick node mode, cancel the pick instead
      if (this.store.getEditionSubMode() === StoreEditionMode.PICK_NODE) {
        this.cancelPickNode('touchEnd:background');
      } else {
        this.editButtonRenderer?.disarmDelete();
        this.store.deselect();
        if (this.store.getSelectedRecipeId() && !this.store.getFocusedRecipeId()) {
          this.deselectRecipe();
        }
      }
    }, { capture: true, passive: false });
  }

  // ============================================================================
  // POOL OPERATIONS
  // ============================================================================

  /**
   * Load root nodes from the search API (browse mode).
   * Fetches RecipeListItem[] from the unified search endpoint and creates
   * DisplayNode instances spread across the viewport.
   *
   * @param items - Optional pre-fetched recipe list items. If omitted, fetches from API.
   */
  /**
   * Load root nodes for browse mode (one collapsed node per recipe) and
   * kick off a background load of each recipe's full graph.
   *
   * Node creation, pool/recipe bookkeeping, and the background-load loop
   * all live in RecipeStore.addRootNodes — the single implementation. This
   * wrapper only fetches the item list if not provided and does view-only
   * follow-up (viewport fit, simulation).
   */
  async addRootNodes(items?: RecipeListItem[]): Promise<void> {
    if (!items) {
      items = await searchRecipeList({ limit: 20 });
    }

    await this.store.addRootNodes(items);

    this.renderAll();
    const displayNodes = items
      .map(item => this.nodePool.get(item.mainRootNodeId))
      .filter((n): n is DisplayNode => !!n);
    this.fitViewportToNodes(displayNodes, 0);
    this.restartSimulation(0.1);
  }

  /**
   * Load a full recipe graph from the API and show it.
   *
   * The actual fetch/build/link/auto-fold pipeline lives in
   * RecipeStore.loadFullRecipe — the single implementation both this view
   * and discardChanges() rely on. This wrapper only does view-only follow-up
   * (viewport fit, simulation, side panel).
   *
   * @param recipeId - The recipe ID to load
   */
  async loadFullRecipe(recipeId: string): Promise<void> {
    await this.store.loadFullRecipe(recipeId);

    this.renderAll();
    const recipeNodes = this.store.getRecipeNodes(recipeId);
    this.fitViewportToNodes(recipeNodes.filter(n => !this.store.isNodeHidden(n.id)), 0);
    this.updateSidePanel();
    this.restartSimulation(0.1);
  }

  /**
   * Background-load a recipe's full graph into the pool without changing the
   * visual state (see RecipeStore.backgroundLoadRecipeGraph). This wrapper
   * only refreshes the side panel if the currently selected node belongs to
   * the recipe that just finished loading.
   */
  async backgroundLoadRecipeGraph(recipeId: string): Promise<void> {
    await this.store.backgroundLoadRecipeGraph(recipeId);

    if (this.store.getSelectedNode()?.recipeId === recipeId) {
      this.updateSidePanel();
    }
  }

  /**
   * Expand a node: if subgraph not loaded, fetch it; otherwise unhide descendants.
   * If the node is already expanded, collapse it instead.
   *
   * The cascade/auto-unfold/subgraph-fetch logic lives in RecipeStore.expandNode
   * — the single implementation both this view and List View call, so a node
   * expands identically no matter which view triggers it. This wrapper only
   * does view-only follow-up (viewport fit, simulation, side panel, toasts).
   *
   * @param nodeId - The node ID to expand
   */
  async expandNode(nodeId: string): Promise<void> {
    const node = this.nodePool.get(nodeId);
    if (!node) return;

    // If already showing children, fold instead
    if (this.store.isNodeExpanded(node.id)) {
      this.foldNode(nodeId);
      return;
    }

    try {
      await this.store.expandNode(nodeId);
    } catch (error) {
      console.error('Failed to expand node', nodeId, error);
      this.renderAll();
      return;
    }

    // Update rendering (the caret will show ▼ for auto-folded nodes)
    this.renderAll();
    this.fitViewportToNodes(Array.from(this.nodePool.values()).filter(n => !this.store.isNodeHidden(n.id)));
    this.updateSidePanel();
    this.restartSimulation(0.15);
  }

  /**
   * Fold a node: hide its descendants (cascading, with auto-fold of any
   * ancestor that lost its last visible child). Folding the main root hides
   * every other node of the recipe at once and drops focus.
   *
   * The cascade/auto-fold logic lives in RecipeStore.collapseNode — the
   * single implementation both this view and List View call, so a node
   * collapses identically no matter which view triggered it. This wrapper
   * only does view-only follow-up (viewport fit, simulation, side panel).
   *
   * Implements the folding specification in docs/wiki/folding.md.
   *
   * @param nodeId - The node ID to collapse
   */
  foldNode(nodeId: string): void {
    const node = this.nodePool.get(nodeId);
    if (!node) return;

    this.store.collapseNode(nodeId);

    this.renderAll();
    this.fitViewportToNodes(Array.from(this.nodePool.values()).filter(n => !this.store.isNodeHidden(n.id)));

    // If the selected node was hidden (or its parent folded) by this
    // operation, select the folded node instead of leaving a stale side panel.
    const selectedNode = this.store.getSelectedNode();
    if (selectedNode && (this.store.isNodeHidden(selectedNode.id) || !this.store.isNodeExpanded(selectedNode.id))) {
      this.selectNode(node);
    } else {
      this.updateSidePanel();
    }

    this.restartSimulation(0.05);
  }

  /**
   * Toggle expand/collapse for a node.
   *
   * Folding a main root also unfocuses its recipe (see RecipeStore.collapseNode)
   * — if that recipe is the one currently being edited and has unsaved
   * changes, confirm save/discard first, same as exiting edit mode or
   * switching the selected recipe. This is the single entry point both the
   * header's focus button and the node's own caret call, so the guard
   * covers both.
   *
   * @param nodeId - The node ID to toggle
   */
  async toggleExpandNode(nodeId: string): Promise<void> {
    const node = this.nodePool.get(nodeId);
    if (!node) return;

    // Use actual children visibility:
    // if children are currently visible → fold them
    // if children are hidden or not loaded yet → expand them
    if (this.hasVisibleChildren(node)) {
      if (node.isMainRoot && this.store.isEditMode() && this.store.hasUnsavedChanges()) {
        showUnsavedChangesDialog({
          recipeTitle: this.getSelectedRecipeTitle() ?? 'la recepta',
          isMobile: this._isMobile,
          onSave: async () => {
            try {
              await this.saveRecipeChanges();
              this.foldNode(nodeId);
            } catch {
              // Save failed — stay focused
            }
          },
          onDiscard: () => {
            this.discardChanges();
            this.foldNode(nodeId);
          },
          onCancel: () => {
            // Stay focused — don't fold
          },
        });
        return;
      }
      this.foldNode(nodeId);
    } else {
      await this.expandNode(nodeId);
    }
  }

  /**
   * Remove all nodes of a recipe from the pool.
   *
   * @param recipeId - The recipe ID to remove
   */
  clearRecipe(recipeId: string): void {
    // Collect all node IDs for this recipe
    const nodeIdsToRemove: string[] = [];
    this.nodePool.forEach((node) => {
      if (node.recipeId === recipeId) {
        nodeIdsToRemove.push(node.id);
      }
    });

    this.store.removeNodesFromPool(nodeIdsToRemove);
    this.store.removeRecipe(recipeId);

    // If the cleared recipe was selected, also deselect recipe.
    // Clear the change tracker first since the recipe is already deleted
    // from the backend — there's nothing to save or discard.
    if (this.store.getSelectedRecipeId() === recipeId) {
      this.store.clearChangeTracker();
      this.store.selectRecipe(null);
    }

    // Clear selection if the selected node belonged to this recipe
    if (this.store.getSelectedNode()?.recipeId === recipeId) {
      this.store.deselect();
    }

    this.renderAll();
    this.updateRecipeHeader();
    this.restartSimulation(0.1);
  }

  /**
   * Hide a recipe from the visualizer (non-destructive).
   *
   * Removes the recipe's nodes from the pool and stores the recipe ID in
   * _hiddenRecipes so it can be restored later via restoreRecipe().
   * Unlike clearRecipe(), this does NOT delete the recipe from the backend.
   */
  hideRecipe(recipeId: string): void {
    this.store.hideRecipe(recipeId);

    // Collect all node IDs for this recipe
    const nodeIdsToRemove: string[] = [];
    this.nodePool.forEach((node) => {
      if (node.recipeId === recipeId) {
        nodeIdsToRemove.push(node.id);
      }
    });

    this.store.removeNodesFromPool(nodeIdsToRemove);
    this.store.removeRecipe(recipeId);

    // Deselect if the selected node/recipe belonged to this recipe
    if (this.store.getSelectedRecipeId() === recipeId) {
      this.store.clearChangeTracker();
      this.store.selectRecipe(null);
    }
    if (this.store.getSelectedNode()?.recipeId === recipeId) {
      this.store.deselect();
    }

    this.renderAll();
    this.updateRecipeHeader();
    this.restartSimulation(0.1);
  }

  /**
   * Restore a previously hidden recipe back into the visualizer.
   * Reloads the full recipe graph from the API.
   */
  async restoreRecipe(recipeId: string): Promise<void> {
    if (!this.store.isRecipeHidden(recipeId)) return;
    this.store.showRecipe(recipeId);
    await this.loadFullRecipe(recipeId);
  }

  /**
   * Return the list of recipe IDs that have been hidden from view.
   */
  getHiddenRecipes(): string[] {
    return Array.from(this.store.getHiddenRecipes());
  }

  /**
   * Apply ingredient-search results to the graph.
   *
   * Recipes that match the search stay visible (keeping their positions);
   * new matching recipes are added as root nodes; non-matching recipes
   * are hidden from view. After the update, the viewport fits all nodes.
   *
   * The actual restore/hide/add-and-background-load pipeline lives in
   * RecipeStore.applySearchResults — the single implementation. This
   * wrapper only does view-only follow-up (viewport fit, simulation).
   *
   * @param items - Search results from searchRecipeList
   */
  async applySearchResults(items: RecipeListItem[]): Promise<void> {
    await this.store.applySearchResults(items);

    this.renderAll();
    this.fitViewportToNodes(Array.from(this.nodePool.values()).filter(n => !this.store.isNodeHidden(n.id)), 0);
    this.restartSimulation(0.1);
  }

  /**
   * Handle "Afegeix elaboració" action.
   * Fetches ingredient recipe subgraphs and opens a popup for selection.
   */
  private async handleFindIngredientRecipes(
    nodeId: string,
    ingredientId: string,
    ingredientName: string
  ): Promise<void> {
    try {
      const { getIngredientRecipeSubgraphs } = await import('../api/ingredients.js');
      const subgraphs = await getIngredientRecipeSubgraphs(ingredientId);

      const { IngredientRecipeSelector } = await import('../recipe_renderer/components/IngredientRecipeSelector.js');
      const selector = new IngredientRecipeSelector(
        subgraphs,
        ingredientName,
        async (subgraphNodes) => {
          await this.importSubgraphIntoPool(nodeId, subgraphNodes);
        }
      );

      await selector.open();
    } catch (error) {
      console.error('Failed to fetch ingredient recipes:', error);
      showToast('Error cercant receptes per a l\'ingredient', 'error');
    }
  }

  /**
   * Get the conversion factor from one unit to another.
   * Returns null if no conversion exists.
   */
  private async getUnitConversionFactor(fromUnitId: string, toUnitId: string): Promise<number | null> {
    if (fromUnitId === toUnitId) return 1;

    try {
      const { getUnitConversions } = await import('../api/units.js');
      const conversions = await getUnitConversions(fromUnitId);
      const conversion = conversions.find(c => c.toUnitId === toUnitId);
      return conversion?.factor ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Import a subgraph into the node pool as children of the given parent node.
   * Similar to DisplayGraph.importSubgraph() but operates on the DisplayNode pool.
   */
  private async importSubgraphIntoPool(
    parentNodeId: string,
    subgraphNodes: Record<string, import('../api/types.js').IngredientRecipeSubgraphNode>
  ): Promise<void> {
    const parentNode = this.nodePool.get(parentNodeId);
    if (!parentNode) return;

    const origRootId = Object.keys(subgraphNodes)[0];
    const nonRootIds = Object.keys(subgraphNodes).filter(id => id !== origRootId);

    if (nonRootIds.length === 0) {
      return; // Nothing to import
    }

    // Calculate scale factor for quantities
    let scaleFactor: number | null = null;
    const parentQty = parentNode.data.ingredientInfo?.quantity;
    const rootNode = subgraphNodes[origRootId];
    const importedRootQty = rootNode.ingredientInfo?.quantity;

    if (parentQty && importedRootQty) {
      // Both have quantities - try to calculate scale factor
      const conversionFactor = await this.getUnitConversionFactor(
        importedRootQty.unit,
        parentQty.unit
      );

      if (conversionFactor !== null) {
        // Units are compatible - calculate scale factor
        // scaleFactor = parentQty / (importedRootQty * conversionFactor)
        // This gives us how much to multiply imported quantities by
        const importedRootInParentUnits = importedRootQty.amount * conversionFactor;
        if (importedRootInParentUnits > 0) {
          scaleFactor = parentQty.amount / importedRootInParentUnits;
        }
      }
      // If conversionFactor is null, units are incompatible - scaleFactor stays null
    }
    // If either quantity is missing, scaleFactor stays null

    // Generate new unique IDs
    const idMapping: Record<string, string> = {};
    for (const origId of nonRootIds) {
      idMapping[origId] = `imported_${Date.now()}_${origId}`;
    }
    // Also used for remapping transformation `parameters` (see
    // remapTransformationParams()) — a param referencing the skipped
    // root itself must become a reference to `parentNodeId`, since the
    // root's own children/transformations end up grafted onto it.
    const paramIdMapping: Record<string, string> = { ...idMapping, [origRootId]: parentNodeId };

    // Renormalize imported stepOrder values to land entirely after
    // whatever the target recipe already has — we can't know how an
    // imported node's step should interleave with the target's own, so
    // the whole imported batch goes at the back, keeping only its own
    // internal relative order (source steps 8, 12, 9 — ascending — stay
    // in that same relative sequence, just shifted to start right after
    // the target's current last step).
    let targetMaxStepOrder = 0;
    for (const existing of this.nodePool.values()) {
      if (existing.recipeId === parentNode.recipeId) {
        targetMaxStepOrder = Math.max(targetMaxStepOrder, existing.data.stepOrder);
      }
    }
    const importedStepOrder = [...nonRootIds].sort(
      (a, b) => subgraphNodes[a].stepOrder - subgraphNodes[b].stepOrder,
    );
    const stepOrderMapping = new Map<string, number>();
    importedStepOrder.forEach((id, i) => stepOrderMapping.set(id, targetMaxStepOrder + i + 1));

    // Create DisplayNodes for non-root nodes
    const newDisplayNodes: DisplayNode[] = [];
    const baseX = parentNode.x ?? this.width / 2;
    const baseY = parentNode.y ?? this.height * 0.3;

    for (let i = 0; i < nonRootIds.length; i++) {
      const origId = nonRootIds[i];
      const origNode = subgraphNodes[origId];
      const newId = idMapping[origId];

      // Remap children references
      const mappedChildren = origNode.children
        .filter(cid => cid !== origRootId)
        .map(cid => idMapping[cid] ?? cid);

      // Scale or remove quantity
      let scaledQuantity: import('../api/types.js').Quantity | undefined = undefined;
      if (origNode.ingredientInfo?.quantity) {
        if (scaleFactor !== null) {
          // Scale the quantity
          const origQty = origNode.ingredientInfo.quantity;
          const conversionFactor = await this.getUnitConversionFactor(
            origQty.unit,
            parentQty!.unit
          );

          if (conversionFactor !== null) {
            // Convert and scale
            const amountInParentUnits = origQty.amount * conversionFactor;
            scaledQuantity = {
              amount: amountInParentUnits * scaleFactor,
              unit: parentQty!.unit,
            };
          } else {
            // Units incompatible - remove quantity
            scaledQuantity = undefined;
          }
        } else {
          // No scale factor - remove quantity
          scaledQuantity = undefined;
        }
      }

      // Create RecipeNode data
      const newNodeData: import('../api/types.js').RecipeNode = {
        id: newId,
        name: origNode.name,
        description: origNode.description,
        imageUrl: origNode.imageUrl,
        autoFold: false,
        ingredientInfo: origNode.ingredientInfo ? {
          ingredient: { ...origNode.ingredientInfo.ingredient },
          description: origNode.ingredientInfo.description,
          quantity: scaledQuantity,  // Use scaled or removed quantity
        } : undefined,
        transformationInfos: (origNode.transformationInfos ?? []).map(ti => ({
          transformation: ti.transformation ? { ...ti.transformation } : undefined,
          description: ti.description,
          parameters: remapTransformationParams(ti.parameters, paramIdMapping),
          duration: ti.duration,
          attentionDuration: ti.attentionDuration,
        })),
        toolInfos: origNode.toolInfos?.map(ti => ({
          tool: { ...ti.tool },
          description: ti.description,
          fresh: ti.fresh,
          hidden: ti.hidden,
          counts: ti.counts,
        })),
        children: mappedChildren,
        stepOrder: stepOrderMapping.get(origId) ?? targetMaxStepOrder + 1,
      };

      // Create DisplayNode
      const displayNode = this.store.createDisplayNodeFromApi(newNodeData, parentNode.recipeId, false);
      displayNode.x = baseX + 80 + (i % 3) * 30;
      displayNode.y = baseY + 100 + Math.floor(i / 3) * 60;

      newDisplayNodes.push(displayNode);
      this.store.addNodes([displayNode]);
    }

    // Create links
    const newLinks: Link<DisplayNode>[] = [];
    for (const origId of nonRootIds) {
      const origNode = subgraphNodes[origId];
      const sourceId = idMapping[origId];

      for (const childId of origNode.children) {
        if (childId === origRootId) continue;
        const targetId = idMapping[childId];
        if (targetId) {
          const sourceNode = this.nodePool.get(sourceId);
          const targetNode = this.nodePool.get(targetId);
          if (sourceNode && targetNode) {
            newLinks.push({
              source: sourceNode,
              target: targetNode,
              shape: { x: 0, y: 0 },
              active: true,
            });
          }
        }
      }
    }

    // Link parent to imported root's children
    for (const childId of rootNode.children) {
      const newChildId = idMapping[childId];
      if (newChildId) {
        const targetNode = this.nodePool.get(newChildId);
        if (targetNode) {
          newLinks.push({
            source: parentNode,
            target: targetNode,
            shape: { x: 0, y: 0 },
            active: true,
          });
        }
      }
    }

    // Update parent's children array
    const newChildIds = rootNode.children
      .map(cid => idMapping[cid])
      .filter((id): id is string => id !== undefined);
    this.store.updateNodeChildren(parentNode.id, [...(parentNode.data.children || []), ...newChildIds]);

    // Transfer root's transformations/tools to parent if parent doesn't
    // already have any of its own.
    if (rootNode.transformationInfos?.length && !parentNode.data.transformationInfos?.length) {
      const transformationInfos = rootNode.transformationInfos.map(ti => ({
        transformation: ti.transformation ? { ...ti.transformation } : undefined,
        description: ti.description,
        parameters: remapTransformationParams(ti.parameters, paramIdMapping),
        duration: ti.duration,
        attentionDuration: ti.attentionDuration,
      }));
      this.store.updateNodeData(parentNode.id, { transformationInfos });
      this.store.updateNodeName(parentNode.id, resolveNodeTitle(parentNode.data));
      this.store.updateNodeType(parentNode.id, this.getNodeType(parentNode.data));
    }
    if (rootNode.toolInfos?.length && !parentNode.data.toolInfos?.length) {
      const toolInfos = rootNode.toolInfos.map(ti => ({
        tool: { ...ti.tool },
        description: ti.description,
        fresh: ti.fresh,
        hidden: ti.hidden,
        counts: ti.counts,
      }));
      this.store.updateNodeData(parentNode.id, { toolInfos });
    }

    // Add links to pool
    this.store.addLinks(newLinks);

    // Re-render
    this.renderAll();
    this.restartSimulation(0.2);

    // Keep parent selected
    if (this.store.getSelectedNode()?.id === parentNodeId) {
      this.updateSidePanel();
    }
  }

  /**
   * Toggle focus on the currently selected recipe: folds/unfolds its main
   * root, which (per RecipeStore.expandNode/collapseNode) also
   * focuses/unfocuses it — the one place other recipes get hidden so
   * this one is the only thing shown in the graph/list view. Reuses
   * toggleExpandNode(), the same fold/unfold entry point the node's own
   * caret uses, so this is exactly equivalent to clicking that caret.
   */
  private toggleFocusSelectedRecipe(): void {
    const recipeId = this.store.getSelectedRecipeId();
    if (!recipeId) return;
    const entry = this.store.getRecipeEntry(recipeId);
    if (!entry) return;
    const mainRootId = entry.rootNodeIds[entry.mainRootIndex];
    if (!mainRootId) return;
    void this.toggleExpandNode(mainRootId);
  }

  /**
   * Refresh the current recipe from the API, discarding temporary modifications.
   */
  private async handleRefreshRecipe(): Promise<void> {
    if (!this.store.getSelectedRecipeId()) return;
    const recipeId = this.store.getSelectedRecipeId()!;

    const wasEditing = this.store.isEditMode();

    // In edit mode with unsaved changes, prompt first
    if (wasEditing && this.store.hasUnsavedChanges()) {
      showUnsavedChangesDialog({
        recipeTitle: this.getSelectedRecipeTitle() ?? 'la recepta',
        isMobile: this._isMobile,
        onSave: async () => {
          try {
            await this.saveRecipeChanges();
            await this.reloadRecipe(recipeId, true);
          } catch {
            // Save failed
          }
        },
        onDiscard: async () => {
          await this.reloadRecipe(recipeId, true);
        },
        onCancel: () => {
          // Do nothing
        },
      });
    } else {
      await this.reloadRecipe(recipeId, wasEditing);
    }
  }

  /**
   * Capture a recipe's main-root fold state, on-screen position, and focus
   * state before a destructive clearRecipe() + loadFullRecipe() reload
   * cycle (used by refresh and by save, which reloads to pick up
   * server-assigned permanent IDs). Call reapplyFocusImmediately() right
   * after clearRecipe() (synchronously, before any await) and
   * restoreRecipeViewState() once loadFullRecipe() resolves.
   */
  private captureRecipeViewState(recipeId: string): {
    wasFolded: boolean;
    wasFocused: boolean;
    oldX?: number;
    oldY?: number;
  } {
    const recipeEntry = this.store.getRecipeEntry(recipeId);
    const mainRootId = recipeEntry?.rootNodeIds[recipeEntry.mainRootIndex];
    const mainRootBefore = mainRootId ? this.nodePool.get(mainRootId) : undefined;
    return {
      wasFolded: mainRootId ? !this.store.isNodeExpanded(mainRootId) : false,
      // Was this recipe focused (i.e. hiding every other recipe)?
      // clearRecipe() removes it from the pool, and removeRecipe() correctly
      // unfocuses when the removed recipe was the focused one.
      wasFocused: mainRootId != null && this.store.getFocusedRecipeId() === recipeId,
      oldX: mainRootBefore?.x,
      oldY: mainRootBefore?.y,
    };
  }

  /**
   * Re-hide the other recipes immediately — call synchronously right after
   * clearRecipe(), before awaiting the reload's API call. clearRecipe()
   * un-focuses (revealing every other recipe) as a side effect of removing
   * this recipe from the pool; without this, that revealed state sits there
   * for the entire duration of the network request and gets painted — a
   * visible flash of every recipe appearing before this one reloads. Doing
   * it here instead means focus never actually leaves the DOM: both the
   * un-focus and the re-focus happen in the same synchronous tick, before
   * the browser gets a chance to paint in between.
   */
  private reapplyFocusImmediately(recipeId: string, viewState: { wasFocused: boolean }): void {
    if (viewState.wasFocused) {
      this.store.focusRecipe(recipeId);
    }
  }

  /**
   * Restore fold state and position once loadFullRecipe() resolves.
   * Re-resolves the main root by recipe entry rather than by the old node
   * id, since save can replace node ids entirely. Focus was already
   * restored by reapplyFocusImmediately() and needs no further action here.
   */
  private restoreRecipeViewState(
    recipeId: string,
    viewState: { wasFolded: boolean; oldX?: number; oldY?: number },
  ): void {
    const { wasFolded, oldX, oldY } = viewState;
    const newRecipeEntry = this.store.getRecipeEntry(recipeId);
    const newMainRootId = newRecipeEntry?.rootNodeIds[newRecipeEntry.mainRootIndex];
    const mainRootAfter = newMainRootId ? this.nodePool.get(newMainRootId) : undefined;

    // Shift the whole recipe (root + children, positioned relative to it)
    // back so the root lands exactly where it was before the reload.
    if (mainRootAfter && oldX != null && oldY != null && mainRootAfter.x != null && mainRootAfter.y != null) {
      const dx = oldX - mainRootAfter.x;
      const dy = oldY - mainRootAfter.y;
      if (dx !== 0 || dy !== 0) {
        for (const node of this.nodePool.values()) {
          if (node.recipeId !== recipeId) continue;
          if (node.x != null) node.x += dx;
          if (node.y != null) node.y += dy;
        }
        this.renderAll();
        this.fitViewportToNodes(
          this.store.getRecipeNodes(recipeId).filter(n => !this.store.isNodeHidden(n.id)),
          0,
        );
      }
    }

    // Restore the root's fold state
    if (wasFolded && newMainRootId && this.nodePool.has(newMainRootId)) {
      this.foldNode(newMainRootId);
    }
  }

  /**
   * Reload a recipe from the API.
   *
   * Fetches first, before touching anything on screen: if the request
   * fails, the currently-displayed recipe is left completely untouched
   * instead of vanishing with nothing to replace it. Once the data is in
   * hand, the clear-and-rebuild is done as one synchronous block (no
   * `await` in between) so the browser has no opportunity to paint an
   * in-between frame — the recipe is never actually removed from the
   * screen, it's replaced in the same tick.
   */
  private async reloadRecipe(recipeId: string, enterEditMode: boolean): Promise<void> {
    let recipe: Recipe;
    try {
      recipe = await this.store.fetchRecipe(recipeId);
    } catch (error) {
      console.error('Failed to reload recipe:', error);
      showToast('No s\'ha pogut recarregar la recepta', 'error');
      return;
    }

    const viewState = this.captureRecipeViewState(recipeId);
    this.clearRecipe(recipeId);
    // Re-focus immediately so the "unfocused" state clearRecipe() leaves
    // behind is never actually painted — see reapplyFocusImmediately()'s
    // doc comment. Nothing here awaits anything, so none of this is
    // visible before the recipe reappears in the next line.
    this.reapplyFocusImmediately(recipeId, viewState);
    this.store.applyLoadedRecipe(recipeId, recipe);
    this.restoreRecipeViewState(recipeId, viewState);

    this.renderAll();
    const recipeNodes = this.store.getRecipeNodes(recipeId);
    this.fitViewportToNodes(recipeNodes.filter(n => !this.store.isNodeHidden(n.id)), 0);
    this.updateSidePanel();
    this.restartSimulation(0.1);

    // Re-enter edit mode if needed. this.enterEditMode() (not
    // store.enterEditMode()) re-snapshots the store's change tracker —
    // needed since clearRecipe() above already cleared it. enterEditMode()
    // no longer auto-expands/focuses, so this can't undo the fold state
    // restoreRecipeViewState() just restored.
    if (enterEditMode) {
      this.store.setEditMode(true);
      this.enterEditMode();
      this._fireEditModeChanged(true);
    }
  }

  /**
   * Synchronise the main root's folded state with the visibility of non-root nodes.
   * If any non-main-root node of the recipe is visible, the main root is unfolded.
   * If ALL non-root nodes are hidden or absent, the main root is folded.
   *
   * Call after any operation that adds, removes, hides, or shows nodes.
   */
  // ============================================================================
  // CAPABILITY TOGGLES
  // ============================================================================

  get editMode(): boolean {
    return this.store.isEditMode();
  }

  set editMode(value: boolean) {
    if (value === this.store.isEditMode()) return;

    if (value) {
      // this.enterEditMode() (not store.enterEditMode(), which only flips the
      // flag and dims inactive nodes) also snapshots the recipe graph into
      // the store's change tracker (store.initializeChangeTracker()) —
      // without that, every self-tracking mutation silently no-ops and the
      // Save/Discard buttons never appear.
      this.store.setEditMode(true);
      this.enterEditMode();
      this._fireEditModeChanged(true);
    } else {
      // this.exitEditMode() (not store.exitEditMode()) handles the unsaved-
      // changes confirmation dialog and only sets _editMode to false if the
      // user actually confirms the exit (discard or save). If the user
      // cancels, edit mode stays on.
      this.exitEditMode();
    }
  }

  /**
   * Fire the edit-mode-changed callbacks.
   */
  private _fireEditModeChanged(editing: boolean): void {
    if (!editing && this.store.isMultiSelectMode()) {
      // Multi-select is purely an editing concept — turn it off along
      // with edit mode itself.
      this.store.setMultiSelectMode(false);
    }
    if (this.recipeHeaderStrip) {
      this.recipeHeaderStrip.setEditModeActive(editing);
      // Leaving edit mode: restore the reload button if a recipe is
      // still selected (entering it already hid reload via setEditModeActive).
      if (!editing && this.store.getSelectedRecipeId()) {
        this.recipeHeaderStrip.showRefreshButton();
      }
    }
    if (this.editToolbar) {
      if (editing) {
        this.updateSaveButtonVisibility();
      }
      this.updateEditToolbarButtons();
    }
    for (const cb of this._onEditModeChangedCallbacks) {
      try { cb(editing); } catch (e) { console.error('onEditModeChanged error:', e); }
    }
  }

  /**
   * Recompute every EditToolbar button's visibility/mode from current
   * selection + focus state, AND the toolbar bar's own visibility. Single
   * source of truth for the three places that need this (entering/leaving
   * edit mode, mounting the toolbar, and any selection/hover change) so
   * they can't drift out of sync.
   *
   * In "global" edition mode (no recipe focused) only [+] and, with a
   * recipe selected, [×] delete-recipe apply — every other edit-mode
   * button is scoped to nodes within a focused recipe, so they're all
   * hidden regardless of what's selected while browsing unfocused.
   *
   * [i]/[⚘]/[⚗]/[⚗ׯ] used to be edit-mode-independent exceptions here —
   * they've since moved to List View's own row header (see
   * ListNodeItem.ts), so this toolbar is purely edit-mode-gated again.
   *
   * [↥]/[↟] (add parent, transform) moved there too, for a *single*
   * selected node — that row already has its own [↥]/[↟] then. They stay
   * here only for the multi-select case (2+ nodes), which has no row of
   * its own to live on. [+] is the one exception: it always stays,
   * regardless of selection — "add a new node" doesn't depend on there
   * being a row to attach it to.
   */
  private updateEditToolbarButtons(): void {
    if (!this.editToolbar) return;

    const inEditMode = this.store.isEditMode();
    const recipeFocused = !!this.store.getFocusedRecipeId();
    const count = (inEditMode && recipeFocused) ? this.selectedNodeCount() : 0;

    if (inEditMode) {
      this.editToolbar.show();
    } else {
      this.editToolbar.hide();
    }

    this.editToolbar.setCreateVisible(inEditMode);
    this.editToolbar.setDeleteRecipeVisible(inEditMode && !recipeFocused && !!this.store.getSelectedRecipeId());
    this.editToolbar.setCreateAsChildMode(count >= 2);
    this.editToolbar.setEditEnabled(count === 1);
    this.editToolbar.setEditButtonVisible(!this.isWideScreen());
    // 2+ only — a single selected node has its own [↥]/[↟] on its row now.
    this.editToolbar.setAddParentEnabled(count >= 2);
    this.editToolbar.setTransformEnabled(count >= 2);
    this.editToolbar.setMakeMainRootVisible(recipeFocused && this.canMakeSelectedNodeMainRoot());
    this.editToolbar.setMultiSelectVisible(inEditMode && recipeFocused);
    this.editToolbar.setMultiSelectActive(recipeFocused && this.store.isMultiSelectMode());
    this.editToolbar.setDeleteAllVisible(count >= 1);
  }

  // ============================================================================
  // RECIPE SELECTION
  // ============================================================================

  /**
   * Detect whether the current device is mobile based on viewport width
   * and touch support.
   */
  private detectMobile(): boolean {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth < 1024;
    return isTouchDevice && isSmallScreen;
  }

  /**
   * Whether the viewport is at the wide-screen layout breakpoint — same
   * `lg:` (1024px) cutoff the rest of the responsive layout uses.
   */
  private isWideScreen(): boolean {
    return window.innerWidth >= 1024;
  }

  /**
   * Returns whether the visualizer is running on a mobile device.
   */
  isMobile(): boolean {
    return this._isMobile;
  }

  /**
   * Get the currently selected recipe ID.
   */
  getSelectedRecipeId(): string | null {
    return this.store.getSelectedRecipeId();
  }

  /**
   * Set the selected recipe ID. Updates the header strip and fires callback.
   * If there are unsaved changes on the current recipe, shows a confirmation
   * dialog before switching.
   */
  setSelectedRecipeId(recipeId: string | null): void {
    if (this.store.getSelectedRecipeId() === recipeId) return;

    const previousId = this.store.getSelectedRecipeId();

    // If there's a change tracker with unsaved changes, prompt the user
    if (previousId !== null && this.store.hasUnsavedChanges()) {
      showUnsavedChangesDialog({
        recipeTitle: this.getSelectedRecipeTitle() ?? 'la recepta',
        isMobile: this._isMobile,
        onSave: async () => {
          try {
            await this.saveRecipeChanges();
            this.switchSelectedRecipe(recipeId, previousId);
          } catch {
            // Save failed — stay on current recipe
          }
        },
        onDiscard: () => {
          this.discardChanges();
          this.switchSelectedRecipe(recipeId, previousId);
        },
        onCancel: () => {
          // Stay on current recipe
        },
      });
      return; // Don't switch yet — will be called from dialog handlers
    }

    this.switchSelectedRecipe(recipeId, previousId);
  }

  /**
   * Internal: perform the recipe switch after any unsaved changes are resolved.
   */
  private switchSelectedRecipe(recipeId: string | null, previousId: string | null): void {
    // Leaving a recipe (deselecting, or switching straight to a different
    // one) — fold its main root first if it was expanded/focused, so focus
    // never keeps pointing at a recipe that's no longer selected.
    if (previousId !== null && previousId !== recipeId) {
      const previousEntry = this.store.getRecipeEntry(previousId);
      if (previousEntry) {
        const previousRootId = previousEntry.rootNodeIds[previousEntry.mainRootIndex];
        const previousMainRoot = this.nodePool.get(previousRootId);
        if (previousMainRoot && !!this.store.isNodeExpanded(previousMainRoot.id)) {
          this.foldNode(previousRootId);
        }
      }
    }

    this.store.selectRecipe(recipeId);

    // When switching recipes in edit mode, update inactive states so that only
    // the selected recipe's nodes are active and all others are dimmed.
    // If recipeId is null (deselected), no nodes are dimmed.
    if (this.store.isEditMode()) {
      this.store.setNodesInactiveWhere(node => recipeId !== null && node.recipeId !== recipeId);

      // Initialize or re-initialize the change tracker for the new recipe
      // so edits are tracked and the save button appears.
      if (recipeId !== null) {
        const newEntry = this.store.getRecipeEntry(recipeId);
        if (newEntry?.fullyLoaded) {
          try {
            this.store.initializeChangeTracker(recipeId);
          } catch (err) {
            console.error('Failed to initialize ChangeTracker on recipe switch:', err);
          }
        }
      } else {
        // Deselecting the recipe — clear the change tracker
        this.store.clearChangeTracker();
      }
    }

    // Neither the header strip (title/description/quantity/refresh vs.
    // save-discard/focus) nor the ingredients/preparation tab visibility is
    // set here — store.selectRecipe() above already triggered
    // updateRecipeHeader() and the tab sync via the store subscription,
    // which are the single places that know the edit-mode-aware header
    // rules and can react no matter which code path changed selection.
    // Duplicating the header logic here previously caused the reload
    // button to incorrectly reappear during edit mode, since this block
    // didn't know about that rule.

    this.onRecipeSelectionChangedCallback?.(recipeId);

    // If selection changed due to clearing a recipe, deselect node if it belonged to old recipe
    if (previousId !== null && recipeId === null && this.store.getSelectedNode()?.recipeId === previousId) {
      this.store.deselect();
    }

    // Re-render to update visual state
    this.renderAll();
  }

  /**
   * Deselect the currently selected recipe. Goes through setSelectedRecipeId()
   * so unsaved changes prompt for save/discard/cancel first (§2.3.1), and the
   * previously-selected node is deselected too if it belonged to this recipe.
   */
  deselectRecipe(): void {
    this.setSelectedRecipeId(null);
  }

  /**
   * Register a callback for when the selected recipe changes.
   */
  onRecipeSelectionChanged(callback: (recipeId: string | null) => void): void {
    this.onRecipeSelectionChangedCallback = callback;
  }

  /**
   * Get the current edition sub-mode (for link add/delete flows).
   */
  getEditionSubMode(): EditionMode {
    return this.store.getEditionSubMode() as any as EditionMode;
  }

  /**
   * Set the edition sub-mode.
   */
  setEditionSubMode(mode: EditionMode): void {
    this.store.setEditionSubMode(mode as any);
  }

  /**
   * Get the current yield scaling factor.
   * All displayed quantities are multiplied by this factor.
   */
  getScalingFactor(): number {
    return this._scalingFactor;
  }

  /**
   * Register a callback for when the user wants to create a new recipe
   * (triggered by clicking (+) when no recipe is selected).
   */
  onCreateNewRecipe(callback: () => void): void {
    this._onCreateNewRecipeCallback = callback;
  }

  /**
   * Register a callback fired after a successful save.
   * Intended for the home page to refresh its root node cache.
   */
  onRecipeSaved(callback: () => void): void {
    this._onRecipeSavedCallback = callback;
  }

  /**
   * Register a callback for when focus changes (recipe expanded/folded).
   * Called with `true` and the recipe ID when a recipe is expanded (focused),
   * and with `false` when folded back.
   */
  onFocusChanged(callback: (focused: boolean, recipeId?: string) => void): void {
    this._onFocusChangedCallback = callback;
  }

  /**
   * Called when the user changes the yield amount and/or unit in the header strip.
   * Computes a scaling factor and refreshes the panels.
   *
   * @param newAmount - The new yield amount (user-specified)
   * @param newUnit - The new yield unit (user-specified)
   */
  handleYieldChange(newAmount: number, newUnit: string): void {
    if (!this._originalRootQty) return;

    const { amount: origAmount, unit: origUnit } = this._originalRootQty;

    let factor = 1;

    if (origUnit === newUnit) {
      // Same unit: factor = new / original
      factor = newAmount / origAmount;
    } else {
      // Different unit: convert original amount to new unit first
      // Fetch conversion factor, then compute scaling factor
      this.computeAndApplyYieldChange(origAmount, origUnit, newAmount, newUnit);
      return;
    }

    this._scalingFactor = factor;
    this.refreshScaledPanels();
  }

  /**
   * Async helper: fetch unit conversion factor, compute scaling, refresh.
   */
  private async computeAndApplyYieldChange(
    origAmount: number,
    origUnit: string,
    newAmount: number,
    newUnit: string,
  ): Promise<void> {
    try {
      const { getUnitConversions } = await import('../api/units.js');
      // Get conversions FROM the original unit
      const conversions = await getUnitConversions(origUnit);
      // Find one that goes to newUnit
      const conv = conversions.find(c => c.toUnitId === newUnit);
      if (conv) {
        // Convert original amount to new unit
        const convertedOrig = origAmount * conv.factor;
        this._scalingFactor = newAmount / convertedOrig;
      } else {
        // No conversion found — reset to no scaling
        this._scalingFactor = 1;
      }
    } catch {
      this._scalingFactor = 1;
    }
    this.refreshScaledPanels();
  }

  /**
   * Refresh the side panel node card to reflect the current scaling factor.
   *
   * The standalone Ingredients/Preparation views don't apply scaling —
   * `_scalingFactor` is ephemeral Graph-View-only display state (not
   * store state), same as List View, which has never reflected it either.
   * Only this view's own side panel (and its node cards) show scaled
   * quantities.
   */
  private refreshScaledPanels(): void {
    if (this.store.getSelectedNode() && this.nodeVisualizer) {
      this.nodeVisualizer.setScalingFactor(this._scalingFactor);
      this.nodeVisualizer.updateNode(this.store.getSelectedNode());
    }
  }

  /**
   * Apply the current scaling factor to a quantity amount.
   */
  private scaleAmount(amount: number): number {
    return amount * this._scalingFactor;
  }

  /**
   * Refresh the header strip's quantity display for the current edit mode state.
   * Called when entering or exiting edit mode to toggle editable/read-only.
   */
  private refreshHeaderQuantityDisplay(): void {
    if (!this.recipeHeaderStrip || !this.store.getSelectedRecipeId()) return;
    const recipeEntry = this.store.getRecipeEntry(this.store.getSelectedRecipeId()!);
    if (!recipeEntry) return;
    const mainRootId = recipeEntry.rootNodeIds[recipeEntry.mainRootIndex];
    const mainRoot = this.nodePool.get(mainRootId);
    if (mainRoot) {
      this.updateHeaderStripQuantity(mainRoot);
    }
  }

  /**
   * Register a callback for when edit mode changes.
   * Returns an unregister function to remove the callback.
   */
  onEditModeChanged(callback: (editing: boolean) => void): () => void {
    this._onEditModeChangedCallbacks.push(callback);
    return () => {
      const idx = this._onEditModeChangedCallbacks.indexOf(callback);
      if (idx >= 0) this._onEditModeChangedCallbacks.splice(idx, 1);
    };
  }

  /**
   * Set the container for the recipe header strip.
   * Call this after construction to enable the header strip UI.
   */
  setRecipeHeaderContainer(container: HTMLElement): void {
    this.recipeHeaderContainer = container;
    this.recipeHeaderStrip = new RecipeHeaderStrip();
    this.recipeHeaderStrip.render(
      container,
      () => this.deselectRecipe(),
      () => this.handleRefreshRecipe(),
      () => { this.saveRecipeChanges(); },
      () => { this.discardChanges(); },
      () => { this.toggleFocusSelectedRecipe(); },
    );
    this.recipeHeaderStrip.setEditModeActive(this.store.isEditMode());

    // Header is always visible. Start with the placeholder (no recipe selected)
    // or populate it if a recipe is already selected.
    if (this.store.getSelectedRecipeId()) {
      this.recipeHeaderStrip.show();
      const recipeEntry = this.store.getRecipeEntry(this.store.getSelectedRecipeId()!);
      if (recipeEntry) {
        const mainRootId = recipeEntry.rootNodeIds[recipeEntry.mainRootIndex];
        const mainRoot = this.nodePool.get(mainRootId);
        if (mainRoot) {
          this.recipeHeaderStrip.update(
            mainRoot.name ?? resolveNodeTitle(mainRoot.data),
            mainRoot.data.description,
          );
        }
      }
    } else {
      // No recipe selected yet — show the placeholder
      this.recipeHeaderStrip.showEmpty();
    }
  }

  /**
   * Mount the edit toolbar (create/edit/save/discard) inside the recipe
   * header strip's toolbar slot — a second row in the same bordered box,
   * below the title row. Call this after setRecipeHeaderContainer().
   * The toolbar is shown only while the store is in edit mode.
   */
  setEditToolbarContainer(): void {
    const slot = this.recipeHeaderStrip?.getToolbarSlot();
    if (!slot) return;

    this.editToolbar = new EditToolbar();
    this.editToolbar.render(slot, {
      onCreate: () => {
        this.editButtonRenderer?.disarmDelete();
        this.handleGlobalCreateClick();
      },
      onMakeMainRoot: () => {
        const node = this.store.getSelectedNode();
        if (node) this.store.handleMakeMainRoot(node.id);
      },
      onEdit: () => {
        this.openNodeEditPopup();
      },
      onAddParent: () => {
        this.requestAddParentToSelectedNodes();
      },
      onTransform: () => {
        this.requestTransformSelectedNodes();
      },
      onToggleMultiSelect: () => {
        this.store.setMultiSelectMode(!this.store.isMultiSelectMode());
      },
      onDeleteAllConfirmed: () => {
        this.requestDeleteSelectedNodes();
      },
      onDeleteRecipe: () => {
        this.requestDeleteSelectedRecipe();
      },
    });

    if (this.store.isEditMode()) {
      this.updateSaveButtonVisibility();
    }
    this.updateEditToolbarButtons();
  }

  // ============================================================================
  // TAB SELECTOR
  // ============================================================================

  /**
   * Set the container for the tab selector. Called after construction to
   * enable the tabbed narrow-screen layout. The ingredients/preparation
   * panels are no longer owned here — see IngredientsView/PreparationView —
   * this only manages which tab-content wrapper is visible.
   */
  setTabContainer(tabContainer: HTMLElement): void {
    this.tabSelectorContainer = tabContainer;
    this.tabSelector = new TabSelector(d3.select(tabContainer));

    // Wire tab change to toggle tab-content wrappers
    this.tabSelector.onTabChange((tab: TabId) => {
      this.toggleTabContent(tab);
      // Notify external listeners
      this.onTabChangeCallback?.(tab);
    });

    // Initial state: graph-only (no recipe loaded yet)
    this.tabSelector.showBrowseTabs();
    this.toggleTabContent('graph');
  }

  /**
   * Toggle visibility of tab-content wrapper divs based on active tab.
   * On narrow screen, only the active wrapper is visible.
   * On wide screen, wrappers are hidden (lg:hidden) — top-row / bottom-row take over.
   */
  private toggleTabContent(tab: TabId): void {
    const tabIds: TabId[] = ['graph', 'list', 'elaboracio', 'timeline'];
    for (const tid of tabIds) {
      const el = document.getElementById(`tab-${tid}`);
      if (!el) continue;
      if (tid === tab) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    }
  }

  /**
   * Restore tab-content wrapper visibility based on the TabSelector's
   * current active tab. Called when transitioning from wide back to narrow.
   */
  restoreTabVisibility(): void {
    if (!this.tabSelector) return;
    const active = this.tabSelector.getActive();
    const visible = this.tabSelector.getVisibleTabs();
    const tabIds: TabId[] = ['graph', 'elaboracio'];
    for (const tid of tabIds) {
      const el = document.getElementById(`tab-${tid}`);
      if (!el) continue;
      if (visible.has(tid) && tid === active) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    }
  }

  /**
   * The context RecipeTextRenderer's functions need: the store itself, plus
   * the current (ephemeral, per-view) yield-scaling factor as a function —
   * see _scalingFactor's own doc comment for why that stays view-owned
   * rather than becoming store state.
   */
  private recipeTextContext(): RecipeTextContext {
    return { store: this.store, scaleAmount: (amount: number) => this.scaleAmount(amount) };
  }

  /**
   * Build the ingredients panel HTML for a recipe. Public — the standalone
   * IngredientsView (own container, own store subscription, own click/hover/
   * select wired directly to the store) calls this rather than owning a
   * duplicate of the HTML-building logic (see RecipeTextRenderer.ts).
   */
  buildIngredientsPanelHtml(recipeId: string | null): string {
    const placeholder = '<div class="text-sm text-[#888] font-mono">No hi ha ingredients</div>';
    const rootNode = this.getRecipeRootNode(recipeId);
    if (!rootNode) return placeholder;
    const { ingredientNodes } = this.store.getIngredientAndPrepNodes(rootNode.recipeId);
    return '<div class="text-xs font-bold text-[var(--accent)] uppercase tracking-wider mb-2">Ingredients</div>' +
      (buildIngredientsHtml(this.recipeTextContext(), ingredientNodes, rootNode) || placeholder);
  }

  /**
   * Build the preparation panel HTML for a recipe. Public for the same
   * reason as buildIngredientsPanelHtml() above.
   */
  buildPreparationPanelHtml(recipeId: string | null): string {
    const placeholder = '<div class="text-sm text-[#888] font-mono">No hi ha passos de preparació</div>';
    const rootNode = this.getRecipeRootNode(recipeId);
    if (!rootNode) return placeholder;
    const { prepNodes } = this.store.getIngredientAndPrepNodes(rootNode.recipeId);

    // Total elaboration time for the whole recipe — the root node's own
    // computeNodeTotalDuration() already rolls up every descendant, so no
    // separate traversal is needed here. Same clock-badge shape as the
    // per-step durations, but in a dimmed green (not the default grey)
    // since it sits right next to the header, not a body row.
    const totalDurationSeconds = computeNodeTotalDuration(this.recipeTextContext(), rootNode);
    const header =
      '<div class="flex items-center justify-between mb-2">' +
      '<span class="text-xs font-bold text-[var(--accent)] uppercase tracking-wider">Preparació</span>' +
      durationBadgeHtml(totalDurationSeconds, 'text-[#2a9d57]') +
      '</div>';
    return header +
      (buildPreparationHtml(this.recipeTextContext(), prepNodes, rootNode) || placeholder);
  }

  /**
   * Build the utensils panel HTML for a recipe. Public for the same
   * reason as buildIngredientsPanelHtml() above. Scans the same node set
   * ingredients/preparation already split the recipe into (a tool usage
   * can land on either kind of node — see isBareToolNode()'s doc comment
   * in RecipeTextRenderer.ts) rather than a third traversal of its own.
   */
  buildToolsPanelHtml(recipeId: string | null): string {
    const placeholder = '<div class="text-sm text-[#888] font-mono">No hi ha utensilis</div>';
    const rootNode = this.getRecipeRootNode(recipeId);
    if (!rootNode) return placeholder;
    const { ingredientNodes, prepNodes } = this.store.getIngredientAndPrepNodes(rootNode.recipeId);
    const allNodes = [...ingredientNodes, ...prepNodes];

    // The eye toggle itself is a plain <button> here (pure HTML string) —
    // wireToolsPanelHiddenToggle() injects its icon and click handling
    // once this is actually in the DOM, same as every other button this
    // string-built HTML can't wire directly. Only shown when there's
    // actually something it could reveal.
    const anyHidden = allNodes.some(n => n.data.toolInfos?.some(ti => ti.hidden));
    const toggleBtn = anyHidden ? '<button type="button" class="utensilis-hidden-toggle-btn"></button>' : '';
    const header =
      '<div class="flex items-center justify-between mb-2">' +
      '<span class="text-xs font-bold text-[var(--accent)] uppercase tracking-wider">Utensilis</span>' +
      toggleBtn +
      '</div>';
    return header +
      (buildToolsHtml(this.recipeTextContext(), allNodes, this.store.isShowHiddenTools()) || placeholder);
  }

  /**
   * Wire the Utensilis panel's "show hidden tools" eye toggle — called by
   * IngredientsView's onAfterRender for the tools instance only (see
   * HomePage.ts), since this button only exists in buildToolsPanelHtml()'s
   * own HTML, not the ingredient/preparation panels sharing that same
   * generic view class.
   */
  wireToolsPanelHiddenToggle(container: HTMLElement): void {
    const btn = container.querySelector<HTMLButtonElement>('.utensilis-hidden-toggle-btn');
    if (!btn) return;
    const applyState = () => {
      const shown = this.store.isShowHiddenTools();
      setIcon(btn, shown ? '/static/img/eye-closed.svg' : '/static/img/eye.svg', '13px');
      btn.title = shown
        ? 'Amagant utensilis amagats — fes clic per mostrar-los'
        : 'Mostra els utensilis amagats';
      btn.style.cssText = shown
        ? `
          width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
          border: 1px solid var(--accent-alpha-40); background: var(--accent); color: #0d1117;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          transition: all 0.15s ease;
        `
        : `
          width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
          border: 1px solid var(--accent-alpha-40); background: transparent; color: var(--accent);
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          transition: all 0.15s ease;
        `;
    };
    applyState();
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.store.setShowHiddenTools(!this.store.isShowHiddenTools());
    });
  }

  /**
   * Get the main root DisplayNode for a recipe (or the selected recipe,
   * when recipeId is omitted/null). Delegates to RecipeStore.getRecipeRootNode()
   * — promoted there so TimelineView (and any other store-only view) can
   * call it without going through GraphVisualizer.
   */
  private getRecipeRootNode(recipeId?: string | null): DisplayNode | null {
    return this.store.getRecipeRootNode(recipeId);
  }

  /**
   * Switch to the given tab — on narrow screens that's the whole story; on
   * wide screens it also flips the wide-screen graphListToggle (via
   * HomePage's onTabChange wiring, which forwards every tab value, not
   * just Elaboració) and scrolls the panel into view. Public — besides the
   * ingredient-link-click use below, HomePage also calls this directly to
   * restore a saved tab on back/forward navigation (see selectNodeById()
   * and HomePage.render()'s restoreState handling).
   */
  switchToTabOrScroll(tab: TabId): void {
    // Always update TabSelector's own state (and fire its onTabChange
    // callback via setTabContainer()'s wiring), regardless of screen
    // size — HomePage listens to that callback to keep its wide-screen
    // graphListToggle in sync too, and this keeps the *narrow* tab correct
    // as well if the window is later resized down.
    this.tabSelector?.setActive(tab);

    if (window.innerWidth >= 1024) {
      // Wide screen: also scroll the panel into view — switching alone
      // doesn't guarantee it's visible if the page had scrolled elsewhere.
      const panelEl = document.getElementById('home-elaboracio-panel-wide');
      if (panelEl) {
        panelEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  // ============================================================================
  // CHANGE TRACKING (single tracker, owned by the store — RecipeStore's own
  // mutation methods self-track internally; this view no longer decides
  // when to track. The one exception is real-time keystroke feedback
  // below, which has no store mutation to hook into yet — see
  // setOnNodeModified(). Save/Discard button visibility refreshes from the
  // central store.subscribe() callback in the constructor, since notify()
  // fires after every mutation including every self-tracking one.)
  // ============================================================================

  /**
   * Real-time keystroke tracking — the one legitimate external caller of
   * RecipeStore.trackNodeChange(). See setOnNodeModified() below.
   */
  trackNodeChange(nodeId: string): void {
    this.store.trackNodeChange(nodeId);
  }

  /**
   * Update save/discard button visibility based on change tracker state.
   */
  private updateSaveButtonVisibility(): void {
    if (!this.recipeHeaderStrip) return;
    if (this.store.hasUnsavedChanges()) {
      this.recipeHeaderStrip.showSaveButton();
      this.recipeHeaderStrip.showDiscardButton();
    } else {
      this.recipeHeaderStrip.hideSaveButton();
      this.recipeHeaderStrip.hideDiscardButton();
    }
  }

  /**
   * Refresh the recipe header strip title/description from the current main root.
   */
  private updateRecipeHeader(): void {
    if (!this.recipeHeaderStrip) return;
    
    const selectedRecipeId = this.store.getSelectedRecipeId();
    if (!selectedRecipeId) {
      this.recipeHeaderStrip.showEmpty();
      this.recipeHeaderStrip.hideFocusButton();
      this.recipeHeaderStrip.hideRefreshButton();
      return;
    }

    const recipeEntry = this.store.getRecipeEntry(selectedRecipeId);
    if (!recipeEntry) {
      this.recipeHeaderStrip.showEmpty();
      this.recipeHeaderStrip.hideFocusButton();
      this.recipeHeaderStrip.hideRefreshButton();
      return;
    }
    
    const mainRootId = recipeEntry.rootNodeIds[recipeEntry.mainRootIndex];
    const mainRoot = this.nodePool.get(mainRootId);
    if (mainRoot) {
      this.recipeHeaderStrip.update(
        mainRoot.name ?? resolveNodeTitle(mainRoot.data),
        mainRoot.data.description,
      );
      this.updateHeaderStripQuantity(mainRoot);
      this.recipeHeaderStrip.setTags(mainRoot.data.ingredientInfo?.ingredient?.tags ?? []);
      this.recipeHeaderStrip.show();
      this.recipeHeaderStrip.showFocusButton();
      this.recipeHeaderStrip.setFocusActive(this.store.isNodeExpanded(mainRootId));
      // Reload and save/discard are mutually exclusive by mode — only
      // show reload outside edit mode (setEditModeActive handles hiding
      // it when entering edit mode).
      if (!this.store.isEditMode()) {
        this.recipeHeaderStrip.showRefreshButton();
      }
    } else {
      this.recipeHeaderStrip.showEmpty();
      this.recipeHeaderStrip.hideFocusButton();
      this.recipeHeaderStrip.hideRefreshButton();
    }
  }

  /**
   * Update the header strip's "Per a" quantity row from the root node.
   */
  private updateHeaderStripQuantity(rootNode: DisplayNode): void {
    if (!this.recipeHeaderStrip) return;

    const qty = rootNode.data.ingredientInfo?.quantity ?? null;
    this._originalRootQty = qty ? { amount: qty.amount, unit: qty.unit } : null;

    if (!qty) {
      this.recipeHeaderStrip.updateQuantity(null);
      return;
    }

    // Build initial unit options (just the current unit until conversions arrive)
    const currentUnitLabel = unitCache.getPlural(qty.unit);
    const unitOptions = [{ id: qty.unit, label: currentUnitLabel }];

    this.recipeHeaderStrip.updateQuantity(
      qty,
      unitOptions,
      this.store.isEditMode(),
      () => this.openYieldModal(qty, unitOptions),
      (newAmount, newUnit) => this.handleYieldChange(newAmount, newUnit),
    );

    // Async: fetch conversions to populate the modal's unit dropdown
    this.refreshQuantityUnitOptions(qty.unit);
  }

  /**
   * Fetch conversions for a unit and refresh the header strip's unit dropdown.
   */
  private async refreshQuantityUnitOptions(unitId: string): Promise<void> {
    if (!this.recipeHeaderStrip || !this._originalRootQty) return;
    // Recipe shown when the fetch started — if it's since been deselected
    // or switched (e.g. background click while this was in flight), the
    // stale response below must not re-show a quantity row that's no
    // longer supposed to be visible.
    const recipeIdAtFetchStart = this.store.getSelectedRecipeId();
    try {
      const { getUnitConversions } = await import('../api/units.js');
      const conversions = await getUnitConversions(unitId);
      const allUnits = unitCache.getAll();

      if (this.store.getSelectedRecipeId() !== recipeIdAtFetchStart) return;

      // Build unit options: current unit + any convertible units
      const options: { id: string; label: string }[] = [{
        id: unitId,
        label: unitCache.getPlural(unitId),
      }];
      for (const conv of conversions) {
        const unitDef = allUnits.find(u => u.id === conv.toUnitId);
        if (unitDef) {
          options.push({ id: conv.toUnitId, label: unitDef.plural });
        } else {
          options.push({ id: conv.toUnitId, label: conv.toUnitId });
        }
      }

      // Re-call updateQuantity with full options
      this.recipeHeaderStrip.updateQuantity(
        this._originalRootQty,
        options,
        this.store.isEditMode(),
        () => this.openYieldModal(this._originalRootQty!, options),
        (newAmount, newUnit) => this.handleYieldChange(newAmount, newUnit),
      );
    } catch {
      // Ignore fetch failures — the current unit is already shown
    }
  }

  /**
   * Open a modal dialog for editing the yield amount and unit.
   */
  private openYieldModal(
    qty: { amount: number; unit: string },
    unitOptions: { id: string; label: string }[],
  ): void {
    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 9999; display: flex; align-items: center; justify-content: center;';

    const dialog = document.createElement('div');
    dialog.style.cssText = 'background: #1e1e2e; border: 1px solid var(--accent-alpha-20); border-radius: 8px; padding: 20px; min-width: 280px; font-family: inherit;';

    // Title
    const title = document.createElement('div');
    title.style.cssText = 'color: #e0e0e0; font-size: 14px; font-weight: bold; margin-bottom: 16px;';
    title.textContent = 'Escala la recepta';
    dialog.appendChild(title);

    // Per a label
    const label = document.createElement('label');
    label.style.cssText = 'color: #a0a0a0; font-size: 12px; display: block; margin-bottom: 6px;';
    label.textContent = 'Per a';
    dialog.appendChild(label);

    // Input + select row
    const row = document.createElement('div');
    row.style.cssText = 'display: flex; gap: 8px; align-items: center; margin-bottom: 20px;';

    const input = document.createElement('input');
    input.type = 'number';
    input.min = '0';
    input.step = 'any';
    input.value = String(qty.amount);
    input.style.cssText = 'width: 80px; padding: 4px 8px; font-size: 14px; background: #0d1117; border: 1px solid var(--accent-alpha-20); border-radius: 4px; color: #e0e0e0; font-family: inherit;';
    row.appendChild(input);

    const select = document.createElement('select');
    select.style.cssText = 'padding: 4px 8px; font-size: 14px; background: #0d1117; border: 1px solid var(--accent-alpha-20); border-radius: 4px; color: #e0e0e0; font-family: inherit; cursor: pointer;';
    for (const opt of unitOptions) {
      const optionEl = document.createElement('option');
      optionEl.value = opt.id;
      optionEl.textContent = opt.label;
      if (opt.id === qty.unit) optionEl.selected = true;
      select.appendChild(optionEl);
    }
    row.appendChild(select);
    dialog.appendChild(row);

    // Buttons
    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display: flex; gap: 8px; justify-content: flex-end;';

    const cancelBtn = document.createElement('button');
    cancelBtn.style.cssText = 'padding: 6px 16px; font-size: 12px; font-weight: bold; font-family: inherit; border-radius: 4px; border: 1px solid var(--accent-alpha-40); background: transparent; color: #a0a0a0; cursor: pointer;';
    cancelBtn.textContent = 'Cancel·la';
    cancelBtn.addEventListener('click', () => {
      document.body.removeChild(overlay);
    });
    btnRow.appendChild(cancelBtn);

    const okBtn = document.createElement('button');
    okBtn.style.cssText = 'padding: 6px 16px; font-size: 12px; font-weight: bold; font-family: inherit; border-radius: 4px; border: none; background: var(--accent); color: #0d1117; cursor: pointer;';
    okBtn.textContent = 'D\'acord';
    okBtn.addEventListener('click', () => {
      const amount = parseFloat(input.value);
      if (!isNaN(amount) && amount > 0) {
        this.handleYieldChange(amount, select.value);
        // Update header strip display
        if (this.recipeHeaderStrip && this._originalRootQty) {
          this.recipeHeaderStrip.updateQuantity(
            { amount, unit: select.value },
            unitOptions,
            this.store.isEditMode(),
            () => this.openYieldModal({ amount, unit: select.value }, unitOptions),
            (newAmount, newUnit) => this.handleYieldChange(newAmount, newUnit),
          );
        }
      }
      document.body.removeChild(overlay);
    });
    btnRow.appendChild(okBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Focus the input
    input.focus();
    input.select();

    // Close on overlay click (outside dialog)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        document.body.removeChild(overlay);
      }
    });

    // Close on Escape
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        document.body.removeChild(overlay);
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
  }

  /**
   * Save recipe changes to the API.
   * Builds a save payload from current graph state and calls PUT /api/recipes/{id}.
   * On success, resets the ChangeTracker with a fresh snapshot.
   * On error, keeps changes in the tracker so the user can retry or discard.
   */
  async saveRecipeChanges(): Promise<void> {
    if (!this.store.getSelectedRecipeId()) {
      console.warn('No recipe selected — cannot save');
      return;
    }

    this.recipeHeaderStrip?.setSaveButtonText('Guardant…');
    this.recipeHeaderStrip?.setSaveButtonDisabled(true);

    try {
      const payload = this.store.buildSavePayload(this.store.getSelectedRecipeId()!);

      // Only save if there's actually something to save
      if (Object.keys(payload).length === 0) {
        this.recipeHeaderStrip?.setSaveButtonText('Desa els canvis');
        this.recipeHeaderStrip?.setSaveButtonDisabled(false);
        return;
      }

      const recipeId = this.store.getSelectedRecipeId()!;  // Save before clearing

      // Capture fold state, position, and focus before the reload below —
      // saving shouldn't unfold the recipe, move it, or reveal every other
      // recipe that focus was hiding (same reload cycle as refresh).
      const viewState = this.captureRecipeViewState(recipeId);

      // The PUT response already carries the full, freshly-saved recipe
      // (with permanent IDs) — no separate fetch needed. Once this
      // resolves, everything below is synchronous: clear, rebuild from the
      // response we already have, and restore, all in one tick, so the
      // recipe is never actually removed from the screen without something
      // to instantly replace it.
      const savedRecipe = await this.store.submitRecipeUpdate(recipeId, payload);

      // Remember if we were in edit mode
      const wasEditing = this.store.isEditMode();

      // Clear the old recipe from the pool (this also clears selection)
      this.clearRecipe(recipeId);

      // Re-focus immediately so the "unfocused" state clearRecipe() leaves
      // behind is never actually painted.
      this.reapplyFocusImmediately(recipeId, viewState);

      // Rebuild from the save response — no network round-trip here.
      this.store.applyLoadedRecipe(recipeId, savedRecipe);

      this.restoreRecipeViewState(recipeId, viewState);

      // Re-enter edit mode if we were editing. this.enterEditMode() (not
      // store.enterEditMode()) re-snapshots the store's change tracker and
      // restores search-filter dimming state correctly. It no longer
      // auto-expands/focuses, so this can't undo the fold state
      // restoreRecipeViewState() just restored.
      if (wasEditing) {
        this.store.setEditMode(true);
        this.enterEditMode();
        this._fireEditModeChanged(true);
      }

      // Re-select the main root node (since IDs changed)
      const recipeEntry = this.store.getRecipeEntry(recipeId);
      if (recipeEntry) {
        const mainRootId = recipeEntry.rootNodeIds[recipeEntry.mainRootIndex];
        const mainRoot = this.nodePool.get(mainRootId);
        if (mainRoot) {
          this.selectNode(mainRoot);
        }
      }

      const recipeNodes = this.store.getRecipeNodes(recipeId);
      this.fitViewportToNodes(recipeNodes.filter(n => !this.store.isNodeHidden(n.id)), 0);
      this.restartSimulation(0.1);

      // Reset change tracker with fresh snapshot
      this.store.initializeChangeTracker(recipeId);
      this.updateSaveButtonVisibility();

      // Refresh the recipe header with potentially updated title/description
      this.updateRecipeHeader();

      // Fire the on-recipe-saved callback (for home page to refresh root nodes)
      this._onRecipeSavedCallback?.();

      // Show success state briefly
      this.recipeHeaderStrip?.setSaveButtonText('Guardat!');
      this.recipeHeaderStrip?.setSaveButtonDisabled(false);
      showToast('Recepta guardada correctament', 'success');

      setTimeout(() => {
        this.recipeHeaderStrip?.setSaveButtonText('Desa els canvis');
        this.updateSaveButtonVisibility();
      }, 2000);
    } catch (error) {
      console.error('Failed to save recipe:', error);

      const errorMsg = error instanceof Error ? error.message : 'Error de connexió';

      // Check if it's a 404 — recipe was deleted by another tab/session
      if (errorMsg.includes('404') || errorMsg.includes('not found') || errorMsg.includes('No trobada')) {
        showToast('Aquesta recepta ha estat eliminada', 'error');

        // Remove recipe from pool and clear selection
        if (this.store.getSelectedRecipeId()) {
          const deletedId = this.store.getSelectedRecipeId()!;
          this.clearRecipe(deletedId);
        }
        this.store.deselect();

        // Exit edit mode
        this.store.setEditMode(false);
        this.cleanupEditMode();
        this._fireEditModeChanged(false);

        // Update button text
        if (this.recipeHeaderStrip) {
          this.recipeHeaderStrip.setSaveButtonText('Desa els canvis');
          this.recipeHeaderStrip.hideSaveButton();
          this.recipeHeaderStrip.hideDiscardButton();
        }
        return; // Don't re-throw — handled gracefully
      }

      this.recipeHeaderStrip?.setSaveButtonText('Error en guardar');
      this.recipeHeaderStrip?.setSaveButtonDisabled(false);
      showToast(`Error en guardar: ${errorMsg}`, 'error');

      setTimeout(() => {
        this.recipeHeaderStrip?.setSaveButtonText('Desa els canvis');
        this.updateSaveButtonVisibility();
      }, 3000);

      throw error;
    }
  }

  /**
   * Discard all unsaved changes and restore the original graph state
   * from the ChangeTracker snapshot.
   *
   * The restore loop below calls store.removeNode()/addNodes()/
   * collapseNode()/expandNode(), all of which self-track — so the tracker
   * gets transiently (and harmlessly) dirtied for the recipe being
   * restored while this runs. That's fine: this method is entirely
   * synchronous (no `await` on its real code path — expandNode() always
   * takes a synchronous branch here, since discard only restores nodes
   * that were already loaded) and resetChangeTracker() below is the last
   * mutating step before returning, so nothing paints in between and the
   * end state is correct either way.
   */
  discardChanges(): void {
    const originalGraph = this.store.getOriginalGraph();
    if (!originalGraph || !this.store.getSelectedRecipeId()) return;

    const originalNodeIds = new Set(originalGraph.nodes.map(n => n.id));

    // 1. Remove nodes from pool that were added during editing
    const currentIds = Array.from(this.nodePool.keys());
    for (const id of currentIds) {
      const node = this.nodePool.get(id)!;
      if (node.recipeId === this.store.getSelectedRecipeId()! && !originalNodeIds.has(id)) {
        this.store.removeNode(id);
      }
    }

    // 2. Restore original state for each node that existed before
    for (const origNode of originalGraph.nodes) {
      const existingNode = this.nodePool.get(origNode.id);
      const clonedData = cloneNode(origNode.data);

      if (existingNode) {
        // Preserve current fold state (view preference, not data)
        const currentFolded = !this.store.isNodeExpanded(existingNode.id);
        const currentHidden = this.store.isNodeHidden(existingNode.id);

        // Restore data in-place
        existingNode.data = clonedData;
        existingNode.name = origNode.name;
        existingNode.type = origNode.type;
        existingNode.x = origNode.x;
        existingNode.y = origNode.y;
        if (currentFolded && !this.store.isNodeExpanded(existingNode.id)) {
          this.store.collapseNode(existingNode.id);
        } else if (!currentFolded && this.store.isNodeExpanded(existingNode.id)) {
          this.store.expandNode(existingNode.id);
        }
        if (currentHidden && !this.store.isNodeHidden(existingNode.id)) {
          this.store.hideNode(existingNode.id);
        } else if (!currentHidden && this.store.isNodeHidden(existingNode.id)) {
          this.store.showNode(existingNode.id);
        }
        existingNode.leafDistance = origNode.leafDistance;
        existingNode.rootDistance = origNode.rootDistance;
      } else {
        // Re-add node that was deleted during editing
        const dnode: DisplayNode = {
          id: origNode.id,
          name: origNode.name,
          type: origNode.type,
          data: clonedData,
          recipeId: origNode.recipeId,
          isMainRoot: origNode.isMainRoot,
          leafDistance: origNode.leafDistance,
          rootDistance: origNode.rootDistance,
          x: origNode.x ?? 0,
          y: origNode.y ?? 0,
          vx: 0,
          vy: 0,
        };
        this.store.addNodes([dnode]);
      }
    }

    // 3. Rebuild linkPool from restored children relationships
    this.rebuildLinksForRecipe(this.store.getSelectedRecipeId()!);

    // 4. Re-render SVG nodes and side panel to reflect restored data
    this.renderAll();
    this.updateSidePanel();
    this.restartSimulation(0.1);

    // 5. Reset change tracker with fresh snapshot so hasChanges() returns false
    //    and save/discard buttons hide
    this.store.resetChangeTracker(this.store.getSelectedRecipeId()!);

    // 6. Refresh the recipe header with restored title/description
    this.updateRecipeHeader();

    // 7. Update save button visibility (now hides)
    this.updateSaveButtonVisibility();

    showToast('Canvis descartats', 'info');
  }

  /**
   * Select a node.
   * Clears previous selection, updates visual state, and updates side panel.
   * Also sets the selected recipe to the node's recipe.
   */
  selectNode(node: DisplayNode | null): void {
    // Switching to a node in a *different* recipe while the current one has
    // unsaved changes needs a save/discard/cancel prompt first (§2.3.1/§9.2)
    // — otherwise the in-progress edits are silently lost. Same-recipe node
    // clicks and background clicks (node === null) are always safe.
    const currentRecipeId = this.store.getSelectedRecipeId();
    const targetRecipeId = node?.recipeId ?? null;
    if (
      targetRecipeId !== null &&
      currentRecipeId !== null &&
      targetRecipeId !== currentRecipeId &&
      this.store.hasUnsavedChanges()
    ) {
      showUnsavedChangesDialog({
        recipeTitle: this.getSelectedRecipeTitle() ?? 'la recepta',
        isMobile: this._isMobile,
        onSave: async () => {
          try {
            await this.saveRecipeChanges();
            this.selectNode(node);
          } catch {
            // Save failed — stay on the current node/recipe
          }
        },
        onDiscard: () => {
          this.discardChanges();
          this.selectNode(node);
        },
        onCancel: () => {
          // Stay on the current node/recipe
        },
      });
      return;
    }

    // Clear previous selection and hover state
    if (this.store.getSelectedNode()) {
      this.store.deselect();
      this.store.setHoveredNode(null);
    }
    if (this._hoveredNode && this._hoveredNode !== this.store.getSelectedNode()) {
      this.store.setHoveredNode(null);
    }
    this._hoveredNode = null;

    // Selection state managed by store
    if (node) {
      this.store.selectNode(node.id);
      this.store.setHoveredNode(null); // Ensure hover state doesn't override selection styling

      // Auto-select (or switch) the recipe from the node's recipeId. Route
      // through switchSelectedRecipe() — instead of a bare store.selectRecipe()
      // — whenever the recipe is actually changing, so edit-mode bookkeeping
      // (dimming other recipes' nodes and, crucially, re-snapshotting the
      // store's change tracker for the recipe about to be edited) stays
      // correct even when the recipe is picked by clicking a node rather
      // than going through setSelectedRecipeId()'s unsaved-changes-checked
      // path. Without this, entering edit mode with no recipe selected yet
      // (or with a different one selected) and then clicking a node to edit
      // leaves the change tracker null or stale, and Save/Discard never
      // appear no matter what gets edited afterward.
      if (node.recipeId !== currentRecipeId) {
        this.switchSelectedRecipe(node.recipeId, currentRecipeId);
      }
    }

    this.renderAll();
    // Show caret for newly selected node (renderAll creates fresh elements with hidden carets)
    if (node) {
      this.showCaretForNode(node);
    }
    this.updateSidePanel();
    this.updateRecipeHeader();
    this.onSelectionChanged?.(node);
  }

  /**
   * Get the currently selected node.
   */
  getSelectedNode(): DisplayNode | null {
    return this.store.getSelectedNode();
  }

  /**
   * Deselect the current node.
   * Also cancels any active link flow.
   *
   * Never touches the selected *recipe* — selection and focus are
   * independent, so a folded/unfocused main root is not a reason to drop
   * the recipe from the header too.
   */
  deselect(): void {
    // Cancel active link flow or pick node mode if any
    if (this.store.getEditionSubMode() !== StoreEditionMode.NONE) {
      if (this.store.getEditionSubMode() === StoreEditionMode.PICK_NODE) {
        this.cancelPickNode('deselect');
      } else {
        this.cancelLinkFlow('deselect');
      }
    }

    this.selectNode(null);
  }

  /**
   * Set a callback for when selection changes.
   */
  onSelect(callback: (node: DisplayNode | null) => void): void {
    this.onSelectionChanged = callback;
  }

  /**
   * Set a callback for when user requests to expand a node from the side panel.
   */
  onExpandRequest(callback: (nodeId: string) => void): void {
    this.onExpandRequested = callback;
  }

  /**
   * Set a callback for when user requests to navigate to a recipe page.
   */
  onNavigateToRecipe(callback: (recipeId: string) => void): void {
    this._onNavigateToRecipeCallback = callback;
  }

  /**
   * Set a callback for when the user clicks EditToolbar's [⚘] "go to
   * ingredient" button.
   */
  onNavigateToIngredient(callback: (ingredientId: string) => void): void {
    this._onNavigateToIngredientCallback = callback;
  }

  /**
   * Set a callback for when the active tab changes.
   */
  onTabChange(callback: (tab: TabId) => void): void {
    this.onTabChangeCallback = callback;
  }

  /**
   * Set active (non-inactive) node IDs for filtering.
   * Nodes not in the active set get __inactive = true.
   * Call with empty array to clear all inactive states.
   */
  setActiveNodes(activeIds: string[]): void {
    const activeSet = new Set(activeIds);
    this.store.setNodesInactiveWhere(node => activeIds.length > 0 && !activeSet.has(node.id));
    this.renderAll();
  }

  /**
   * Clear all inactive states on nodes.
   */
  clearInactiveNodes(): void {
    this.store.clearAllInactive();
    this.renderAll();
  }

  /**
   * Get the node pool (read-only).
   */
  getNodePool(): Map<string, DisplayNode> {
    return this.nodePool;
  }

  /**
   * Get the link pool (read-only).
   */
  getLinkPool(): Link<DisplayNode>[] {
    return this.linkPool;
  }

  /**
   * Get the loaded recipes map.
   */
  getLoadedRecipes(): Map<string, RecipeEntry> {
    return this.store.getLoadedRecipes();
  }

  // ============================================================================
  // LIFECYCLE
  // ============================================================================

  /**
   * Destroy the visualizer: stop simulation, remove SVG, cleanup.
   */
  destroy(): void {
    // Silence every outward-facing callback *before* any teardown below —
    // clearNodes()/clearRecipes() trigger the store's own notify(), which
    // this instance is still subscribed to (see the constructor), and
    // that can synchronously detect a "just unfocused" transition (the
    // recipe being torn down was focused) and fire onFocusChanged(false)
    // straight through to whatever the caller registered — e.g.
    // HomePage's handler, which pushes '/' into history. Since destroy()
    // only ever runs because the caller is already navigating elsewhere
    // (main.ts's dispatchRoute()), a stray callback firing mid-teardown
    // clobbers that in-flight navigation's URL before the router ever
    // reads it — this was the exact cause of a "go to ingredient" click
    // landing on '/' instead of /ingredient/<id> (the pushState was still
    // there, just overwritten one tick later, which is why browser
    // back correctly reached it).
    this.onSelectionChanged = null;
    this.onExpandRequested = null;
    this.onRecipeSelectionChangedCallback = null;
    this.onTabChangeCallback = null;
    this._onNavigateToRecipeCallback = null;
    this._onNavigateToIngredientCallback = null;
    this._onCreateNewRecipeCallback = null;
    this._onRecipeSavedCallback = null;
    this._onEditModeChangedCallbacks = [];
    this._onFocusChangedCallback = null;

    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }
    if (this.simulation) {
      this.simulation.stop();
      this.simulation = null;
    }
    this.store.clearNodes();
    this.store.setLinkPool([]);
    this.store.clearRecipes();
    if (this.nodeVisualizer) {
      this.nodeVisualizer.destroy();
      this.nodeVisualizer = null;
    }
    if (this.recipeHeaderStrip) {
      this.recipeHeaderStrip.destroy();
      this.recipeHeaderStrip = null;
    }
    if (this.tabSelector) {
      this.tabSelector.destroy();
      this.tabSelector = null;
    }
    this.container.innerHTML = '';
  }

  // ============================================================================
  // INTERNAL HELPERS
  // ============================================================================

  /**
   * Restart the simulation with a given alpha.
   * Rebuilds simulation with all visible (non-hidden) nodes.
   */
  private restartSimulation(alpha: number = 0.1): void {
    if (this.simulation) {
      this.simulation.stop();
    }

    // Only simulate visible nodes
    const visibleNodes = Array.from(this.nodePool.values())
      .filter(n => !this.store.isNodeHidden(n.id));

    this.simulation = createGraphSimulation(visibleNodes);

    // Set up tick handler
    setupSimulationTick(this.simulation, () => {
      this.renderNodePositions();
      this.renderLinkPositions();
    });

    // Apply initial alpha
    this.simulation.alpha(alpha);
  }

  /**
   * Initialize a NodeVisualizer for the side panel (lazy).
   *
   * On wide screens, while editing, the side panel renders the exact same
   * tabbed editable view as the NodeEditPopup (same createNodeVisualizer()
   * factory, same NodeVisualizer.renderEditableContent — no separate
   * implementation) — there's room for it, and no need for a confirm
   * button since nothing here is modal. On narrow screens it stays
   * read-only and editing goes through the popup instead, since there
   * isn't room for both the persistent side panel and inline editing.
   */
  private ensureNodeVisualizer(): void {
    if (this.nodeVisualizer) return;
    if (!this.sidePanel) return;

    this.nodeVisualizer = this.createNodeVisualizer(
      d3.select(this.sidePanel),
      () => (this.store.isEditMode() && this.isWideScreen()) ? EditionMode.EDIT : EditionMode.NONE,
    );
  }

  /**
   * Build a fully-wired NodeVisualizer targeting the given container.
   * Shared by the side panel (ensureNodeVisualizer) and the NodeEditPopup,
   * so both present the same editable fields with the same callbacks.
   */
  private createNodeVisualizer(
    d3Container: Selection<HTMLElement, unknown, null, undefined>,
    editionModeFn: () => EditionMode,
  ): NodeVisualizer {
    const nv = new NodeVisualizer(
      d3Container,
      editionModeFn,
      async (nodeId: string, field: string, value: unknown) => {
        const node = this.nodePool.get(nodeId);
        if (!node) return;

        // Handle field changes (same logic as RecipeVisualizer) —
        // self-tracks internally, see RecipeStore.handleNodeFieldChange().
        this.store.handleNodeFieldChange(node.id, field, value);

        // Re-render
        this.renderAll();
      },
      // entity update callback
      (entityType: 'ingredient' | 'transformation' | 'tool', entityId: string, updatedEntity: any) => {
        this.store.propagateEntityChange(entityType, entityId, updatedEntity);
      },
      // pick node callback — enters a mode where clicking a graph node (or,
      // via ListView, a list row) copies that node's ID into the
      // transformation parameter value
      (nodeId: string, paramName: string, onPicked: (pickedNodeId: string | null) => void) => {
        this._pickOnPicked = onPicked;
        this._pickSourceNodeId = nodeId;
        this._pickParamName = paramName;
        this.store.setEditionSubMode(StoreEditionMode.PICK_NODE);
        // Show crosshair cursor on the SVG to indicate pick mode is active
        if (this.svg) {
          this.svg.style('cursor', 'crosshair');
        }

        // The node-edit popup covers the whole screen, so if this pick
        // started from it (its "◎" button — the side panel's own copy
        // isn't covering anything, so this is a no-op there), hide it out
        // of the way so the graph/list underneath becomes clickable again
        // — restored by cancelPickNode() once picking ends, one way or
        // another. Show a banner in its place, since the popup being hidden
        // is the one case with no other visible cue something is happening.
        if (this.nodeEditPopup) {
          this.nodeEditPopup.hide();
          this.showPickBanner(() => this.cancelPickNode('bannerCancel'));
        }

        // Register Escape key handler to cancel pick mode
        const escHandler = (e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            this.cancelPickNode('escape');
          }
        };
        document.addEventListener('keydown', escHandler);
        this._pickEscapeHandler = escHandler;

        // Cancel pick mode on any click outside a node — same "click
        // outside dismisses" pattern as a dropdown. Folding/unfolding
        // (the caret) stays available without cancelling, per request.
        // Capturing phase + stopPropagation so the click is swallowed
        // entirely rather than also performing whatever it would
        // otherwise do (e.g. a toolbar button), same as dismissing an
        // overlay via an outside click. Excludes the graph node/caret,
        // list rows and ingredients/utensilis/preparation rows+links
        // (their own handlers complete/allow the pick — see
        // panelRowEvents.ts), the pick button and banner (their own
        // handlers already toggle pick state correctly), the floating
        // edit/create buttons (which the background-click handler above
        // already treats as "not background"), and the Graph/List/
        // Ingredients/Preparation tab controls (switching tabs mid-pick is
        // allowed, per request — the node you're after may be on a
        // different tab).
        const outsideClickHandler = (e: MouseEvent) => {
          const target = e.target as Element | null;
          if (!target) return;
          if (target.closest(
            '.viz-node, .viz-node-caret, .list-node-item, ' +
            '.ingredient-row, .tool-row, .preparation-step-row, .preparation-ingredient, ' +
            '.pick-node-btn, .pick-node-banner, .edit-btn, .edit-button-group, ' +
            '.global-create-btn, .global-create-btn-inner, ' +
            '.tab-selector-btn, .graph-list-toggle-btn'
          )) {
            return;
          }
          e.stopPropagation();
          this.cancelPickNode('outsideClick');
        };
        document.addEventListener('click', outsideClickHandler, true);
        this._pickOutsideClickHandler = outsideClickHandler;

        // Return cancel function
        return () => {
          this.cancelPickNode('callback');
        };
      },
      // find ingredient recipes callback
      undefined,
      // onExpandNode callback — called when user clicks "Obre" on recipe summary
      (nodeId: string) => {
        // If an external handler is set, use it; otherwise expand via the visualizer
        if (this.onExpandRequested) {
          this.onExpandRequested(nodeId);
        } else {
          this.expandNode(nodeId);
        }
      },
      // Add/remove elaboration moved to List View's [⚗]/[⚗ׯ] buttons — see
      // requestElaborationAction()/store.handleRemoveElaboration().
      undefined, // isNodeExpanded — not wired here (defaults to always-true)
      (nodeId: string) => this.nodePool.get(nodeId),
    );

    // Wire step text renderer for the read-only node card
    nv.setStepTextRenderer((n) => renderStepTextForNode(this.recipeTextContext(), n));
    nv.setIngredientsTextRenderer((n) => renderIngredientsTextForNode(this.recipeTextContext(), n));
    nv.setTotalDurationRenderer((n) => computeNodeTotalDuration(this.recipeTextContext(), n));

    // Wire hover highlights: when user hovers a node reference in side-panel
    // text (ingredients or preparation), highlight the corresponding graph node
    // and update the side panel to show the hovered node.
    nv.setOnNodeHover((nodeId: string) => {
      const node = this.nodePool.get(nodeId);
      if (!node) return;
      this.store.setHoveredNode(node.id);
      this._hoveredNode = node;
      this.renderNodeHighlights();
      // Show hovered node in side panel
      this.renderNodeInSidePanelForHover(node);
    });
    // Graph-only hover: only highlights the node, doesn't change side panel
    // Used for node references in the node visualizer's own preparation text
    nv.setOnNodeHoverGraphOnly((nodeId: string) => {
      const node = this.nodePool.get(nodeId);
      if (!node) return;
      this.store.setHoveredNode(node.id);
      this._hoveredNode = node;
      this.renderNodeHighlights();
      // Do NOT call renderNodeInSidePanelForHover()
    });
    nv.setOnNodeUnhover((nodeId: string) => {
      const node = this.nodePool.get(nodeId);
      if (!node) return;
      this.store.setHoveredNode(null);
      this._hoveredNode = null;
      this.renderNodeHighlights();
      // Restore selected node in side panel
      this.updateSidePanel();
    });
    nv.setOnNodeClick((nodeId: string) => {
      const node = this.nodePool.get(nodeId);
      if (!node) return;

      // Ingredient link clicked in side panel → switch to the Elaboració
      // tab (narrow) or scroll to its panel (wide)
      this.switchToTabOrScroll('elaboracio');

      // Ensure the node is visible by unfolding any folded ancestors,
      // then select it.
      this.ensureNodeVisible(nodeId).then(() => {
        this.selectNode(node);
      });
    });

    // Real-time tracking: call trackNodeChange on every keystroke so
    // the save button appears immediately while the user is typing.
    nv.setOnNodeModified((nodeId: string) => {
      this.trackNodeChange(nodeId);
    });

    return nv;
  }

  /**
   * Render any node in the side panel with the correct context
   * (recipe summary for collapsed main roots, recipe detail for expanded
   * main roots, node detail for everything else).
   * Shared by both selection and hover paths so the view is consistent.
   */
  private renderNodeInSidePanel(node: DisplayNode, target?: NodeVisualizer | null): void {
    const nv = target === undefined ? this.nodeVisualizer : target;
    if (!nv) return;
    nv.setScalingFactor(this._scalingFactor);

    const context: VisualizerContext = {
      isRootNode: this.isRootNodeInPool(node),
      isEditing: this.store.isEditMode(),
    };
    nv.updateNode(node, context);
  }

  /**
   * Temporarily render a hovered node in the side panel.
   * Unlike renderNodeInSidePanel, this works even if the node is not selected.
   */
  private renderNodeInSidePanelForHover(node: DisplayNode): void {
    if (!this.nodeVisualizer) return;
    this.nodeVisualizer.setScalingFactor(this._scalingFactor);
    // Don't override the selected node's view if this node IS the selected one
    const selectedNode3 = this.store.getSelectedNode();
    if (selectedNode3 && selectedNode3.id === node.id) return;
    this.nodeVisualizer.updateNode(node);
  }

  /**
   * Update the side panel based on the currently selected node.
   * Delegates to the shared renderNodeInSidePanel for consistent view.
   */
  /**
   * Open the node edit popup for the currently selected node.
   * Mounts a second, always-editable NodeVisualizer instance into the
   * popup's body — same field/entity/pick/elaboration wiring as the side
   * panel, via the shared createNodeVisualizer() factory.
   */
  private openNodeEditPopup(): void {
    this.openNodePopup(EditionMode.EDIT);
  }

  /** Mounts a NodeVisualizer instance into the popup opened above. */
  private openNodePopup(mode: EditionMode): void {
    const node = this.store.getSelectedNode();
    if (!node) return;

    // Close any popup already open before opening a new one.
    this.closeNodeEditPopup();

    this.nodeEditPopup = new NodeEditPopup();
    const bodyEl = this.nodeEditPopup.open(() => {
      this.popupNodeVisualizer?.destroy();
      this.popupNodeVisualizer = null;
      this.nodeEditPopup = null;
    });

    this.popupNodeVisualizer = this.createNodeVisualizer(
      d3.select(bodyEl),
      () => mode,
    );
    this.renderNodeInSidePanel(node, this.popupNodeVisualizer);
  }

  /**
   * Close the node edit popup, if one is open.
   */
  private closeNodeEditPopup(): void {
    this.nodeEditPopup?.close();
  }

  /**
   * Whether a node is a root of its recipe — no other node in the pool
   * lists it as a child. Works for both API-loaded and locally created
   * nodes.
   */
  private isRootNodeInPool(node: DisplayNode): boolean {
    return this.store.getParentIds(node.id).length === 0;
  }

  /**
   * How many nodes are currently selected — drives the switch between
   * single-node toolbar actions (✎/⚗/★ available) and multi-select ones
   * ([+]/[×]/[↥] only, once there are 2+).
   */
  private selectedNodeCount(): number {
    return this.store.getSelectedNodes().length;
  }

  /**
   * Whether the currently selected node can be made the main root: it must
   * be a root of its recipe and not already the main root. Drives the
   * toolbar's ★ button visibility. Only applies with exactly one node
   * selected.
   */
  private canMakeSelectedNodeMainRoot(): boolean {
    if (!this.store.isEditMode() || this.selectedNodeCount() !== 1) return false;
    const node = this.store.getSelectedNode();
    if (!node || node.isMainRoot) return false;
    return this.isRootNodeInPool(node);
  }

  private updateSidePanel(): void {
    this.ensureNodeVisualizer();

    const selectedNodes = this.store.getSelectedNodes();
    const count = selectedNodes.length;

    this.updateEditToolbarButtons();

    if (!this.sidePanel || !this.nodeVisualizer) return;

    if (count >= 2) {
      this.nodeVisualizer.showMultiSelection(selectedNodes);
      return;
    }

    if (count === 0) {
      this.nodeVisualizer.clear();
      return;
    }

    this.renderNodeInSidePanel(selectedNodes[0]);
  }

  /**
   * Enter edit mode — enables edit UI on selected nodes.
   *
   * Never auto-expands/focuses the selected recipe — entering edit mode
   * with a recipe selected but not focused lands in "global" edition mode
   * (only [+]/[×] delete-recipe apply), exactly as if nothing were
   * selected at all for node-editing purposes. If the selected recipe
   * happens to already be fully loaded (e.g. it's focused, or was
   * previously), its ChangeTracker is initialized here; otherwise (not
   * yet focused, so only its minimal search-result stub is in the pool)
   * it's left uninitialized and created later instead, once the recipe is
   * actually focused (see the store subscription in the constructor) —
   * building a snapshot from incomplete data here would leave the tracker
   * comparing against the wrong baseline once the real data loads in.
   */
  private enterEditMode(): void {
    // Save current filter state (for search filters) so we can restore it on exit
    this._savedFilterState = [];
    this.nodePool.forEach(node => {
      if (node.__inactive) {
        this._savedFilterState!.push(node.id);
      }
    });

    // Clear inactive states from search filters — all nodes become fully visible
    this.clearInactiveNodes();

    if (this.store.getSelectedRecipeId()) {
      const recipeEntry = this.store.getRecipeEntry(this.store.getSelectedRecipeId()!);

      // Initialize change tracker with a snapshot of the current recipe
      // graph — only once it's fully loaded (see this method's doc comment).
      if (recipeEntry?.fullyLoaded) {
        try {
          this.store.initializeChangeTracker(this.store.getSelectedRecipeId()!);
        } catch (err) {
          console.error('Failed to initialize ChangeTracker:', err);
        }
      }
    } // else: no recipe selected — all nodes remain normally visible

    // If a recipe is already selected, dim all nodes except those of the selected recipe.
    if (this.store.getSelectedRecipeId()) {
      this.store.setNodesInactiveWhere(node => node.recipeId !== this.store.getSelectedRecipeId());
    }

    // Re-render to show edit state
    this.renderAll();

    // Update side panel so it reflects edit mode (show edit fields)
    this.updateSidePanel();

    // Refresh the quantity row to become editable
    this.refreshHeaderQuantityDisplay();
  }

  /**
   * Exit edit mode — handles unsaved changes, cleans up, and restores view state.
   * If there are unsaved changes, shows a confirmation dialog with save/discard/cancel.
   * Only actually exits if the user confirms (save or discard); cancel keeps edit mode on.
   */
  private exitEditMode(): void {
    if (this.store.hasUnsavedChanges()) {
      showUnsavedChangesDialog({
        recipeTitle: this.getSelectedRecipeTitle() ?? 'la recepta',
        isMobile: this._isMobile,
        onSave: async () => {
          try {
            await this.saveRecipeChanges();
            this.store.setEditMode(false);
            this.cleanupEditMode();
            this._fireEditModeChanged(false);
          } catch {
            // Save failed — stay in edit mode
          }
        },
        onDiscard: () => {
          this.discardChanges();
          this.store.setEditMode(false);
          this.cleanupEditMode();
          this._fireEditModeChanged(false);
        },
        onCancel: () => {
          // Stay in edit mode — don't change _editMode or fire callback
        },
      });
    } else {
      this.store.setEditMode(false);
      this.cleanupEditMode();
      this._fireEditModeChanged(false);
    }
  }

  /**
   * Clean up edit mode state without discarding changes.
   * Called after save or discard to restore view mode UI.
   */
  private cleanupEditMode(): void {
    this.store.clearChangeTracker();

    // Close the node edit popup, if open — its editable content only makes
    // sense while in edit mode.
    this.closeNodeEditPopup();

    // Cancel any active link flow or pick node mode
    if (this.store.getEditionSubMode() !== StoreEditionMode.NONE) {
      if (this.store.getEditionSubMode() === StoreEditionMode.PICK_NODE) {
        this.cancelPickNode('cleanupEditMode');
      } else {
        this.store.setEditionSubMode(StoreEditionMode.NONE);
        if (this.tempLinkGroup) this.tempLinkGroup.style('display', 'none');
        if (this.svg) this.svg.on('mousemove', null);
      }
    }
    this.tempLinkSourceNode = null;

    // Remove edit buttons
    if (this.editButtonRenderer) {
      this.editButtonRenderer.removeAll();
    }

    // Restore saved filter state if any
    if (this._savedFilterState) {
      if (this._savedFilterState.length > 0) {
        this.setActiveNodes(this._savedFilterState);
      } else {
        this.clearInactiveNodes();
      }
      this._savedFilterState = null;
    } else {
      this.clearInactiveNodes();
    }

    // Remove any edit-mode-only inactive states we added
    if (this._savedFilterState === null) {
      this.store.clearAllInactive();
    }

    this.renderAll();

    // Update side panel so it reflects view mode (hide edit fields)
    this.updateSidePanel();

    // Refresh the quantity row to become read-only
    this.refreshHeaderQuantityDisplay();
  }

  // ============================================================================
  // EDIT MODE HELPERS
  // ============================================================================

  /**
   * Get the title of the selected recipe (for dialog messages).
   */
  private getSelectedRecipeTitle(): string | null {
    if (!this.store.getSelectedRecipeId()) return null;
    const recipeEntry = this.store.getRecipeEntry(this.store.getSelectedRecipeId()!);
    if (!recipeEntry) return null;
    const mainRootId = recipeEntry.rootNodeIds[recipeEntry.mainRootIndex];
    const mainRoot = this.nodePool.get(mainRootId);
    return mainRoot?.name ?? null;
  }

  /**
   * Rebuild the linkPool for a specific recipe by walking each node's children array.
   * Removes all existing links that involve this recipe's nodes, then recreates them.
   */
  private rebuildLinksForRecipe(recipeId: string): void {
    // Remove links involving this recipe's nodes
    this.store.setLinkPool(this.linkPool.filter(
      link => link.source.recipeId !== recipeId && link.target.recipeId !== recipeId
    ));

    // Rebuild links from children data
    const recipeNodes = this.store.getRecipeNodes(recipeId);

    for (const node of recipeNodes) {
      const sourceNode = this.nodePool.get(node.id);
      if (!sourceNode) continue;

      for (const childId of (node.data.children || [])) {
        const targetNode = this.nodePool.get(childId);
        if (!targetNode) continue;

        // Check for duplicate
        const exists = this.linkPool.some(
          l => l.source.id === node.id && l.target.id === childId
        );
        if (!exists) {
          const newLink: Link<DisplayNode> = {
            source: sourceNode,
            target: targetNode,
            shape: { x: 0, y: 0 },
            active: true,
          };
          this.store.addLinks([newLink]);
        }
      }
    }

    // Assign shapes for the rebuilt links
    for (const link of this.linkPool) {
      if (link.source.recipeId === recipeId || link.target.recipeId === recipeId) {
        assignLinkShape(link);
      }
    }
  }

  /**
   * Show the "pick a node" banner — only while picking with the node-edit
   * popup hidden out of the way (see the onStartPickNode callback), since
   * that's the one case with no other visible cue that anything is
   * happening. Sticky (no auto-dismiss, unlike showToast()) since picking
   * can take longer than a toast's few seconds — removed by
   * hidePickBanner() once picking ends, one way or another.
   */
  private showPickBanner(onCancel: () => void): void {
    this.hidePickBanner();

    const banner = document.createElement('div');
    banner.className = 'pick-node-banner';
    banner.style.cssText = `
      position: fixed;
      top: 16px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 16px;
      background: var(--accent);
      color: #0d1117;
      font-family: 'Courier New', Consolas, 'Liberation Mono', monospace;
      font-size: 13px;
      font-weight: bold;
      border-radius: 8px;
      z-index: 10001;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `;
    banner.textContent = 'Tria un node al graf, la llista, els ingredients o la preparació…';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.textContent = 'Cancel·la';
    cancelBtn.style.cssText = `
      padding: 2px 10px;
      border-radius: 6px;
      border: 1px solid #0d1117;
      background: transparent;
      color: #0d1117;
      font-family: inherit;
      font-size: 12px;
      font-weight: bold;
      cursor: pointer;
    `;
    cancelBtn.addEventListener('click', onCancel);
    banner.appendChild(cancelBtn);

    document.body.appendChild(banner);
    this._pickBanner = banner;
  }

  /** Remove the "pick a node" banner, if shown. Safe to call unconditionally. */
  private hidePickBanner(): void {
    this._pickBanner?.remove();
    this._pickBanner = null;
  }

  // ============================================================================
  // GLOBAL (+) BUTTON
  // ============================================================================

  /**
   * Handle a click on the toolbar's [+] create button.
   *
   * Global vs. recipe edition mode is keyed on whether a recipe is
   * *focused* (not merely selected) — a recipe can be selected (shown in
   * the header) while browsing unfocused among every other recipe, and in
   * that state [+] still creates a new recipe, same as with nothing
   * selected at all. Only once a recipe is focused (unfolded/zoomed into)
   * does [+] start creating nodes within it.
   * - No recipe focused: opens recipe creation dialog (fires callback)
   * - Recipe focused: creates a new empty node in that recipe
   */
  private handleGlobalCreateClick(): void {
    if (!this.store.isEditMode()) return;

    if (this.selectedNodeCount() >= 2) {
      // 2+ nodes selected — create a node as the child of all of them
      this.createNodeAsChildOfSelectedNodes();
    } else if (this.store.getFocusedRecipeId()) {
      // Recipe focused — create a new node in that recipe
      this.createNodeInSelectedRecipe();
    } else {
      // No recipe focused — fire callback to create new recipe
      // If no callback registered, do nothing (button exists but consumer handles it)
      if (this._onCreateNewRecipeCallback) {
        this._onCreateNewRecipeCallback();
      }
    }
  }

  /**
   * Create a new empty node as the child of every currently selected node
   * (2+ — the toolbar routes here only then). Placed below the centroid
   * of the selected nodes, expanding any of them that was folded so the
   * new node is immediately visible everywhere it's attached.
   */
  private createNodeAsChildOfSelectedNodes(): void {
    const parents = this.store.getSelectedNodes();
    if (parents.length < 2) return;
    const recipeId = parents[0].recipeId;

    const avgX = parents.reduce((sum, n) => sum + (n.x ?? 0), 0) / parents.length;
    const avgY = parents.reduce((sum, n) => sum + (n.y ?? 0), 0) / parents.length;
    const jitterX = (Math.random() - 0.5) * 60;
    const jitterY = (Math.random() - 0.5) * 60;

    const dnode = this.store.createBlankNode(recipeId, avgX + jitterX, avgY + 90 + jitterY);
    const newNodeId = dnode.id;
    this.store.addNodes([dnode]);

    for (const parentNode of parents) {
      this.store.updateNodeChildren(parentNode.id, [...parentNode.data.children, newNodeId]);
      const newLink: Link<DisplayNode> = { source: parentNode, target: dnode, shape: { x: 0, y: 0 }, active: true };
      this.store.addLinks([newLink]);
      assignLinkShape(newLink);

      if (!this.store.isNodeExpanded(parentNode.id)) {
        this.store.expandNode(parentNode.id);
      }
    }

    this.renderAll();
    this.restartSimulation(0.3);
    this.store.syncMainRootFoldState(recipeId);

    this.selectNode(dnode);
  }

  // ============================================================================
  // MUTATION OPERATIONS
  // ============================================================================

  /**
   * Create a new empty node in the currently selected recipe.
   * Auto-enters edit mode if not already in it.
   * If a node is currently selected, the new node is attached as its
   * child (placed just below it, expanding it first if it was folded, so
   * the new node is immediately visible) — otherwise it's placed
   * unattached at the visible viewport center, as before.
   */
  createNodeInSelectedRecipe(): void {
    if (!this.store.getSelectedRecipeId()) {
      console.warn('No recipe selected — cannot create node');
      return;
    }

    // Auto-enter edit mode if not already
    if (!this.store.isEditMode()) {
      this.editMode = true;

      // If edit mode wasn't entered (e.g., expansion failed), bail out
      if (!this.store.isEditMode()) return;
    }

    const selectedNode = this.store.getSelectedNode();
    const parentNode = selectedNode && selectedNode.recipeId === this.store.getSelectedRecipeId()
      ? selectedNode
      : null;

    // Place near the parent if attaching to one, else at the viewport
    // center — jitter either way so successive nodes don't overlap exactly.
    const jitterX = (Math.random() - 0.5) * 60;
    const jitterY = (Math.random() - 0.5) * 60;
    let baseX: number;
    let baseY: number;
    if (parentNode) {
      baseX = parentNode.x ?? 0;
      baseY = (parentNode.y ?? 0) + 90;
    } else {
      const viewCenter = this.getVisibleViewportCenter();
      baseX = viewCenter.x;
      baseY = viewCenter.y;
    }

    const dnode = this.store.createBlankNode(this.store.getSelectedRecipeId()!, baseX + jitterX, baseY + jitterY);
    const newNodeId = dnode.id;
    this.store.addNodes([dnode]);

    if (parentNode) {
      this.store.updateNodeChildren(parentNode.id, [...parentNode.data.children, newNodeId]);
      const newLink: Link<DisplayNode> = { source: parentNode, target: dnode, shape: { x: 0, y: 0 }, active: true };
      this.store.addLinks([newLink]);
      assignLinkShape(newLink);

      // Make sure the new child is actually visible if the parent was folded.
      if (!this.store.isNodeExpanded(parentNode.id)) {
        this.store.expandNode(parentNode.id);
      }
    }

    // Re-render
    this.renderAll();
    this.restartSimulation(0.3);

    // Sync main root fold state — creating the first visible node unfolds the recipe
    this.store.syncMainRootFoldState(dnode.recipeId);

    // Select the new node (opens side panel for immediate editing)
    this.selectNode(dnode);
  }

  /**
   * Handle adding a new node to the pool when the consumer creates a recipe.
   * Called after a recipe is successfully created via the dialog.
   * Adds the main root node and auto-selects.
   */
  addNewRecipeToPool(recipeId: string, mainRootNode: ApiRecipeNode): void {
    const dnode = this.store.createDisplayNodeFromApi(mainRootNode, recipeId, true);
    // Scatter freshly created recipes near the viewport center, like the
    // browse-mode layout does — createDisplayNodeFromApi() otherwise reads
    // position from the node's own (not-yet-set) displayData.
    dnode.x = this.width / 2 + (Math.random() - 0.5) * 100;
    dnode.y = 150 + Math.random() * 20;

    this.store.addRecipe(recipeId, {
      rootNodeIds: [mainRootNode.id],
      mainRootIndex: 0,
      fullyLoaded: true, // Full graph returned by createRecipe API
    });

    this.store.addNodes([dnode]);
    this.renderAll();
    this.restartSimulation(0.3);

    // Auto-select the new recipe
    this.selectNode(dnode);
    
    // Focus on the new recipe (hide other recipes)
    this.store.focusRecipe(recipeId);
  }

  // ============================================================================
  // ADD LINK FLOW
  // ============================================================================

  /**
   * Start the add-link flow: show temp line, dim invalid targets,
   * and listen for clicks/drags to complete or cancel.
   */
  private startAddLinkFlow(sourceNode: DisplayNode, mode: StoreEditionMode.ADD_PARENT_LINK | StoreEditionMode.ADD_CHILD_LINK): void {

    if (this.store.getEditionSubMode() !== StoreEditionMode.NONE) {
      this.cancelLinkFlow('startAddLinkFlow');
    }

    this.store.setEditionSubMode(mode);
    this.tempLinkSourceNode = sourceNode;
    this.tempLinkSourcePos = { x: sourceNode.x ?? 0, y: sourceNode.y ?? 0 };
    this.tempLinkIsAdd = true;

    // Show temp line at source position
    if (this.tempLinkLine && this.tempLinkGroup) {
      this.tempLinkLine
        .attr('x1', sourceNode.x ?? 0)
        .attr('y1', sourceNode.y ?? 0)
        .attr('x2', sourceNode.x ?? 0)
        .attr('y2', sourceNode.y ?? 0)
        .style('stroke', 'var(--accent)')
        .attr('stroke-dasharray', '5,5')
        .style('opacity', '1');
      this.tempLinkGroup.style('display', null);
    }

    // Dim invalid targets
    this.applyLinkTargetInactivation(sourceNode, mode);

    // Set up mousemove (+ capturing touch handlers) BEFORE click listeners,
    // so the drag touchend handler fires FIRST (capturing order = registration
    // order on the same element). This prevents the tap handler from cancelling
    // the link flow before the drag handler can process a drag release.
    this.setupLinkFlowMouseMove();

    // Set up click-on-background to cancel, and on-node click to complete
    this.setupLinkFlowListeners();

    // Update node visual states for inactive targets WITHOUT full re-render.
    // Full renderAll() destroys and recreates edit buttons (including the touch
    // target hit area), which confuses the browser's touch tracking and prevents
    // subsequent touchmove/touchend events from being dispatched.
    if (this.nodesGroup) {
      this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node')
        .select('.viz-node-image')
        .style('opacity', '1');
      this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node')
        .select('.viz-node-title')
        .attr('fill', (d: DisplayNode) => d.__inactive ? '#555' : '#e0e0e0');
    }
  }

  /**
   * Apply __inactive to nodes that are invalid targets for the current link operation.
   * For ADD_PARENT_LINK: dim source node, its descendants, and its existing parents.
   * For ADD_CHILD_LINK: dim source node, its ancestors, and its existing children.
   * Nodes from other recipes are always dimmed (can't link across recipes).
   */
  private applyLinkTargetInactivation(
    sourceNode: DisplayNode,
    mode: StoreEditionMode.ADD_PARENT_LINK | StoreEditionMode.ADD_CHILD_LINK,
  ): void {
    const invalidIds = new Set<string>();
    invalidIds.add(sourceNode.id);

    if (mode === StoreEditionMode.ADD_PARENT_LINK) {
      // Dim descendants
      const descendants = this.store.getDescendantsInPool(sourceNode);
      for (const d of descendants) invalidIds.add(d.id);

      // Dim existing parents
      for (const parentId of this.store.getParentIds(sourceNode.id)) {
        invalidIds.add(parentId);
      }
    } else {
      // ADD_CHILD_LINK: dim ancestors
      const ancestors = this.store.getAncestorsInPool(sourceNode);
      for (const a of ancestors) invalidIds.add(a.id);

      // Dim existing children
      if (sourceNode.data.children) {
        for (const cid of sourceNode.data.children) {
          invalidIds.add(cid);
        }
      }
    }

    // Nodes from other recipes are always invalid targets, alongside invalidIds.
    this.store.setNodesInactiveWhere(node => node.recipeId !== sourceNode.recipeId || invalidIds.has(node.id));
  }

  /**
   * Set up click listeners to complete or cancel the link flow.
   * Click on a valid (non-inactive) node → create link
   * Click on background or Escape → cancel
   */
  private setupLinkFlowListeners(): void {
    if (!this.svg) return;

    const svgNode = this.svg.node();
    if (!svgNode) return;



    // Click handler for completing or cancelling the link flow.
    // (Does nothing if the flow was already completed by a drag mouseup.)
    const clickHandler = (event: MouseEvent) => {
      // Already handled by drag? Bail out.
      if (this.store.getEditionSubMode() === StoreEditionMode.NONE) return;

      const target = event.target as Element | null;
      if (!target) return;

      // If clicking an edit button, ignore
      if (target.closest('.edit-btn, .edit-button-group')) return;

      // If clicking a node, check if it's a valid target
      const nodeGroup = target.closest('.viz-node');
      if (nodeGroup) {
        const nodeId = (nodeGroup as SVGElement).id?.replace('viz-node-', '');
        const node = nodeId ? this.nodePool.get(nodeId) : null;
        
        if (node && !node.__inactive && node.id !== this.tempLinkSourceNode?.id) {
          // Valid target — create link
          this.completeAddLink(node);
          this.cleanupLinkFlowListeners(clickHandler, escapeHandler);
          return;
        }
        // Clicked on an invalid node (source or inactive) — cancel
        this.cancelLinkFlow('clickHandler:invalid');
        this.cleanupLinkFlowListeners(clickHandler, escapeHandler);
        return;
      }

      // Click on background — cancel
      this.cancelLinkFlow('clickHandler:background');
      this.cleanupLinkFlowListeners(clickHandler, escapeHandler);
    };

    const escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.store.getEditionSubMode() !== StoreEditionMode.NONE) {
        this.cancelLinkFlow('escape');
        this.cleanupLinkFlowListeners(clickHandler, escapeHandler);
      }
    };

    // Capturing-phase touchend for mobile tap handling.
    // Fires before D3 drag's handlers can call preventDefault() and block click synthesis.
    // Uses elementFromPoint() to determine what was tapped.
    const touchEndHandler = (e: TouchEvent) => {
      if (this.store.getEditionSubMode() === StoreEditionMode.NONE) return;
      if (e.defaultPrevented) return;

      const touch = e.changedTouches[0];
      const elem = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!elem) return;

      // If touching an edit button, ignore (the user is still interacting with controls)
      if (elem.closest('.edit-btn, .edit-button-group')) return;

      const nodeGroup = elem.closest('.viz-node');
      if (nodeGroup) {
        const nodeId = (nodeGroup as SVGElement).id?.replace('viz-node-', '');
        const node = nodeId ? this.nodePool.get(nodeId) : null;

        if (node && !node.__inactive && node.id !== this.tempLinkSourceNode?.id) {
          // Valid target — create link
          this.completeAddLink(node);
          this.cleanupLinkFlowListeners(clickHandler, escapeHandler);
          return;
        }
        // Invalid target (source or inactive) — cancel
        this.cancelLinkFlow('touchEnd:invalid');
        this.cleanupLinkFlowListeners(clickHandler, escapeHandler);
        return;
      }

      // Background — cancel
      this.cancelLinkFlow('touchEnd:background');
      this.cleanupLinkFlowListeners(clickHandler, escapeHandler);
    };

    svgNode.addEventListener('click', clickHandler);
    svgNode.addEventListener('touchend', touchEndHandler, { capture: true, passive: true });
    document.addEventListener('keydown', escapeHandler);

    // Store cleanup for the capturing-phase touchend handler
    this.tempLinkCleanups.push(() => {
      svgNode.removeEventListener('touchend', touchEndHandler, { capture: true });
    });
  }

  /**
   * Set up mousemove to update the temp link line,
   * and mouseup to detect drag-to-create when released over empty space.
   */
  private setupLinkFlowMouseMove(): void {
    if (!this.svg) return;

    const svgEl = this.svg.node() as SVGSVGElement;
    if (!svgEl) return;

    // ── Capturing-phase touch handlers for drag tracking (mobile) ──
    // These fire before D3 drag's handlers can preventDefault(), so they
    // can track touch movement even when dragging from an edit button.
    // They handle drag-to-create release (Issue 3) and ignore real taps
    // (<10px movement) which are handled by the touchend tap handler.
    let touchDragStartX = 0;
    let touchDragStartY = 0;
    let touchDragStarted = false;

    const touchMoveHandler = (e: TouchEvent) => {
      if (this.store.getEditionSubMode() === StoreEditionMode.NONE) return;
      const touch = e.touches[0];

      if (!touchDragStarted) {
        touchDragStartX = touch.clientX;
        touchDragStartY = touch.clientY;
        touchDragStarted = true;
      }
      if (!this.tempLinkLine || !this.tempLinkSourceNode) return;

      // Only update temp line if moved enough (avoid micro-movement noise)
      const dx = touch.clientX - touchDragStartX;
      const dy = touch.clientY - touchDragStartY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 5) return;

      const [svgX, svgY] = d3.pointer(touch, this.mainGroup!.node()!);

      // Check for snap target
      const elem = document.elementFromPoint(touch.clientX, touch.clientY);
      const nodeGroup = elem?.closest('.viz-node');
      let snapTarget: DisplayNode | null = null;
      if (nodeGroup) {
        const nodeId = (nodeGroup as SVGElement).id?.replace('viz-node-', '');
        const node = nodeId ? this.nodePool.get(nodeId) : null;
        if (node && !node.__inactive && node.id !== this.tempLinkSourceNode.id) {
          snapTarget = node;
        }
      }

      if (snapTarget) {
        this.tempLinkLine.attr('x2', snapTarget.x ?? 0).attr('y2', snapTarget.y ?? 0);
        if (this.tempGhostNode) this.tempGhostNode.style('display', 'none');
      } else {
        this.tempLinkLine.attr('x2', svgX).attr('y2', svgY);
        if (this.tempGhostNode) {
          this.tempGhostNode.style('display', null).attr('transform', `translate(${svgX}, ${svgY})`);
        }
      }
    };

    const touchDragEndHandler = (e: TouchEvent) => {
      if (this.store.getEditionSubMode() === StoreEditionMode.NONE || !this.tempLinkSourceNode) return;
      if (!touchDragStarted) return;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - touchDragStartX;
      const dy = touch.clientY - touchDragStartY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 10) return; // Real tap — handled by capturing touchend in setupLinkFlowListeners

      // Process as a drag release
      const elem = document.elementFromPoint(touch.clientX, touch.clientY);
      const nodeGroup = elem?.closest('.viz-node');

      if (nodeGroup) {
        const nodeId = (nodeGroup as SVGElement).id?.replace('viz-node-', '');
        const node = nodeId ? this.nodePool.get(nodeId) : null;
        if (node && !node.__inactive && node.id !== this.tempLinkSourceNode.id) {
          this.completeAddLink(node);
          cleanup();
          return;
        }
      }

      // Over empty space (or invalid node) — drag-to-create
      const [svgX, svgY] = d3.pointer(touch, this.mainGroup!.node()!);

      this.dragCreateNodeWithLink(svgX, svgY);
      cleanup();
    };

    svgEl.addEventListener('touchmove', touchMoveHandler, { capture: true, passive: true });
    svgEl.addEventListener('touchend', touchDragEndHandler, { capture: true, passive: true });
    this.tempLinkCleanups.push(() => {
      svgEl.removeEventListener('touchmove', touchMoveHandler, { capture: true });
      svgEl.removeEventListener('touchend', touchDragEndHandler, { capture: true });
    });

    // Track whether mouse has moved significantly (to distinguish click vs drag)
    let hasDragged = false;

    const mouseMoveHandler = (event: MouseEvent) => {
      if (!this.tempLinkLine || !this.tempLinkSourceNode) return;

      // Convert screen coords to mainGroup local (pre-transform) coords.
      // d3.pointer correctly handles SVG viewBox scaling + zoom/pan transforms.
      const [svgX, svgY] = d3.pointer(event, this.mainGroup!.node()!);

      // Check if cursor is over a valid target node (using elementFromPoint
      // since the temp line has pointer-events: none)
      const elem = document.elementFromPoint(event.clientX, event.clientY);
      const nodeGroup = elem?.closest('.viz-node');
      let snapTarget: DisplayNode | null = null;

      if (nodeGroup) {
        const nodeId = (nodeGroup as SVGElement).id?.replace('viz-node-', '');
        const node = nodeId ? this.nodePool.get(nodeId) : null;
        if (node && !node.__inactive && node.id !== this.tempLinkSourceNode.id) {
          snapTarget = node;
        }
      }

      if (snapTarget) {
        // Snap temp line to the target node's center
        this.tempLinkLine
          .attr('x2', snapTarget.x ?? 0)
          .attr('y2', snapTarget.y ?? 0);
        // Hide ghost node
        if (this.tempGhostNode) {
          this.tempGhostNode.style('display', 'none');
        }
      } else {
        // Follow cursor
        this.tempLinkLine
          .attr('x2', svgX)
          .attr('y2', svgY);
        // Show ghost node only during active drag (mouse button held).
        // After the initial click (mouseup), the cursor may still move but
        // the user is navigating to a target — no ghost node should appear.
        if (event.buttons > 0 && this.tempGhostNode) {
          this.tempGhostNode
            .style('display', null)
            .attr('transform', `translate(${svgX}, ${svgY})`);
        } else if (this.tempGhostNode) {
          this.tempGhostNode.style('display', 'none');
        }
      }

      hasDragged = true;
    };

    // Clean up any previous document listener before setting up a new one
    if (this.tempLinkDocMouseUpHandler) {
      document.removeEventListener('mouseup', this.tempLinkDocMouseUpHandler);
    }
    this.tempLinkDocMouseUpHandler = null;

    const mouseUpHandler = (event: MouseEvent) => {
      if (!hasDragged) return; // Was a click, not a drag — handled by click listener

      if (!this.store.isEditMode() || this.store.getEditionSubMode() === StoreEditionMode.NONE || !this.tempLinkSourceNode) {
        cleanup();
        return;
      }

      // Check if released over a valid target node
      const target = event.target as Element | null;
      const nodeGroup = target?.closest('.viz-node');

      if (nodeGroup) {
        const nodeId = (nodeGroup as SVGElement).id?.replace('viz-node-', '');
        const node = nodeId ? this.nodePool.get(nodeId) : null;
        
        if (node && !node.__inactive && node.id !== this.tempLinkSourceNode.id) {
          // Released over a valid target node → create link
          this.completeAddLink(node);
          cleanup();
          return;
        }
      }

      // Released over empty space → drag-to-create: new node + link
      const [svgX, svgY] = d3.pointer(event, this.mainGroup!.node()!);

      this.dragCreateNodeWithLink(svgX, svgY);
      cleanup();
    };

    const cleanup = () => {
      if (this.svg) {
        this.svg.on('mousemove', null);
        this.svg.on('mouseup', null);
      }
      if (this.tempLinkDocMouseUpHandler) {
        document.removeEventListener('mouseup', this.tempLinkDocMouseUpHandler);
        this.tempLinkDocMouseUpHandler = null;
      }
    };

    // Store as class field so cancelLinkFlow can clean it up too
    this.tempLinkDocMouseUpHandler = (e: MouseEvent) => {
      mouseUpHandler(e);
    };

    // Register mouse handlers for desktop drag tracking
    this.svg.on('mousemove', mouseMoveHandler as any);
    this.svg.on('mouseup', mouseUpHandler as any);
    // Also listen on document to catch releases outside the SVG
    document.addEventListener('mouseup', this.tempLinkDocMouseUpHandler);
  }

  /**
   * Drag-to-create: create a new node at the given position and link it
   * from the current source node (or to it, depending on the sub-mode).
   */
  private dragCreateNodeWithLink(x: number, y: number): void {
    if (!this.tempLinkSourceNode || !this.store.getSelectedRecipeId()) return;

    const sourceNode = this.tempLinkSourceNode;
    const isAddParent = this.store.getEditionSubMode() === StoreEditionMode.ADD_PARENT_LINK;
    const recipeId = this.store.getSelectedRecipeId()!;

    const dnode = this.store.createBlankNode(recipeId, x, y);
    this.store.addNodes([dnode]);

    // Create link depending on mode
    if (isAddParent) {
      // new node → source (parent link: new node is parent of source)
      this.store.addChildToNode(dnode.id, sourceNode.id);
      const newLink: Link<DisplayNode> = {
        source: dnode,
        target: sourceNode,
        shape: { x: 0, y: 0 },
        active: true,
      };
      this.store.addLinks([newLink]);
      assignLinkShape(newLink);

      // If source was the main root, the new parent becomes the new main root
      if (sourceNode.isMainRoot) {
        this.store.handleMakeMainRoot(dnode.id);
      }
    } else {
      // source → new node (child link: new node is child of source)
      this.store.addChildToNode(sourceNode.id, dnode.id);
      const newLink: Link<DisplayNode> = {
        source: sourceNode,
        target: dnode,
        shape: { x: 0, y: 0 },
        active: true,
      };
      this.store.addLinks([newLink]);
      assignLinkShape(newLink);
    }

    // Clean up link flow
    this.cancelLinkFlow('dragCreateNodeWithLink');

    // Re-render
    this.renderAll();
    this.restartSimulation(0.3);

    // Select the new node
    this.selectNode(dnode);
  }

  /**
   * Complete the add-link flow by creating a link from source to target.
   */
  private completeAddLink(targetNode: DisplayNode): void {
    if (!this.tempLinkSourceNode) return;

    const sourceNode = this.tempLinkSourceNode;
    const isAddParent = this.store.getEditionSubMode() === StoreEditionMode.ADD_PARENT_LINK;

    if (isAddParent) {
      // Add parent link: target becomes parent of source
      this.store.addChildToNode(targetNode.id, sourceNode.id);
      // If source was the main root, the target becomes the new main root
      if (sourceNode.isMainRoot) {
        this.store.handleMakeMainRoot(targetNode.id);
      }
    } else {
      // Add child link: target becomes child of source
      this.store.addChildToNode(sourceNode.id, targetNode.id);
      // If target was the main root, the source (which becomes its parent)
      // becomes the new main root
      if (targetNode.isMainRoot) {
        this.store.handleMakeMainRoot(sourceNode.id);
      }
    }

    // Create visual link
    const newLink: Link<DisplayNode> = {
      source: isAddParent ? targetNode : sourceNode,
      target: isAddParent ? sourceNode : targetNode,
      shape: { x: 0, y: 0 },
      active: true,
    };

    // Check for duplicate before adding
    const exists = this.linkPool.some(
      l => l.source.id === newLink.source.id && l.target.id === newLink.target.id
    );

    if (!exists) {
      this.store.addLinks([newLink]);
      assignLinkShape(newLink);
    }

    // Clean up flow state
    this.cancelLinkFlow('completeAddLink');

    // Re-render
    this.renderAll();
    this.restartSimulation(0.2);
  }

  /**
   * Clean up link flow event listeners.
   */
  private cleanupLinkFlowListeners(
    clickHandler: (event: MouseEvent) => void,
    escapeHandler: (e: KeyboardEvent) => void,
  ): void {
    const svgNode = this.svg?.node();
    if (svgNode) {
      svgNode.removeEventListener('click', clickHandler);
    }
    document.removeEventListener('keydown', escapeHandler);
    if (this.svg) {
      this.svg.on('mousemove', null);
      this.svg.on('mouseup', null);
    }
    // Clean up capturing-phase touch handlers
    this.runTempLinkCleanups();
  }

  /**
   * Cancel the current link flow: remove temp line, restore inactive states.
   */
  private cancelLinkFlow(caller?: string): void {
    // Hide temp line
    if (this.tempLinkGroup) {
      this.tempLinkGroup.style('display', 'none');
    }

    // Restore inactive states for non-selected-recipe nodes
    if (this.store.isEditMode() && this.store.getSelectedRecipeId()) {
      this.store.setNodesInactiveWhere(node => node.recipeId !== this.store.getSelectedRecipeId());
    } else {
      this.store.clearAllInactive();
    }

    // Reset state
    this.store.setEditionSubMode(StoreEditionMode.NONE);
    this.tempLinkSourceNode = null;

    // Remove event listeners
    if (this.svg) {
      this.svg.on('mousemove', null);
      this.svg.on('mouseup', null);
    }
    if (this.tempLinkDocMouseUpHandler) {
      document.removeEventListener('mouseup', this.tempLinkDocMouseUpHandler);
      this.tempLinkDocMouseUpHandler = null;
    }
    this.runTempLinkCleanups();

    // Re-render
    this.renderAll();
  }

  /**
   * Run all cleanup functions for capturing-phase touch handlers
   * and clear the cleanup array.
   */
  private runTempLinkCleanups(): void {
    for (const cleanup of this.tempLinkCleanups) {
      cleanup();
    }
    this.tempLinkCleanups = [];
  }

  // ============================================================================
  // DELETE LINK FLOW
  // ============================================================================

  /**
   * Handle delete parent/child link click.
   * If exactly 1 parent/child: immediately delete.
   * If 2+: enter sub-mode to let user pick which link to delete.
   */
  private handleDeleteLink(node: DisplayNode, direction: 'parent' | 'child'): void {
    if (!this.store.getSelectedRecipeId()) return;
    const recipeId = this.store.getSelectedRecipeId()!;

    if (direction === 'parent') {
      // Find all parents of this node
      const parents = this.store.getParentIds(node.id)
        .map(id => this.nodePool.get(id))
        .filter((n): n is DisplayNode => !!n && n.recipeId === node.recipeId);

      if (parents.length === 0) return;

      if (parents.length === 1) {
        // Exactly one parent — delete immediately
        this.removeLink(parents[0], node);
      } else {
        // Multiple parents — enter sub-mode
        this.startDeleteLinkFlow(node, parents, 'parent');
      }
    } else {
      // Find all children of this node
      const children: DisplayNode[] = [];
      for (const childId of (node.data.children || [])) {
        const child = this.nodePool.get(childId);
        if (child && child.recipeId === node.recipeId) {
          children.push(child);
        }
      }

      if (children.length === 0) return;

      if (children.length === 1) {
        // Exactly one child — delete immediately
        this.removeLink(node, children[0]);
      } else {
        // Multiple children — enter sub-mode
        this.startDeleteLinkFlow(node, children, 'child');
      }
    }
  }

  /**
   * Enter delete-link sub-mode: dim all nodes except valid targets,
   * let user click one to delete that specific link.
   * Hovering over a candidate target highlights the link to be deleted.
   */
  private startDeleteLinkFlow(
    sourceNode: DisplayNode,
    targets: DisplayNode[],
    direction: 'parent' | 'child',
  ): void {
    this.store.setEditionSubMode(direction === 'parent'
      ? StoreEditionMode.DELETE_PARENT_LINK
      : StoreEditionMode.DELETE_CHILD_LINK);

    // Dim all nodes except the valid targets, in one pass.
    const targetIds = new Set(targets.map(t => t.id));
    this.store.setNodesInactiveWhere(node => !targetIds.has(node.id));

    this.renderAll();

    // Helper to find the link between source and a target
    const findLink = (t: DisplayNode) => {
      if (direction === 'parent') {
        return this.linkPool.find(l => l.source.id === t.id && l.target.id === sourceNode.id);
      } else {
        return this.linkPool.find(l => l.source.id === sourceNode.id && l.target.id === t.id);
      }
    };

    // Helper to highlight a link
    const highlightLink = (t: DisplayNode | null) => {
      if (!this.linksGroup) return;
      // Reset all links to normal style
      this.linksGroup.selectAll<SVGLineElement, any>('line')
        .style('stroke', 'var(--accent-alpha-40)')
        .attr('stroke-width', 3);
      // Highlight the target link
      if (t) {
        const link = findLink(t);
        if (link) {
          const linkId = `${link.source.id}→${link.target.id}`;
          this.linksGroup.selectAll<SVGLineElement, any>('line')
            .filter((d: any) => `${d.source.id}→${d.target.id}` === linkId)
            .style('stroke', 'var(--accent)')
            .attr('stroke-width', 5);
        }
      }
    };

    // Set up hover listeners on candidate nodes for link highlighting
    const nodeGroups = this.nodesGroup?.selectAll<SVGGElement, DisplayNode>('.viz-node');
    if (nodeGroups) {
      nodeGroups.each(function (d: DisplayNode) {
        if (targetIds.has(d.id)) {
          const el = d3.select<SVGGElement, DisplayNode>(this);
          el.on('mouseenter.deleteLink', () => {
            highlightLink(d);
          });
          el.on('mouseleave.deleteLink', () => {
            highlightLink(null);
          });
        }
      });
    }

    // Set up click-to-select on valid targets
    if (!this.svg) return;
    const svgNode = this.svg.node();
    if (!svgNode) return;

    const cleanup = () => {
      svgNode.removeEventListener('click', clickHandler);
      document.removeEventListener('keydown', escapeHandler);
      // Remove hover listeners
      if (nodeGroups) {
        nodeGroups.on('mouseenter.deleteLink', null);
        nodeGroups.on('mouseleave.deleteLink', null);
      }
      // Reset link highlights
      highlightLink(null);
    };

    const clickHandler = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target) return;
      if (target.closest('.edit-btn, .edit-button-group')) return;

      const nodeGroup = target.closest('.viz-node');
      if (nodeGroup) {
        const nodeId = (nodeGroup as SVGElement).id?.replace('viz-node-', '');
        const node = nodeId ? this.nodePool.get(nodeId) : null;
        if (node && !node.__inactive) {
          // User clicked a valid target — delete that link
          if (direction === 'parent') {
            this.removeLink(node, sourceNode);
          } else {
            this.removeLink(sourceNode, node);
          }
          this.cancelDeleteLinkFlow('deleteClick:valid');
          cleanup();
          return;
        }
      }

      // Background click or invalid target → cancel
      this.cancelDeleteLinkFlow('deleteClick:invalid');
      cleanup();
    };

    const escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.cancelDeleteLinkFlow('deleteFlow:escape');
        cleanup();
      }
    };

    // Capturing-phase touchend for mobile tap handling in delete flow.
    // Same logic as clickHandler but uses touch coordinates directly,
    // bypassing D3 drag's click suppression.
    const deleteTouchEndHandler = (e: TouchEvent) => {
      if (this.store.getEditionSubMode() === StoreEditionMode.NONE) return;
      if (e.defaultPrevented) return;

      const touch = e.changedTouches[0];
      const elem = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!elem) return;

      if (elem.closest('.edit-btn, .edit-button-group')) return;

      const nodeGroup = elem.closest('.viz-node');
      if (nodeGroup) {
        const nodeId = (nodeGroup as SVGElement).id?.replace('viz-node-', '');
        const node = nodeId ? this.nodePool.get(nodeId) : null;

        if (node && !node.__inactive) {
          if (direction === 'parent') {
            this.removeLink(node, sourceNode);
          } else {
            this.removeLink(sourceNode, node);
          }
          this.cancelDeleteLinkFlow('touchEnd:valid');
          cleanup();
          return;
        }
      }

      // Background or invalid → cancel
      this.cancelDeleteLinkFlow('touchEnd:invalid');
      cleanup();
    };

    svgNode.addEventListener('click', clickHandler);
    svgNode.addEventListener('touchend', deleteTouchEndHandler, { capture: true, passive: true });
    document.addEventListener('keydown', escapeHandler);

    this.tempLinkCleanups.push(() => {
      svgNode.removeEventListener('touchend', deleteTouchEndHandler, { capture: true });
    });
  }

  /**
   * Remove a link between two nodes: remove from children arrays and linkPool.
   */
  private removeLink(source: DisplayNode, target: DisplayNode): void {
    // Remove from source's children array
    this.store.updateNodeChildren(source.id, source.data.children.filter(cid => cid !== target.id));

    // Remove from linkPool
    this.store.removeLink(source.id, target.id);

    // Re-render
    this.renderAll();
    this.restartSimulation(0.2);
  }

  /**
   * Cancel delete-link sub-mode and restore normal edit inactive states.
   */
  private cancelDeleteLinkFlow(caller?: string): void {
    this.store.setEditionSubMode(StoreEditionMode.NONE);

    // Reset link highlights
    if (this.linksGroup) {
      this.linksGroup.selectAll<SVGLineElement, any>('line')
        .style('stroke', '#4a5a4a')
        .attr('stroke-width', 3);
    }

    // Remove hover listeners from nodes
    if (this.nodesGroup) {
      this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node')
        .on('mouseenter.deleteLink', null)
        .on('mouseleave.deleteLink', null);
    }

    // Restore normal inactive state for edit mode
    if (this.store.isEditMode() && this.store.getSelectedRecipeId()) {
      this.store.setNodesInactiveWhere(node => node.recipeId !== this.store.getSelectedRecipeId());
    } else {
      this.store.clearAllInactive();
    }

    this.runTempLinkCleanups();
    this.renderAll();
  }

  // ============================================================================
  // DELETE NODE
  // ============================================================================

  /**
   * Handle node pick during pick-node mode.
   * Copies the picked node's ID into the transformation parameter value
   * via the onPicked callback, then cleans up pick mode.
   */
  private handleNodePick(node: DisplayNode): void {
    if (this._pickOnPicked) {
      this._pickOnPicked(node.id);
    }
    this.cancelPickNode('picked');
  }

  /**
   * Public entry point for completing a pick from outside the graph
   * canvas — List View's row click, when a pick is in progress (see the
   * onStartPickNode callback in createNodeVisualizer()). Same effect as
   * clicking the node on the graph.
   */
  completePickNode(nodeId: string): void {
    const node = this.nodePool.get(nodeId);
    if (node) this.handleNodePick(node);
  }

  /**
   * Cancel pick-node mode without applying a picked node.
   * Cleans up state and restores normal node interaction.
   */
  private cancelPickNode(caller?: string): void {
    this._pickOnPicked = null;
    this._pickSourceNodeId = null;
    this._pickParamName = null;
    if (this.store.getEditionSubMode() === StoreEditionMode.PICK_NODE) {
      this.store.setEditionSubMode(StoreEditionMode.NONE);
      // Restore normal cursor
      if (this.svg) {
        this.svg.style('cursor', null);
      }
    }
    // Remove Escape key handler
    if (this._pickEscapeHandler) {
      document.removeEventListener('keydown', this._pickEscapeHandler);
      this._pickEscapeHandler = null;
    }
    // Remove outside-click cancel handler
    if (this._pickOutsideClickHandler) {
      document.removeEventListener('click', this._pickOutsideClickHandler, true);
      this._pickOutsideClickHandler = null;
    }
    // Restore the node-edit popup if it was hidden for this pick, and
    // remove the banner shown in its place — safe to call unconditionally,
    // both no-op if there was nothing to undo.
    this.nodeEditPopup?.show();
    this.hidePickBanner();
  }

  /**
   * Handle delete node (×) click.
   * Non-root: remove node + links.
   * Root: delete entire recipe with confirmation.
   */
  private handleDeleteNode(node: DisplayNode): void {
    if (node.isMainRoot) {
      this.handleDeleteRecipe(node);
    } else {
      // Non-root node: remove from pool and all links
      this.removeSingleNode(node);
    }
  }

  /**
   * Public entry point for requesting a node's deletion from outside the
   * graph view (e.g. List View's delete button). Reuses the same
   * root-vs-non-root branching, confirmation dialogs, and change tracking
   * as the graph's own (×) button.
   */
  requestDeleteNode(nodeId: string): void {
    const node = this.nodePool.get(nodeId);
    if (!node) return;
    this.handleDeleteNode(node);
  }

  /**
   * Public entry point for requesting a node's edit popup from outside the
   * graph view (e.g. List View's ✎ button) — same effect as the
   * EditToolbar's global edit button, for whichever node is given.
   */
  requestEditNode(nodeId: string): void {
    const node = this.nodePool.get(nodeId);
    if (!node) return;
    if (this.store.getSelectedNode()?.id !== node.id) {
      this.selectNode(node);
    }
    this.openNodeEditPopup();
  }

  /**
   * Public entry point for navigating to a node's ingredient page from
   * outside the graph view (e.g. List View's [⚘] button) — same effect as
   * the (now removed) EditToolbar's own [⚘] button.
   */
  requestGoToIngredient(nodeId: string): void {
    const node = this.nodePool.get(nodeId);
    const ing = node?.data.ingredientInfo?.ingredient;
    if (ing) this._onNavigateToIngredientCallback?.(ing.id);
  }

  /**
   * Public entry point for the add-elaboration action from outside the
   * graph view (List View's [⚗] button — only shown for a leaf node with
   * no elaboration yet; a node with one shows [⚗ׯ] remove instead, so
   * there's no longer a "modify" case to branch into here).
   */
  requestElaborationAction(nodeId: string): void {
    const node = this.nodePool.get(nodeId);
    const ing = node?.data.ingredientInfo?.ingredient;
    if (!node || !ing) return;
    void this.handleFindIngredientRecipes(node.id, ing.id, ing.name);
  }

  /**
   * Public entry point for the remove-elaboration action from outside the
   * graph view (e.g. List View's [⚗ׯ] button).
   */
  requestRemoveElaboration(nodeId: string): void {
    this.store.handleRemoveElaboration(nodeId);
  }

  /**
   * Public entry point for the [↧] add-child action from outside the
   * graph view (e.g. List View's own button) — same effect as the
   * EditToolbar's [+]/[↧] button with this node as the (only) selection,
   * reusing its exact same branching (handleGlobalCreateClick()).
   */
  requestCreateChildNode(nodeId: string): void {
    const node = this.nodePool.get(nodeId);
    if (!node) return;
    if (this.store.getSelectedNode()?.id !== node.id) {
      this.selectNode(node);
    }
    this.handleGlobalCreateClick();
  }

  /**
   * Persist a sibling reorder (List View drag-and-drop, edit mode).
   * Just the order changes — the set of parent/child links is unaffected,
   * so linkPool needs no update.
   */
  requestReorderSiblings(parentId: string, newChildOrder: string[]): void {
    const parent = this.nodePool.get(parentId);
    if (!parent) return;
    this.store.updateNodeChildren(parentId, newChildOrder);
    this.renderAll();
    this.restartSimulation(0.2);
  }

  /**
   * Persist a reparent (List View drag-and-drop, edit mode): removes
   * nodeId from every current parent's children (and linkPool) and adds
   * it to newParentId's children — at insertIndex if given (a drop
   * between two of the new parent's existing children), otherwise
   * appended last (a drop directly onto the new parent). Same
   * children-array + linkPool update Graph View's own add/delete-
   * parent-link flow uses, so both views stay consistent.
   *
   * No-ops (invalid drop) when: same node, cross-recipe, or newParentId
   * is a descendant of nodeId (would create a cycle).
   */
  requestReparentNode(nodeId: string, newParentId: string, insertIndex?: number): void {
    const node = this.nodePool.get(nodeId);
    const newParent = this.nodePool.get(newParentId);
    if (!node || !newParent) return;
    if (node.id === newParent.id) return;
    if (node.recipeId !== newParent.recipeId) return;
    if (this.store.isDescendantOf(newParentId, nodeId)) return;

    for (const oldParentId of this.store.getParentIds(nodeId)) {
      if (oldParentId === newParentId) continue;
      const oldParent = this.nodePool.get(oldParentId);
      if (!oldParent) continue;
      this.store.updateNodeChildren(oldParentId, oldParent.data.children.filter(cid => cid !== nodeId));
      this.store.removeLink(oldParentId, nodeId);
    }

    if (!newParent.data.children.includes(nodeId)) {
      const newChildren = [...newParent.data.children];
      const idx = insertIndex !== undefined
        ? Math.max(0, Math.min(insertIndex, newChildren.length))
        : newChildren.length;
      newChildren.splice(idx, 0, nodeId);
      this.store.updateNodeChildren(newParentId, newChildren);

      const newLink: Link<DisplayNode> = { source: newParent, target: node, shape: { x: 0, y: 0 }, active: true };
      this.store.addLinks([newLink]);
      assignLinkShape(newLink);
    }

    this.renderAll();
    this.restartSimulation(0.2);
  }

  /**
   * Persist dropping a node to leave it without any parent (List View
   * drag-and-drop, edit mode). It then satisfies the ordinary root-node
   * definition (no node lists it as a child) and appears as an extra
   * top-level entry for its recipe — no new state needed.
   */
  requestOrphanNode(nodeId: string): void {
    const node = this.nodePool.get(nodeId);
    if (!node) return;

    for (const oldParentId of this.store.getParentIds(nodeId)) {
      const oldParent = this.nodePool.get(oldParentId);
      if (!oldParent) continue;
      this.store.updateNodeChildren(oldParentId, oldParent.data.children.filter(cid => cid !== nodeId));
      this.store.removeLink(oldParentId, nodeId);
    }

    this.renderAll();
    this.restartSimulation(0.2);
  }

  /**
   * Transform a node: creates a new node as its parent, taking over every
   * parent link the node currently has (so the new node becomes the child
   * of the node's former parents, and the node itself keeps exactly one
   * parent — the new node). If the node was the main root, the new node
   * becomes the main root instead — same "promotion" rule
   * completeAddLink() uses. No cycle check is needed: the new node is
   * brand new, so it can't already appear anywhere in the graph.
   *
   * `seed`, when given, pre-fills the new node's ingredient/transformation
   * (and default image) instead of leaving it blank — see
   * requestTransformSelectedNodes()'s suggestion flow.
   */
  requestTransformNode(nodeId: string, seed?: TransformSeed): void {
    const node = this.nodePool.get(nodeId);
    if (!node || !this.store.getSelectedRecipeId()) return;

    // Capture the node's current parents *before* the new node exists —
    // otherwise, since the new node's own children include nodeId from
    // the moment it's created, it would match this same scan and be
    // treated as one of its own "old parents" to take over from itself.
    const oldParentIds = this.store.getParentIds(nodeId);

    const newNodeId = `node_${Date.now()}`;
    const jitterX = (Math.random() - 0.5) * 60;
    const jitterY = (Math.random() - 0.5) * 60;

    const newNode = this.store.createBlankNode(node.recipeId, (node.x ?? 0) + jitterX, (node.y ?? 0) - 90 + jitterY, newNodeId);
    newNode.data.children = [nodeId];
    applyTransformSeed(newNode, seed);
    this.store.addNodes([newNode]);

    // Take over every existing parent link.
    for (const oldParentId of oldParentIds) {
      const oldParent = this.nodePool.get(oldParentId);
      if (!oldParent) continue;
      this.store.updateNodeChildren(oldParentId, [
        ...oldParent.data.children.filter(cid => cid !== nodeId),
        newNodeId,
      ]);
      this.store.removeLink(oldParentId, nodeId);
      const parentLink: Link<DisplayNode> = { source: oldParent, target: newNode, shape: { x: 0, y: 0 }, active: true };
      this.store.addLinks([parentLink]);
      assignLinkShape(parentLink);
    }

    // New node → selected node link.
    const childLink: Link<DisplayNode> = { source: newNode, target: node, shape: { x: 0, y: 0 }, active: true };
    this.store.addLinks([childLink]);
    assignLinkShape(childLink);

    if (node.isMainRoot) {
      this.store.handleMakeMainRoot(newNodeId);
    }

    this.renderAll();
    this.restartSimulation(0.3);

    this.selectNode(newNode);
  }

  /**
   * Add a plain parent to the current selection (1+ nodes): creates one
   * new node as the parent of every selected node, without touching any
   * of their existing parent links — the no-rewiring counterpart to
   * transform. Selected nodes keep whatever parents they already had, in
   * addition to the new one. If one of them was the main root, the new
   * node is promoted to main root instead — same "promotion" rule
   * transform/completeAddLink use. No cycle check is needed: the new node
   * is brand new, so it can't already appear anywhere in the graph and
   * nothing existing is being reattached to it.
   */
  requestAddParentToSelectedNodes(): void {
    const nodes = this.store.getSelectedNodes();
    if (nodes.length === 0) return;
    const recipeId = nodes[0].recipeId;
    if (!nodes.every(n => n.recipeId === recipeId)) return;

    const avgX = nodes.reduce((sum, n) => sum + (n.x ?? 0), 0) / nodes.length;
    const avgY = nodes.reduce((sum, n) => sum + (n.y ?? 0), 0) / nodes.length;
    const jitterX = (Math.random() - 0.5) * 60;
    const jitterY = (Math.random() - 0.5) * 60;

    const newNode = this.store.createBlankNode(recipeId, avgX + jitterX, avgY - 90 + jitterY);
    const newNodeId = newNode.id;
    newNode.data.children = nodes.map(n => n.id);
    this.store.addNodes([newNode]);

    let anyMainRoot = false;
    for (const n of nodes) {
      if (n.isMainRoot) anyMainRoot = true;
      const childLink: Link<DisplayNode> = { source: newNode, target: n, shape: { x: 0, y: 0 }, active: true };
      this.store.addLinks([childLink]);
      assignLinkShape(childLink);
    }

    if (anyMainRoot) {
      this.store.handleMakeMainRoot(newNodeId);
    }

    this.renderAll();
    this.restartSimulation(0.3);

    this.selectNode(newNode);
  }

  /**
   * Transform the current selection: one node uses the single-parent
   * logic above; 2+ nodes create one new shared parent that takes over
   * every one of their combined parent links (see
   * requestTransformMultipleNodes). No-op with 0 selected.
   *
   * If any selected node has an ingredient, first offers "suggested
   * transformations" — the dual of "Afegeix elaboració": nodes elsewhere
   * whose children are comprised of the selected ingredient(s), i.e. what
   * they're commonly composed *into* (e.g. "ceba" -> "ceba tallada").
   * Dismissing that dialog creates nothing; picking "Cap (node buit)"
   * (always offered) is today's plain blank-node behavior. No selected
   * node has an ingredient -> skip straight to a blank node, same as
   * today, since there'd be nothing to suggest.
   */
  async requestTransformSelectedNodes(): Promise<void> {
    const nodes = this.store.getSelectedNodes();
    if (nodes.length === 0) return;

    const ingredientIds = Array.from(new Set(
      nodes
        .map(n => n.data.ingredientInfo?.ingredient?.id)
        .filter((id): id is string => !!id)
    ));

    let seed: TransformSeed | undefined;
    if (ingredientIds.length > 0) {
      try {
        const { getTransformationSuggestions } = await import('../api/index.js');
        const suggestions = await getTransformationSuggestions(ingredientIds);
        const { TransformationSuggestionSelector } =
          await import('../recipe_renderer/components/TransformationSuggestionSelector.js');
        const picked = await new TransformationSuggestionSelector(suggestions).open();
        if (picked === null) return; // dismissed — create nothing
        if (picked !== 'blank') {
          seed = {
            ingredient: picked.ingredient,
            transformation: picked.transformation,
            imageUrl: picked.ingredient ? getDefaultImageUrl(picked.ingredient.images) : undefined,
          };
        }
      } catch (error) {
        console.error('Failed to fetch transformation suggestions:', error);
        showToast('Error cercant transformacions suggerides', 'error');
        return;
      }
    }

    if (nodes.length === 1) {
      this.requestTransformNode(nodes[0].id, seed);
    } else {
      this.requestTransformMultipleNodes(nodes.map(n => n.id), seed);
    }
  }

  /**
   * Transform 2+ selected nodes: creates one new node as the parent of
   * all of them. Every parent link any of them currently has is taken
   * over by the new node (a parent shared by two selected nodes ends up
   * pointing at the new node once, not twice — old-parent ids are
   * de-duplicated before adding links) — *except* a link whose transfer
   * would create a loop, which is left untouched instead (see
   * findLoopingOldParentIds). If one of them was the main root, the new
   * node is promoted to main root. Requires all selected nodes to be in
   * the same recipe (should always be true — multi-select itself is
   * restricted to one recipe at a time).
   *
   * `seed` — see requestTransformNode().
   */
  private requestTransformMultipleNodes(nodeIds: string[], seed?: TransformSeed): void {
    const nodes = nodeIds.map(id => this.nodePool.get(id)).filter((n): n is DisplayNode => !!n);
    if (nodes.length < 2) return;
    const recipeId = nodes[0].recipeId;
    if (!nodes.every(n => n.recipeId === recipeId)) return;

    // Capture every node's current parents *before* the new node exists
    // (see requestTransformNode for why this ordering matters).
    const oldParentIdsByNode = new Map<string, string[]>();
    for (const n of nodes) {
      oldParentIdsByNode.set(n.id, this.store.getParentIds(n.id));
    }

    // Which of those old-parent ids would form a loop if taken over by the
    // new node — computed against the pre-transform graph, before any of
    // this function's edits, so later skip/transfer decisions can't affect
    // earlier ones (see findLoopingOldParentIds's own doc for the exact rule).
    const allCandidateOldParentIds = new Set<string>();
    for (const ids of oldParentIdsByNode.values()) {
      for (const id of ids) allCandidateOldParentIds.add(id);
    }
    const loopingOldParentIds = this.findLoopingOldParentIds(allCandidateOldParentIds, nodeIds);

    const avgX = nodes.reduce((sum, n) => sum + (n.x ?? 0), 0) / nodes.length;
    const avgY = nodes.reduce((sum, n) => sum + (n.y ?? 0), 0) / nodes.length;
    const jitterX = (Math.random() - 0.5) * 60;
    const jitterY = (Math.random() - 0.5) * 60;

    const newNode = this.store.createBlankNode(recipeId, avgX + jitterX, avgY - 90 + jitterY);
    const newNodeId = newNode.id;
    newNode.data.children = [...nodeIds];
    applyTransformSeed(newNode, seed);
    this.store.addNodes([newNode]);

    // Take over every existing parent link, one node at a time — a
    // parent shared by two+ selected nodes is only visited once per
    // node it's actually a parent of, but children updates accumulate
    // correctly since each lookup below re-reads the pool's current state.
    // Old parents flagged as looping are skipped entirely: their link to
    // the selected node is left exactly as it was.
    let anyMainRoot = false;
    const transferredOldParentIds = new Set<string>();
    for (const n of nodes) {
      if (n.isMainRoot) anyMainRoot = true;
      for (const oldParentId of oldParentIdsByNode.get(n.id) ?? []) {
        if (loopingOldParentIds.has(oldParentId)) continue;
        transferredOldParentIds.add(oldParentId);
        const oldParent = this.nodePool.get(oldParentId);
        if (!oldParent) continue;
        const filtered = oldParent.data.children.filter(cid => cid !== n.id);
        this.store.updateNodeChildren(oldParentId, filtered.includes(newNodeId) ? filtered : [...filtered, newNodeId]);
        this.store.removeLink(oldParentId, n.id);
      }
    }
    for (const oldParentId of transferredOldParentIds) {
      const oldParent = this.nodePool.get(oldParentId);
      if (!oldParent) continue;
      const parentLink: Link<DisplayNode> = { source: oldParent, target: newNode, shape: { x: 0, y: 0 }, active: true };
      this.store.addLinks([parentLink]); // addLinks already skips duplicates
      assignLinkShape(parentLink);
    }

    // New node → each selected node.
    for (const n of nodes) {
      const childLink: Link<DisplayNode> = { source: newNode, target: n, shape: { x: 0, y: 0 }, active: true };
      this.store.addLinks([childLink]);
      assignLinkShape(childLink);
    }

    if (anyMainRoot) {
      this.store.handleMakeMainRoot(newNodeId);
    }

    this.renderAll();
    this.restartSimulation(0.3);

    this.selectNode(newNode);
  }

  /**
   * Among a set of candidate old-parent ids (nodes that are currently a
   * direct parent of at least one node being transformed), find the ones
   * that must NOT be taken over by the new shared parent because doing so
   * would create a cycle back to it.
   *
   * The new node becomes a parent of every selected node, so after the
   * transform its descendants are: the selected nodes themselves, plus
   * whatever they were already ancestors of. Taking over an old-parent
   * link from a candidate `p` closes a loop back to the new node exactly
   * when `p` is already one of those descendants-to-be — i.e. when `p` is
   * itself one of the selected nodes (a direct parent–child pair within
   * the selection), or `p` is a descendant, in the graph as it stood
   * before this transform, of any selected node (an indirect ancestor/
   * descendant pair within the selection, connected through nodes that
   * aren't themselves selected).
   *
   * Must be called before any of the transform's own edits — it relies on
   * the pre-transform graph shape.
   */
  private findLoopingOldParentIds(candidateIds: Set<string>, selectedNodeIds: string[]): Set<string> {
    const looping = new Set<string>();
    const selectedSet = new Set(selectedNodeIds);
    for (const candidateId of candidateIds) {
      if (selectedSet.has(candidateId) || selectedNodeIds.some(sid => this.store.isDescendantOf(candidateId, sid))) {
        looping.add(candidateId);
      }
    }
    return looping;
  }

  /**
   * Delete whatever's currently selected, after the toolbar's own
   * arm/confirm click. With exactly one node selected, this is the same
   * as the per-node × button (requestDeleteNode/handleDeleteNode) —
   * including its own extra confirmation dialog when that one node is a
   * main root (full recipe deletion). With 2+ selected, each node is
   * just unlinked and removed, same as removeSingleNode does for one —
   * never cascades to descendants (they become parentless roots instead)
   * and never routes a selected main root into full recipe deletion,
   * even if one happens to be part of the selection.
   */
  requestDeleteSelectedNodes(): void {
    const nodes = this.store.getSelectedNodes();
    if (nodes.length === 1) {
      this.requestDeleteNode(nodes[0].id);
    } else if (nodes.length > 1) {
      for (const node of nodes) {
        this.removeSingleNode(node);
      }
    }
  }

  /**
   * Remove a single non-root node and all its links.
   */
  private removeSingleNode(node: DisplayNode): void {
    // Remove from all parents' children arrays
    for (const parentId of this.store.getParentIds(node.id)) {
      const parent = this.nodePool.get(parentId);
      if (!parent) continue;
      this.store.updateNodeChildren(parentId, parent.data.children.filter(cid => cid !== node.id));
    }

    // Remove all links to/from this node
    this.store.setLinkPool(this.linkPool.filter(
      l => l.source.id !== node.id && l.target.id !== node.id
    ));

    // Remove from pool
    this.store.removeNode(node.id);

    // If this node was selected, deselect
    if (this.store.getSelectedNode()?.id === node.id) {
      this.store.deselect();
    }

    // Re-render
    this.renderAll();
    this.restartSimulation(0.2);

    // Sync main root fold state — if this was the last visible node, fold the recipe
    this.store.syncMainRootFoldState(node.recipeId);
  }

  /**
   * Delete the currently selected recipe — the EditToolbar's [×] button
   * next to [+], shown only in "global" edition mode (a recipe selected
   * but not focused, so none of the node-level edit actions apply to it).
   * Single click: reuses handleDeleteRecipe()'s own confirmation dialog
   * rather than an arm/confirm two-step, since that dialog already is the
   * confirmation.
   */
  private requestDeleteSelectedRecipe(): void {
    const recipeId = this.store.getSelectedRecipeId();
    if (!recipeId) return;
    const entry = this.store.getRecipeEntry(recipeId);
    if (!entry) return;
    const mainRootId = entry.rootNodeIds[entry.mainRootIndex];
    const mainRoot = this.nodePool.get(mainRootId);
    if (!mainRoot) return;
    this.handleDeleteRecipe(mainRoot);
  }

  /**
   * Handle deleting a recipe (root node × click).
   * Shows confirmation dialog, then deletes via API.
   */
  private handleDeleteRecipe(rootNode: DisplayNode): void {
    const recipeTitle = rootNode.name ?? 'aquesta recepta';

    // Show confirmation dialog (same style as unsaved-changes dialog)
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.6);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

    const dialog = document.createElement('div');
    dialog.style.cssText = `
      background: #1e1e2e;
      border: 1px solid var(--accent-alpha-30);
      border-radius: 12px;
      padding: 24px;
      max-width: 400px;
      width: 90%;
      font-family: 'Courier New', Consolas, 'Liberation Mono', monospace;
    `;

    dialog.innerHTML = `
      <h3 style="color:#e0e0e0; font-size:16px; margin-bottom:12px; font-family:inherit;">Eliminar recepta</h3>
      <p style="color:#a0a0a0; font-size:13px; margin-bottom:20px; line-height:1.5; font-family:inherit;">
        Esteu segur que voleu eliminar "${escapeHtml(recipeTitle)}"? Aquesta acció no es pot desfer.
      </p>
      <div style="display:flex; gap:8px; justify-content:flex-end;">
        <button class="dialog-btn-cancel" style="
          padding:8px 16px; border-radius:6px; border:1px solid #555;
          background:transparent; color:#a0a0a0; cursor:pointer; font-family:inherit; font-size:13px;
        ">Cancel·la</button>
        <button class="dialog-btn-confirm" style="
          padding:8px 16px; border-radius:6px; border:none;
          background:#ef4444; color:white; cursor:pointer; font-family:inherit; font-size:13px; font-weight:bold;
        ">Elimina</button>
      </div>
    `;

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // On mobile, make the dialog full-screen (see §8.9)
    if (this._isMobile) {
      dialog.style.cssText += `
        max-width: 100vw;
        width: 100vw;
        height: 100vh;
        border-radius: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
      `;
      const btnContainer = dialog.querySelector('div:last-child') as HTMLElement;
      if (btnContainer) {
        btnContainer.style.flexDirection = 'column';
        (btnContainer.querySelectorAll('button') as NodeListOf<HTMLElement>).forEach(b => {
          b.style.width = '100%';
          b.style.textAlign = 'center';
        });
      }
    }

    dialog.querySelector('.dialog-btn-cancel')?.addEventListener('click', () => {
      overlay.remove();
    });

    dialog.querySelector('.dialog-btn-confirm')?.addEventListener('click', async () => {
      overlay.remove();
      await this.executeDeleteRecipe(rootNode);
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.remove();
    });
  }

  /**
   * Execute recipe deletion: call API and clean up pool.
   */
  private async executeDeleteRecipe(rootNode: DisplayNode): Promise<void> {
    const recipeId = rootNode.recipeId;

    try {
      await this.store.deleteRecipe(recipeId);

      // Check if we're on the recipe page (the recipe was loaded directly)
      const isRecipePage = !!this.store.getRecipeEntry(recipeId)?.fullyLoaded;

      // Clear recipe from pool
      this.clearRecipe(recipeId);

      // If on recipe page, navigate to home
      if (isRecipePage && window.location.pathname.startsWith('/recipe/')) {
        history.pushState({}, '', '/');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }

      showToast('Recepta eliminada', 'success');
    } catch (error) {
      console.error('Failed to delete recipe:', error);
      const msg = error instanceof Error ? error.message : 'Error de connexió';
      showToast(`Error en eliminar: ${msg}`, 'error');
    }
  }

  /**
   * Get the center of the visible viewport in SVG coordinates.
   * Accounts for zoom/pan transforms.
   */
  private getVisibleViewportCenter(): { x: number; y: number } {
    if (!this.svg) return { x: this.width / 2, y: this.height / 2 };

    const svgEl = this.svg.node() as SVGSVGElement | null;
    const target = this.mainGroup?.node();
    if (!svgEl || !target) return { x: this.width / 2, y: this.height / 2 };

    const rect = svgEl.getBoundingClientRect();

    // Convert the viewport center from screen coords to mainGroup local coords
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const [cx, cy] = d3.pointer({ clientX: centerX, clientY: centerY } as MouseEvent, target);

    return { x: cx, y: cy };
  }

  // ============================================================================
  // RENDERING
  // ============================================================================

  /**
   * Render everything: links, nodes, clip paths, edit buttons.
   */
  private renderAll(): void {
    this.renderLinks();
    this.updateClipPaths();
    this.renderNodes();

    // Render edit buttons when in edit mode.
    // Skip while a link/delete-link/transform sub-mode flow is in progress:
    // EditButtonRenderer.renderAll() always removes and recreates every
    // .edit-button-group from scratch (see its own comment), which destroys
    // the exact DOM node a touch gesture may currently be mid-drag on. Since
    // starting one of these flows (e.g. startAddLinkFlow → setNodesInactiveWhere)
    // triggers this very renderAll() synchronously — from inside the (+)/(−)
    // button's own touchstart handler — rebuilding here would detach the touch's
    // original target element while the browser is still tracking it, which
    // silently stops delivery of the touchmove/touchend that complete the drag
    // (a mouse drag isn't affected: mousemove/mouseup aren't tied to a specific
    // DOM node the way an in-flight touch is). None of these flows need buttons
    // rebuilt anyway — only __inactive dimming changes during them, which
    // renderNodes() already reflects via its normal (non-destructive) data join.
    if (this.store.isEditMode() && this.editButtonRenderer && this.store.getEditionSubMode() === StoreEditionMode.NONE) {
      this.editButtonRenderer.renderAll(this.nodePool);
    }

    // After re-render, show caret only on the node currently shown in the sidebar
    if (this.store.getSelectedNode()) {
      const selNode = this.store.getSelectedNode()!;
      this.showCaretForNode(selNode);
    }
  }

  /**
   * Render/update link elements in the SVG.
   */
  private renderLinks(): void {
    if (!this.linksGroup) return;

    const totalLinks = this.linkPool.length;
    const linksMissingSource = this.linkPool.filter(l => !this.nodePool.get(l.source.id)).length;
    const linksMissingTarget = this.linkPool.filter(l => !this.nodePool.get(l.target.id)).length;
    const linksSourceHidden = this.linkPool.filter(l => {
      const s = this.nodePool.get(l.source.id);
      return s && this.store.isNodeHidden(s.id);
    }).length;
    const linksTargetHidden = this.linkPool.filter(l => {
      const t = this.nodePool.get(l.target.id);
      return t && this.store.isNodeHidden(t.id);
    }).length;

    const visibleLinks = this.linkPool.filter(link => {
      const source = this.nodePool.get(link.source.id);
      const target = this.nodePool.get(link.target.id);
      if (!source || !target) return false;
      // Hide links to/from hidden nodes
      return !this.store.isNodeHidden(source.id) && !this.store.isNodeHidden(target.id);
    });


    const linkSelection = this.linksGroup.selectAll<SVGLineElement, Link<DisplayNode>>('line')
      .data(visibleLinks, (d: Link<DisplayNode>) => `${d.source.id}→${d.target.id}`);

    // EXIT
    linkSelection.exit().remove();

    // ENTER
    const enter = linkSelection.enter().append('line');

    // MERGE
    const merged = enter.merge(linkSelection);

    merged
      .attr('x1', (d: Link<DisplayNode>) => d.source.x ?? 0)
      .attr('y1', (d: Link<DisplayNode>) => d.source.y ?? 0)
      .attr('x2', (d: Link<DisplayNode>) => d.target.x ?? 0)
      .attr('y2', (d: Link<DisplayNode>) => d.target.y ?? 0)
      .style('stroke', '#4a5a4a')       // Solid green-gray for links
      .attr('stroke-width', 3)
      .attr('fill', 'none');
  }

  /**
   * Update clip paths in defs for all visible nodes.
   */
  private updateClipPaths(): void {
    if (!this.defsEl) return;

    const visibleNodes = Array.from(this.nodePool.values())
      .filter(n => !this.store.isNodeHidden(n.id));

    for (const node of visibleNodes) {
      // Use unescaped ID for the SVG element attribute (XML/SVG IDs are literal)
      const clipIdRaw = `viz-clip-${node.id}`;
      // Use CSS.escape() for the CSS selector to check existence
      const clipIdEscaped = `viz-clip-${CSS.escape(node.id)}`;
      if (this.defsEl.select(`#${clipIdEscaped}`).empty()) {
        const clipPath = this.defsEl.append('clipPath')
          .attr('id', clipIdRaw);
        clipPath.append('circle')
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('r', NODE_RADIUS);
      }
    }
  }

  /**
   * Render/update node elements in the SVG.
   */
  private renderNodes(): void {
    if (!this.nodesGroup) return;

    const visibleNodes = Array.from(this.nodePool.values())
      .filter(n => !this.store.isNodeHidden(n.id));

    const nodeSelection = this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node')
      .data(visibleNodes, (d: DisplayNode) => d.id);

    // EXIT - remove clip paths and elements
    const exitingData = nodeSelection.exit().data() as DisplayNode[];
    for (const d of exitingData) {
      if (this.defsEl) {
        this.defsEl.select(`#viz-clip-${CSS.escape(d.id)}`).remove();
      }
    }
    nodeSelection.exit().remove();

    // ENTER
    const enter = nodeSelection.enter()
      .append('g')
      .attr('class', 'viz-node')
      .attr('id', (d: DisplayNode) => `viz-node-${d.id}`)
      .attr('cursor', 'pointer');

    enter.append('polygon').attr('class', 'viz-node-mainroot-ring');
    enter.append('circle').attr('class', 'viz-node-bg');
    enter.append('image').attr('class', 'viz-node-image');
    // Fallback icon for a node with no image of its own — same app-logo
    // icon on the same dark-blue background used everywhere else this
    // fallback appears (e.g. the list view's own rows), in place of the
    // plain name text this used to show.
    enter.append('image').attr('class', 'viz-node-fallback-icon');
    enter.append('text').attr('class', 'viz-node-title');

    // Fold/unfold caret (hidden by default, shown on hover)
    const caretG = enter.append('g')
      .attr('class', 'viz-node-caret')
      .style('display', 'none')
      .style('cursor', 'pointer');
    caretG.append('circle')
      .attr('class', 'viz-node-caret-bg')
      .attr('r', 13)
      .attr('fill', '#0d1117')
      .attr('stroke', '#4a5a4a')
      .attr('stroke-width', 1.5);
    caretG.append('path')
      .attr('class', 'viz-node-caret-chevron')
      .attr('d', 'M-3,-4 L3,0 L-3,4')
      .attr('fill', 'none')
      .attr('stroke', 'var(--accent)')
      .attr('stroke-width', 2)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round');

    // Main roots show this instead of the chevron (see the per-render
    // toggle below) — folding/unfocusing a main root is a bigger operation
    // (it focuses/unfocuses the whole recipe) than a plain fold, so it gets
    // the same ⌖ glyph as the header's focus button instead of looking like
    // an ordinary caret.
    caretG.append('text')
      .attr('class', 'viz-node-caret-focus-icon')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', 18)
      .attr('font-weight', 'bold')
      .attr('pointer-events', 'none')
      .style('display', 'none')
      .text('⌖');

    // On mobile, add touch hit area to the caret (44×44px, see §8.3.4)
    if (this._isMobile) {
      caretG.insert('rect', 'circle')
        .attr('class', 'viz-node-caret-toucharea')
        .attr('x', -22)
        .attr('y', -22)
        .attr('width', 44)
        .attr('height', 44)
        .attr('fill', 'transparent')
        .attr('pointer-events', 'all')
        .attr('touch-action', 'manipulation');
    }

    // Close (×) button for main root nodes — removes recipe from view.
    // Always visible on main roots in normal mode, hidden in edit mode.
    const closeBtnG = enter.append('g')
      .attr('class', 'viz-node-close-btn')
      .style('display', 'none')
      .style('cursor', 'pointer');
    closeBtnG.append('circle')
      .attr('r', 13)
      .attr('fill', '#0d1117')
      .attr('stroke', '#4a5a4a')
      .attr('stroke-width', 1.5);
    closeBtnG.append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('dy', 1)
      .attr('fill', 'var(--accent)')
      .attr('font-size', '17px')
      .attr('font-weight', 'bold')
      .attr('pointer-events', 'none')
      .text('×');

    // On mobile, add touch hit area to the close button (44×44px)
    if (this._isMobile) {
      closeBtnG.insert('rect', 'circle')
        .attr('class', 'viz-node-closebtn-toucharea')
        .attr('x', -22)
        .attr('y', -22)
        .attr('width', 44)
        .attr('height', 44)
        .attr('fill', 'transparent')
        .attr('pointer-events', 'all')
        .attr('touch-action', 'manipulation');
    }

    // Close button click handler
    closeBtnG.on('click', (event: MouseEvent, d: DisplayNode) => {
      event.stopPropagation();
      this.hideRecipe(d.recipeId);
    });

    // Drag behavior — moves the dragged node and its descendants together.
    //
    // Uses getDescendantsToDrag which traverses the tree with a per-branch
    // hiddenOnly flag: when a folded node is encountered its descendants are
    // only included if they are __hidden, and visible nodes that are only
    // reachable through folded ancestors are skipped (e.g. node 4 in
    // 1→1.5→2→3→4 when 2 is folded and 4 has another visible path).
    //
    // Hidden descendants (not in the simulation) have their x/y updated
    // directly so they stay in the right position when revealed later.
    let dragDescendants: DisplayNode[] = [];
    // Track whether the pointer actually moved — d3-drag's 'drag' event only
    // fires on real movement past its threshold, so seeing it at all means
    // the node's position changed and this should count as an edit (§5.7).
    let dragMoved = false;

    const dragHandler = d3.drag<SVGGElement, DisplayNode>()
      .filter((event: MouseEvent | TouchEvent) => {
        // Replicate d3-drag's own default filter (ignore ctrl-click / non-primary
        // mouse button) — .filter() replaces the default entirely, so we still
        // need it here. TouchEvent has no .button, so this is a no-op for touch.
        if ('button' in event && (event.ctrlKey || event.button)) return false;

        // Never start a node drag when the gesture begins on an edit button
        // (+/−/×/transform/etc.) — those buttons run their own mousedown/
        // touchstart handling (link-flow, arm-delete, ...) and must not also
        // drag the node underneath them. Relying solely on each button's own
        // stopPropagation() is fragile on touch — it only runs on mobile-gated
        // devices (see EditButtonRenderer's isMobile check) and, even then, only
        // stops the *touchstart* from bubbling, not the touchmove/touchend that
        // follow — so enforce it here too via d3-drag's own filter hook, which
        // stops the drag from ever starting regardless of device detection.
        const target = event.target as Element | null;
        return !target?.closest('.edit-btn, .edit-button-group');
      })
      .on('start', (event: any, d: DisplayNode) => {
        if (!event.active) {
          this.simulation?.alphaTarget(0.3).restart();
        }
        d.fx = d.x ?? 0;
        d.fy = d.y ?? 0;
        dragMoved = false;

        // Collect descendants using the hidden-only traversal
        dragDescendants = this.getDescendantsToDrag(d);
        for (const nd of dragDescendants) {
          nd.fx = nd.x ?? 0;
          nd.fy = nd.y ?? 0;
        }
      })
      .on('drag', (event: any, d: DisplayNode) => {
        // Compute the delta from the previous fixed position
        const dx = event.x - d.fx!;
        const dy = event.y - d.fy!;
        d.fx = event.x;
        d.fy = event.y;
        dragMoved = true;

        // Move all pinned descendants by the same delta
        for (const nd of dragDescendants) {
          nd.fx! += dx;
          nd.fy! += dy;
          // Hidden nodes are not in the simulation, so the tick won't copy
          // fx→x for them. Update x/y directly so their position persists
          // when they become visible again upon expansion.
          if (this.store.isNodeHidden(nd.id)) {
            nd.x = nd.fx!;
            nd.y = nd.fy!;
          }
        }
      })
      .on('end', (event: any, d: DisplayNode) => {

        if (!event.active) {
          this.simulation?.alphaTarget(0);
        }
        d.fx = null;
        d.fy = null;

        // Release all descendants
        for (const nd of dragDescendants) {
          nd.fx = null;
          nd.fy = null;
        }
        dragDescendants = [];

        // Commit the position change so Save/Discard appear (§5.7) — a
        // drag that actually moved the node persists via
        // displayData.position. RecipeStore.setNodePosition() is the one
        // hook point for this, since position is otherwise mutated
        // directly on the DisplayNode object every simulation tick, never
        // through a store method — it excludes the main root itself
        // (never a savable change) and is a no-op outside edit mode.
        if (dragMoved) {
          this.store.setNodePosition(d.id, d.x ?? 0, d.y ?? 0);
        }
        dragMoved = false;
      });
    enter.call(dragHandler as any);

    // Caret click — toggle expand/collapse without selecting the node.
    // Fold state change tracking is handled inside
    // RecipeStore.collapseNode()/expandNode() themselves.
    caretG.on('click', async (event: MouseEvent, d: DisplayNode) => {
      event.stopPropagation();

      // Click on caret (not × button) — disarm any pending delete arm
      this.editButtonRenderer?.disarmDelete();

      await this.toggleExpandNode(d.id);
    });

    // Hover interactions — the caret always follows the sidebar:
    // - When hovering a non-selected node, sidebar shows that node → show its caret, hide selected's
    // - When unhovering, sidebar restores selected node → hide unhovered's caret, show selected's — use shared renderNodeInSidePanel for consistent view
    enter.on('mouseenter', (_event: MouseEvent, d: DisplayNode) => {
      if (!this.store.isNodeSelected(d.id)) {
        this.store.setHoveredNode(d.id);
        this._hoveredNode = d;
        this.renderNodeHighlights();
        // During pick-node mode, keep the side panel fixed on the source node
        // whose parameter is being edited — don't show the hovered node there.
        if (this.store.getEditionSubMode() !== StoreEditionMode.PICK_NODE) {
          if (this.store.getSelectedNode()) {
            this.hideCaretForNode(this.store.getSelectedNode()!);
          }
          this.showCaretForNode(d);
          if (this.nodeVisualizer) {
            this.renderNodeInSidePanel(d);
          }
        }
      }
    })
    .on('mouseleave', (_event: MouseEvent, d: DisplayNode) => {
      if (!this.store.isNodeSelected(d.id)) {
        this.store.setHoveredNode(null);
        this._hoveredNode = null;
        this.renderNodeHighlights();
        this.hideCaretForNode(d);
        // During pick-node mode, the side panel was never updated on
        // mouseenter (it stayed on the source node), so there's nothing
        // to restore here — skip to avoid destroying the pick button.
        if (this.store.getEditionSubMode() !== StoreEditionMode.PICK_NODE) {
          if (this.store.getSelectedNode()) {
            this.showCaretForNode(this.store.getSelectedNode()!);
          }
          if (this.nodeVisualizer) {
            if (this.store.getSelectedNode()) {
              this.renderNodeInSidePanel(this.store.getSelectedNode()!);
            } else {
              this.nodeVisualizer.clear();
            }
          }
        }
      }
    })
    // Click to select (or handle link-flow actions / pick-node in sub-modes)
    .on('click', (event: MouseEvent, d: DisplayNode) => {
      // Pick node mode: clicking a node copies its ID to the param
      if (this.store.getEditionSubMode() === StoreEditionMode.PICK_NODE) {
        event.stopPropagation();
        this.handleNodePick(d);
        return;
      }

      // In other sub-modes, don't stop propagation — the SVG-level
      // handlers need the event to bubble up to complete/cancel.
      if (this.store.getEditionSubMode() !== StoreEditionMode.NONE) {
        return;
      }

      event.stopPropagation();

      // Click on a node body (not a button) — disarm any pending delete arm
      this.editButtonRenderer?.disarmDelete();

      // Multi-select mode: clicking toggles the node in/out of the
      // selection instead of replacing it.
      if (this.store.isMultiSelectMode()) {
        this.store.toggleMultiSelectNode(d.id);
        return;
      }

      // If already selected, deselect; otherwise select the node.
      // Folding/unfolding is only done via the caret (>) button.
      if (this.store.getSelectedNode()?.id === d.id) {
        this.store.deselect();
      } else {
        this.selectNode(d);
      }
    });

    // MERGE (enter + update)
    const merged = enter.merge(nodeSelection);

    // Position
    merged.attr('transform', (d: DisplayNode) => {
      const x = d.x != null && isFinite(d.x) ? d.x : 0;
      const y = d.y != null && isFinite(d.y) ? d.y : 0;
      return `translate(${x},${y})`;
    });

    // Main root decorative ring (13-sided polygon) — green for selected recipe, gray-green for others
    merged.select('.viz-node-mainroot-ring')
      .attr('points', generateMainRootPolygon(NODE_RADIUS + 4))
      .attr('fill', (d: DisplayNode) => {
        if (!d.isMainRoot) return 'none';
        if (this.store.isNodeHovered(d.id)) return '#55dd88';
        return d.recipeId === this.store.getSelectedRecipeId() ? '#22c55e' : '#3a4a3a';
      })
      .attr('stroke', (d: DisplayNode) => {
        if (!d.isMainRoot) return 'none';
        if (this.store.isNodeHovered(d.id)) return '#55dd88';
        return d.recipeId === this.store.getSelectedRecipeId() ? '#22c55e' : '#3a4a3a';
      })
      .attr('stroke-width', (d: DisplayNode) => d.isMainRoot ? 2.5 : 0)
      .attr('opacity', (d: DisplayNode) => d.isMainRoot ? 1 : 0)
      .style('display', (d: DisplayNode) => d.isMainRoot ? 'inline' : 'none');

    // Circle backgrounds
    merged.select('.viz-node-bg')
      .attr('r', NODE_RADIUS)
      .attr('fill', (d: DisplayNode) => this.getNodeColor(d))
      .attr('stroke', (d: DisplayNode) => this.getNodeStroke(d))
      .attr('stroke-width', (d: DisplayNode) => this.getNodeStrokeWidth(d));

    // Images
    merged.select('.viz-node-image')
      .attr('x', -NODE_RADIUS)
      .attr('y', -NODE_RADIUS)
      .attr('width', NODE_RADIUS * 2)
      .attr('height', NODE_RADIUS * 2)
      .attr('preserveAspectRatio', 'xMidYMid slice')
      .attr('clip-path', (d: DisplayNode) => `url(#viz-clip-${d.id})`)
      .attr('href', (d: DisplayNode) => this.getNodeImageUrl(d) ?? '')
      .style('display', (d: DisplayNode) => this.getNodeImageUrl(d) ? 'inline' : 'none')
      .style('opacity', '1');

    // Fallback icon (shown only when no image) — replaces the plain name
    // text that used to fill this spot, same app-logo-on-dark-blue
    // treatment the list view's own rows use for the same case.
    const FALLBACK_ICON_SIZE = NODE_RADIUS * 0.8;
    merged.select('.viz-node-fallback-icon')
      .attr('x', -FALLBACK_ICON_SIZE / 2)
      .attr('y', -FALLBACK_ICON_SIZE / 2)
      .attr('width', FALLBACK_ICON_SIZE)
      .attr('height', FALLBACK_ICON_SIZE)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('href', '/static/img/coberts.png')
      .attr('pointer-events', 'none')
      .style('display', (d: DisplayNode) => this.getNodeImageUrl(d) ? 'none' : 'inline');

    // Title text — no longer shown; the fallback icon above now fills the
    // no-image case it used to cover.
    merged.select('.viz-node-title')
      .attr('x', 0)
      .attr('y', 0)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('fill', (d: DisplayNode) => d.__inactive ? '#555' : '#e0e0e0')
      .attr('font-family', MONO_FONT)
      .attr('font-weight', 'bold')
      .attr('font-size', 11)
      .attr('pointer-events', 'none')
      .text((d: DisplayNode) => this.getNodeLabel(d))
      .style('display', 'none');

    // Caret: position, visibility, and rotation
    // Reset display to hidden on every render — renderAll() will show
    // it for the selected node, and hover handlers toggle per-node.
    merged.select('.viz-node-caret')
      .style('display', 'none')
      .attr('transform', `translate(${NODE_RADIUS - 4}, ${NODE_RADIUS - 4})`);

    // Main roots show the ⌖ focus icon instead of the plain chevron —
    // highlighted (filled background, matching the header's focus button)
    // when the recipe is currently focused. Non-root nodes keep the
    // ordinary chevron, rotated ▼ (fold) when children are visible, ▶
    // (expand) otherwise.
    merged.select('.viz-node-caret-chevron')
      .style('display', (d: DisplayNode) => d.isMainRoot ? 'none' : null)
      .attr('transform', (d: DisplayNode) => this.hasVisibleChildren(d) ? 'rotate(90)' : 'rotate(0)');
    merged.select('.viz-node-caret-focus-icon')
      .style('display', (d: DisplayNode) => d.isMainRoot ? null : 'none')
      .attr('fill', (d: DisplayNode) => this.store.isNodeExpanded(d.id) ? '#0d1117' : 'var(--accent)');
    merged.select('.viz-node-caret-bg')
      .attr('fill', (d: DisplayNode) => (d.isMainRoot && this.store.isNodeExpanded(d.id)) ? 'var(--accent)' : '#0d1117');

    // Close (×) button: shown on hover/selection for main root nodes in normal mode
    merged.select('.viz-node-close-btn')
      .style('display', null)
      .attr('transform', `translate(${NODE_RADIUS - 4}, ${-(NODE_RADIUS - 4)})`)
      .style('opacity', (d: DisplayNode) => {
        if (!d.isMainRoot || this.store.isEditMode()) return 0;
        return (this.store.isNodeSelected(d.id) || this.store.isNodeHovered(d.id)) ? 1 : 0;
      })
      .style('pointer-events', (d: DisplayNode) => {
        if (!d.isMainRoot || this.store.isEditMode()) return 'none';
        return (this.store.isNodeSelected(d.id) || this.store.isNodeHovered(d.id)) ? 'auto' : 'none';
      });
  }

  /**
   * Show the fold/unfold caret for a node (if expandable).
   */
  private showCaretForNode(d: DisplayNode): void {
    // Main roots always get a caret, even with no other nodes in the
    // recipe — clicking it focuses/unfocuses the recipe (hides/shows every
    // other recipe), which is meaningful regardless of whether this one
    // has children to fold. Non-root nodes only get one if they actually
    // have children to fold.
    const canExpand = d.isMainRoot
      ? true
      : (d.data.children && d.data.children.length > 0);
    if (!canExpand) return;
    const sel = `#viz-node-${CSS.escape(d.id)} .viz-node-caret`;
    const caretEl = this.nodesGroup?.select<SVGGElement>(sel);
    if (caretEl && !caretEl.empty()) {
      caretEl.style('display', null);
      if (d.isMainRoot) {
        // Refresh the focus icon's highlighted state in case focus changed
        // since the last full render (see renderNodes()'s per-render toggle).
        const isFocused = this.store.isNodeExpanded(d.id);
        caretEl.select('.viz-node-caret-bg').attr('fill', isFocused ? 'var(--accent)' : '#0d1117');
        caretEl.select('.viz-node-caret-focus-icon').attr('fill', isFocused ? '#0d1117' : 'var(--accent)');
      } else {
        // Update chevron direction to match current fold state
        const chevron = caretEl.select('.viz-node-caret-chevron');
        if (!chevron.empty()) {
          chevron.attr('transform', this.hasVisibleChildren(d) ? 'rotate(90)' : 'rotate(0)');
        }
      }
    }
  }

  private hasVisibleChildren(node: DisplayNode): boolean {
    // For the main root, "has visible children" means any non-root node
    // is visible (which is the opposite of __folded).
    if (node.isMainRoot) {
      return !!this.store.isNodeExpanded(node.id);
    }
    const children = node.data.children || [];
    return children.length > 0 && children.some(childId => {
      const child = this.nodePool.get(childId);
      return child && !this.store.isNodeHidden(child.id);
    });
  }

  /**
   * Hide the fold/unfold caret for a node.
   */
  private hideCaretForNode(d: DisplayNode): void {
    if (!d) return;
    const sel = `#viz-node-${CSS.escape(d.id)} .viz-node-caret`;
    const caretEl = this.nodesGroup?.select<SVGGElement>(sel);
    if (caretEl && !caretEl.empty()) {
      caretEl.style('display', 'none');
    }
  }

  /**
   * Update node highlights (color/stroke) without full re-render.
   * Also updates edit button visibility on hover/selection change.
   */
  private renderNodeHighlights(): void {
    if (!this.nodesGroup) return;

    this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node')
      .select('.viz-node-bg')
      .attr('fill', (d: DisplayNode) => this.getNodeColor(d))
      .attr('stroke', (d: DisplayNode) => this.getNodeStroke(d))
      .attr('stroke-width', (d: DisplayNode) => this.getNodeStrokeWidth(d));

    // Update main root decorative ring — brightens on hover like the node border
    this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node')
      .select('.viz-node-mainroot-ring')
      .attr('fill', (d: DisplayNode) => {
        if (!d.isMainRoot) return 'none';
        if (this.store.isNodeHovered(d.id)) return '#55dd88';
        return d.recipeId === this.store.getSelectedRecipeId() ? '#22c55e' : '#3a4a3a';
      })
      .attr('stroke', (d: DisplayNode) => {
        if (!d.isMainRoot) return 'none';
        if (this.store.isNodeHovered(d.id)) return '#55dd88';
        return d.recipeId === this.store.getSelectedRecipeId() ? '#22c55e' : '#3a4a3a';
      })
      .attr('stroke-width', (d: DisplayNode) => d.isMainRoot ? 2.5 : 0)
      .attr('opacity', (d: DisplayNode) => d.isMainRoot ? 1 : 0)
      .style('display', (d: DisplayNode) => d.isMainRoot ? 'inline' : 'none');

    // Update edit button visibility on hover/selection change
    if (this.editButtonRenderer) {
      this.editButtonRenderer.updateVisibility(this.store.getSelectedNode(), this._hoveredNode);
    }

    // Update close (×) button visibility on hover/selection change —
    // shown on main root nodes when hovered or selected (normal mode only)
    this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node')
      .select('.viz-node-close-btn')
      .style('opacity', (d: DisplayNode) => {
        if (!d.isMainRoot || this.store.isEditMode()) return 0;
        return (this.store.isNodeSelected(d.id) || this.store.isNodeHovered(d.id)) ? 1 : 0;
      })
      .style('pointer-events', (d: DisplayNode) => {
        if (!d.isMainRoot || this.store.isEditMode()) return 'none';
        return (this.store.isNodeSelected(d.id) || this.store.isNodeHovered(d.id)) ? 'auto' : 'none';
      });
  }

  /**
   * Update node positions from simulation tick.
   */
  private renderNodePositions(): void {
    if (!this.nodesGroup) return;

    this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node')
      .attr('transform', (d: DisplayNode) => {
        const x = d.x != null && isFinite(d.x) ? d.x : 0;
        const y = d.y != null && isFinite(d.y) ? d.y : 0;
        return `translate(${x},${y})`;
      });
  }

  /**
   * Update link positions from simulation tick.
   */
  private renderLinkPositions(): void {
    if (!this.linksGroup) return;

    this.linksGroup.selectAll<SVGLineElement, Link<DisplayNode>>('line')
      .attr('x1', (d: Link<DisplayNode>) => d.source.x ?? 0)
      .attr('y1', (d: Link<DisplayNode>) => d.source.y ?? 0)
      .attr('x2', (d: Link<DisplayNode>) => d.target.x ?? 0)
      .attr('y2', (d: Link<DisplayNode>) => d.target.y ?? 0);
  }

  // ============================================================================
  // NODE HELPERS
  // ============================================================================

  /**
   * Get the fill color for a node's circle background.
   */
  private getNodeColor(node: DisplayNode): string {
    if (this.store.isNodeSelected(node.id)) {
      return 'var(--accent)';
    }
    // Inactive check BEFORE hovered — an inactive node should never show hover styling
    if (node.__inactive) {
      if (this.getNodeImageUrl(node)) {
        return '#08080c'; // Even darker behind the dimmed image
      }
      return '#111118';
    }
    if (this.store.isNodeHovered(node.id)) {
      return 'var(--accent-dark)';
    }
    if (this.getNodeImageUrl(node)) {
      return '#0d1117';
    }
    return '#1a1a2e'; // same dark blue used for the no-image placeholder elsewhere
  }

  /**
   * Get the stroke color for a node's circle border.
   * Priority: selected (bright) > hovered (semi-transparent) > normal (subtle).
   * Nodes with images also respect state, just with slightly more visible defaults.
   */
  private getNodeStroke(node: DisplayNode): string {
    if (this.store.isNodeSelected(node.id)) {
      return 'var(--accent)';          // Full bright accent
    }
    // Inactive check BEFORE hovered — an inactive node should never show hover styling
    if (node.__inactive) {
      return '#333338';
    }
    if (this.store.isNodeHovered(node.id)) {
      return '#55dd88';                // Solid bright accent for hover
    }
    // Only apply main root green to nodes from the selected recipe
    if (node.isMainRoot && node.recipeId === this.store.getSelectedRecipeId()!) {
      return '#22c55e';                // Green ring for main root
    }
    if (this.getNodeImageUrl(node)) {
      return '#3a4a3a';                // Solid gray-green for image nodes
    }
    return '#3a4a3a';                  // Solid gray-green by default
  }

  /**
   * Get stroke width for a node's border.
   * Selected gets a thicker, more prominent border.
   */
  private getNodeStrokeWidth(node: DisplayNode): number {
    if (this.store.isNodeSelected(node.id)) return 5;
    if (this.store.isNodeHovered(node.id)) return 4;
    if (node.isMainRoot && node.recipeId === this.store.getSelectedRecipeId()!) return 4;
    return 2.5;
  }

  /**
   * Resolve the image URL for a node — a node's own explicit imageUrl, no
   * more derivation. Thin wrapper around the shared resolver so the six
   * call sites below don't each need their own import.
   */
  private getNodeImageUrl(node: DisplayNode): string | undefined {
    return resolveNodeImageUrl(node.data);
  }

  /**
   * Get the display label for a node (truncated).
   */
  private getNodeLabel(node: DisplayNode): string {
    const maxChars = Math.floor((NODE_RADIUS * 2 - 12) / 7);
    if (node.name.length <= maxChars) return node.name;
    return node.name.substring(0, maxChars - 1) + '…';
  }

  /**
   * Get the node type from API node data.
   */
  private getNodeType(node: ApiRecipeNode): NodeType {
    const hasIngredient = !!node.ingredientInfo;
    const hasTransformation = !!node.transformationInfos?.length;
    if (hasIngredient && hasTransformation) return 'combined';
    if (hasIngredient) return 'ingredient';
    if (hasTransformation) return 'transformation';
    return 'ingredient';
  }

  /**
   * Get descendants to move when dragging a node.
   *
   * Traversal carries a `hiddenOnly` flag per branch:
   *   - Start with `hiddenOnly = !this.store.isNodeExpanded(draggedNode.id)`
   *   - If `hiddenOnly` is true and a node is NOT `__hidden`, skip it
   *     (don't move, don't traverse its children)
   *   - If a node is folded, its own children are traversed with
   *     `hiddenOnly = true`
   *   - Otherwise children keep the same `hiddenOnly` flag
   *
   * This means:
   *   - A visible node only reachable through a folded ancestor is skipped
   *     (e.g. node 4 in 1→2→3→4 when 2 is folded → 4 has another visible
   *      parent so it's not __hidden, and the branch from folded 2 runs
   *      in hidden-only mode).
   *   - A visible node reachable through an expanded path is still included
   *     (e.g. node 4 via 5 in 1→5→4, where 5 is expanded).
   *   - Hidden descendants of folded ancestors are included and propagate
   *     the hidden-only mode further down.
   */
  private getDescendantsToDrag(node: DisplayNode): DisplayNode[] {
    const result: DisplayNode[] = [];
    interface QueueItem { id: string; hiddenOnly: boolean; }
    const queue: QueueItem[] = [...(node.data.children || [])].map(id => ({
      id,
      hiddenOnly: !!!this.store.isNodeExpanded(node.id),
    }));
    const visited = new Set<string>();

    while (queue.length > 0) {
      const item = queue.shift()!;
      if (visited.has(item.id)) continue;
      visited.add(item.id);

      const child = this.nodePool.get(item.id);
      if (!child) continue;

      // In hidden-only mode, skip non-hidden nodes entirely
      if (item.hiddenOnly && !this.store.isNodeHidden(child.id)) continue;

      result.push(child);

      if (!child.data.children) continue;

      // If the child is folded, its children are traversed in hidden-only mode
      const nextHiddenOnly = !this.store.isNodeExpanded(child.id) ? true : item.hiddenOnly;
      for (const gid of child.data.children) {
        queue.push({ id: gid, hiddenOnly: nextHiddenOnly });
      }
    }

    return result;
  }

  /**
   * Make a node visible by unfolding all folded ancestors, then fit the
   * viewport to what's now visible. The unfold algorithm itself is
   * store.revealNode() (pure store state, shared with IngredientsView/
   * PreparationView, which call it directly since they have no canvas to
   * fit) — this wrapper only adds the Graph-canvas-specific follow-up.
   */
  private async ensureNodeVisible(nodeId: string): Promise<void> {
    if (!this.store.isNodeHidden(nodeId)) return; // Already visible or doesn't exist
    await this.store.revealNode(nodeId);
    this.fitViewportToNodes(Array.from(this.nodePool.values()).filter(n => !this.store.isNodeHidden(n.id)));
    this.restartSimulation(0.15);
  }

  /**
   * Select a node by id, unfolding hidden ancestors first if needed —
   * same sequence setOnNodeClick()'s own click handler uses internally.
   * Public, for callers with just an id rather than a live DisplayNode
   * reference — e.g. HomePage restoring a saved selection on back/forward
   * navigation (see render()'s restoreState handling). A no-op if the
   * node isn't in the pool at all — already deleted, or, for the
   * unfocused browse case, simply not part of whatever's currently
   * loaded (the same pre-existing "outside the loaded set" limitation
   * the recipe deep-link fix already accepts elsewhere).
   */
  async selectNodeById(nodeId: string): Promise<void> {
    const node = this.nodePool.get(nodeId);
    if (!node) return;
    await this.ensureNodeVisible(nodeId);
    this.selectNode(node);
  }
}

// ============================================================================
// Utility functions
// ============================================================================

/**
 * Apply a picked "suggested transformation" to a freshly-created blank
 * Transform node — sets its ingredient/transformation and the ingredient's
 * default image, exactly as if the user had picked them by hand afterwards
 * (no explicit node name: resolveNodeTitle() already falls back to the
 * ingredient's/transformation's own name). A no-op for the "blank node"
 * choice (undefined seed).
 */
function applyTransformSeed(node: DisplayNode, seed: TransformSeed | undefined): void {
  if (!seed) return;
  if (seed.ingredient) {
    node.data.ingredientInfo = { ingredient: seed.ingredient };
  }
  if (seed.transformation) {
    node.data.transformationInfos = [{ transformation: seed.transformation }];
  }
  if (seed.imageUrl) {
    node.data.imageUrl = seed.imageUrl;
  }
}

/**
 * Generate the SVG points string for a regular 13-sided polygon (triskaidecagon)
 * centered at (0,0) with the given radius. Used to visually distinguish the main
 * root node with a decorative ring.
 */
function generateMainRootPolygon(radius: number): string {
  const n = 13;
  const points: string[] = [];
  for (let i = 0; i < n; i++) {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2; // start at 12 o'clock
    const x = radius * Math.cos(angle);
    const y = radius * Math.sin(angle);
    points.push(`${x.toFixed(2)},${y.toFixed(2)}`);
  }
  return points.join(' ');
}
